/* Phase 2Z V1 — Rota Style Builder grid editor.

   Weekly grid where rows = employees, columns = cycle days. Each cell
   carries 1..N assignments, an optional HH:MM time window and (when
   timed) an optional break. Manager picks the target week (default
   next Monday) and clicks "Generate" to seed a draft rota period from
   the active style. If the target week already has live allocations
   we warn first; publishing later will only fill empty cells. */

import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
// TP-AT (Assignment Timing for Rota Style) -- reuse the helpers
// the Roster allocation modal already uses, so cell editor maths
// stay in lock-step. Backend remains the source of truth on save.
import {
  addMinutesToHHMM as _atAddMinutes,
  formatHM as _atFormatHM,
  entryMinutesFromTimes as _atEntryMins,
  findOverlaps as _atOverlaps,
} from "@/lib/timing";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Plus,
  X,
  Save,
  Trash2,
  CalendarPlus,
  Loader2,
  AlertTriangle,
  Coffee,
  Info,
  CheckCircle2,
  Users,
  CalendarDays,
  ListChecks,
  Square,
  Grid3x3,
  Clock,
  RotateCcw,
  FileClock,
  Repeat,
  Shuffle,
  Sliders,
  Sparkles,
  BrainCircuit,
  ShieldCheck,
  Wand2,
  ChevronDown,
  Eye,
  Zap,
} from "lucide-react";
// Phase 2Z.n — Coverage Guard V1 (read-only badge + slim drawer).
import { CoverageGuardBadge } from "@/components/CoverageGuardBadge";
import { CoverageGuardDrawer } from "@/components/CoverageGuardDrawer";
// Undo/Redo V1 (Feb 2026) -- simple in-memory stacks for single cell
// edits on the currently visible profile grid only. Restores replay
// the existing PUT /rota-style/profile/{id}/cells write path, so the
// plan gate, team scope and validation rules apply unchanged.
import { useGridUndo } from "@/hooks/useGridUndo";
import { UndoRedoButtons, UndoRedoConfirmDialog } from "@/components/GridUndoRedo";
import { snapRotaCell, projectRotaSnap, rsCellAtSlot, buildRotaRestoreBody } from "@/lib/gridUndo";
import { Switch } from "@/components/ui/switch";
import LockedFeatureCard from "@/components/LockedFeatureCard";
import { isPaidStarter, hasCommandAccess } from "@/lib/effectivePlan";
import { useAuth } from "@/context/AuthContext";
// Phase 2Z.o — cycle-week chip (display-only V1).
import { computeCycleWeek, nextMondayUTC } from "@/lib/cycleWeek";
// Phase 2Z.p — Relevant-week Dynamic Rotation (Option B), read-only
// preview. Pure helper + buildRotationPreview render the preview
// drawer; the engine itself is untouched until Stage 3.
import {
  rotateRelevantWeekCells, buildRotationPreview, weekDayRange,
} from "@/lib/dynamicRotation";
import { useCoverageGuard } from "@/hooks/useCoverageGuard";
// TP-AT.RS.8.2 (Feb 2026) -- shared apply-template helper reused
// by the Roster cell editor, Bulk Add and now the Rota Style
// cell editor. UI prefill only; no Rota Style Apply / RSA /
// materialiser touch.
import { deriveBlankWorkGapEntries } from "@/lib/deriveBlankWorkGaps";
import {
  evaluateRotaStyleCoverage, simulateRmiAdjustMix,
  computeRmiSafeCells,
} from "@/lib/coverageGuard";

const WEEK_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Phase 2Z.h — Week-aware day labels for multi-week cycles.
//   cycleLen <= 7 → "Mon", "Tue" … (today's behaviour — byte-for-byte).
//   cycleLen >  7 → "Mon W1", "Tue W1" … "Mon W2", … so customers can
//                  tell Week A Monday from Week B Monday at a glance.
function dayLabel(idx, cycleLen, upper = false) {
  const base = WEEK_LABELS[idx % 7] || "";
  if (cycleLen <= 7) return upper ? base.toUpperCase() : base;
  const week = Math.floor(idx / 7) + 1;
  const text = `${base} W${week}`;
  return upper ? text.toUpperCase() : text;
}

// Phase 2Z.h — Cycle-type defaults for the empty-state chooser. The
// `cycle_type` value matches the backend Literal exactly; we never
// invent new types here. "weekly" preserves the previous default.
const CYCLE_TYPE_OPTIONS = [
  { key: "weekly", label: "Weekly", days: 7,
    blurb: "Same routine every week." },
  { key: "biweekly", label: "Biweekly", days: 14,
    blurb: "Week A / Week B alternate every 2 weeks." },
  { key: "fourweekly", label: "Four-weekly", days: 28,
    blurb: "Four-week rotation, common for shift patterns." },
  { key: "custom", label: "Custom", days: 7,
    blurb: "Pick exact cycle length later in settings." },
];

// Deterministic colour palette for assignment chips. Picked from a
// muted set so the grid stays calm even when many categories are
// stacked in one cell. Hashing the category name (lowercased) keeps the
// same colour across renders without storing anything on the backend.
const CHIP_PALETTE = [
  { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800", dot: "bg-emerald-500" },
  { bg: "bg-sky-50",      border: "border-sky-200",      text: "text-sky-800",      dot: "bg-sky-500"      },
  { bg: "bg-violet-50",   border: "border-violet-200",   text: "text-violet-800",   dot: "bg-violet-500"   },
  { bg: "bg-amber-50",    border: "border-amber-200",    text: "text-amber-900",    dot: "bg-amber-500"    },
  { bg: "bg-rose-50",     border: "border-rose-200",     text: "text-rose-800",     dot: "bg-rose-500"     },
  { bg: "bg-teal-50",     border: "border-teal-200",     text: "text-teal-800",     dot: "bg-teal-500"     },
  { bg: "bg-indigo-50",   border: "border-indigo-200",   text: "text-indigo-800",   dot: "bg-indigo-500"   },
  { bg: "bg-fuchsia-50",  border: "border-fuchsia-200",  text: "text-fuchsia-800",  dot: "bg-fuchsia-500"  },
];

function chipPaletteFor(name) {
  const s = String(name || "").trim().toLowerCase();
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return CHIP_PALETTE[h % CHIP_PALETTE.length];
}

function avatarInitials(name) {
  return String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

const AVATAR_PALETTE = [
  "bg-emerald-500", "bg-sky-500", "bg-violet-500", "bg-amber-500",
  "bg-rose-500",    "bg-teal-500", "bg-indigo-500", "bg-fuchsia-500",
];

function avatarColorFor(name) {
  const s = String(name || "").trim().toLowerCase();
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}

function localISODate(d) {
  // Build YYYY-MM-DD from the Date's *local* components. Avoids the
  // off-by-one trap of `.toISOString()` which always converts to UTC
  // and can shift a local Monday 00:30 BST back to a UTC Sunday.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function nextMondayISO() {
  const d = new Date();
  // 1=Mon ... 7=Sun (Sunday=0 in JS, treat as 7)
  const day = d.getDay() === 0 ? 7 : d.getDay();
  const offset = 8 - day; // Mon -> 7, Sun -> 1
  d.setDate(d.getDate() + offset);
  return localISODate(d);
}

function isoToHuman(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function snapToMondayISO(iso) {
  // Snap any chosen date back to the Monday of its containing week.
  // The date string is parsed as a *local* date (the `T00:00:00`
  // suffix) so we don't accidentally roll across the UTC boundary
  // when computing the weekday or formatting the result.
  if (!iso) return nextMondayISO();
  const d = new Date(iso + "T00:00:00");
  const day = d.getDay() === 0 ? 7 : d.getDay();
  d.setDate(d.getDate() - (day - 1));
  return localISODate(d);
}

// Phase 2A Step 4 - shared helper that mirrors the entries-only
// editor model back into the legacy shadow break fields. Used by
// the Rota Style cell editor save and the Bulk Apply Pattern apply
// path so the rule is identical across both surfaces.
//   - When NO Away row is present, returns `break_minutes: 0` and
//     `break_start_time: ""` so a legacy `break_minutes > 0` is
//     cleared on the next save (no double-deduct downstream).
//   - When one or more Away rows are present, returns the sum of
//     Away minutes (falling back to the time-window when minutes
//     are null) and the first Away row's start_time.
// The helper accepts `picked` (the editor's ordered assignment
// list) plus the active `categories` array so the kind lookup is
// local and does not require touching the parent.
function _p2aMinutesBetweenHHMM(a, b) {
  if (!a || !b) return 0;
  const [ah, am] = a.split(":").map(Number);
  const [bh, bm] = b.split(":").map(Number);
  return Math.max(0, (bh * 60 + bm) - (ah * 60 + am));
}
function _p2aMirrorBreakFromPicked(picked, categories, shiftStart, shiftEnd) {
  const kindByCat = new Map(
    (categories || []).map((c) => [c.category_id, c.kind || "work"]));
  const awayRows = (picked || []).filter(
    (a) => kindByCat.get(a.category_id) === "away");
  if (awayRows.length === 0) {
    return { break_minutes: 0, break_start_time: "" };
  }
  let totalMins = 0;
  for (const a of awayRows) {
    if (typeof a.minutes === "number" && a.minutes > 0) {
      totalMins += a.minutes;
    } else if (a.start_time && a.end_time) {
      totalMins += _p2aMinutesBetweenHHMM(a.start_time, a.end_time);
    } else if (shiftStart && shiftEnd) {
      totalMins += _p2aMinutesBetweenHHMM(shiftStart, shiftEnd);
    }
  }
  return {
    break_minutes: totalMins,
    break_start_time: awayRows[0]?.start_time || "",
  };
}

export default function RotaStylePage() {
  // Phase 2Z.aa - paid Starter customers see a locked-state card.
  // Trial / founder / Growth / Command / Enterprise all pass.
  // Wrapping the existing inner component keeps the long list of
  // useState/useEffect/useMemo calls below from running for locked
  // Starter users AND keeps React Hooks rules-of-hooks satisfied.
  const [me, setMe] = useState(null);
  useEffect(() => {
    api.get("/billing/me").then(r => setMe(r.data)).catch(() => setMe({}));
  }, []);
  if (me && isPaidStarter(me)) {
    return (
      <LockedFeatureCard
        testid="rota-style-locked-starter"
        badge="GROWTH"
        title="Rota Style setup is available on Growth and above"
        body="You can still build and publish rosters manually from the Roster page."
        primary={{ to: "/app/billing", label: "Upgrade to Growth" }}
        secondary={{ to: "/app/roster", label: "Open Roster" }}
      />
    );
  }
  if (!me) return null;  // brief loading sliver; avoids hook-count drift
  return <RotaStylePageInner billingMe={me} />;
}

function RotaStylePageInner({ billingMe }) {
  const navigate = useNavigate();
  // TP-1 -- Team Admins get read-only access to the company-wide
  // Rota Style pattern when team_roster_edit_enabled is on for the
  // company. The Workspace Admin still owns mutations.
  // TP-RS.team -- when team_rota_style_enabled is ON, Team Admins
  // also get WRITE access scoped to their own team's profile via
  // canEditOwnRotaStyle. Workspace Admin behaviour is unchanged.
  const { user } = useAuth();
  const isTeamAdmin = user?.role === "team_admin";
  const canEditOwnRotaStyleFlag = Boolean(
    billingMe?.feature_flags?.team_rota_style_enabled,
  );
  const teamRotaStyleWrite = isTeamAdmin && canEditOwnRotaStyleFlag;
  const readOnly = isTeamAdmin && !teamRotaStyleWrite;
  // TP-RS.team -- which profile scope is the page viewing? null means
  // the legacy company-wide profile; a team_id string means a
  // team-scoped profile. Workspace Admin can switch via the picker.
  // Team Admin is forced to one of THEIR assigned team scopes.
  // TP-MultiTeamTA (Feb 2026) -- a multi-team admin lands on their
  // primary team (or first assigned team) and can switch to any of
  // their other assigned teams via the in-page scope picker.
  const _taScope = useMemo(
    () => (isTeamAdmin ? (
      [user?.team_id, ...((user?.assigned_team_ids) || [])]
        .filter(Boolean)
        .filter((v, i, a) => a.indexOf(v) === i)
    ) : []),
    [isTeamAdmin, user?.team_id, user?.assigned_team_ids],
  );
  const [selectedTeamId, setSelectedTeamId] = useState(() => {
    if (isTeamAdmin) {
      const primary = user?.team_id;
      const extras = Array.isArray(user?.assigned_team_ids)
        ? user.assigned_team_ids : [];
      return primary || extras[0] || null;
    }
    return null;
  });
  const [profilesList, setProfilesList] = useState([]);
  const [profile, setProfile] = useState(null);
  const [cells, setCells] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [categories, setCategories] = useState([]);
  // TP-AT.RS.8.2 (Feb 2026) -- Shift Templates are loaded once
  // per Rota Style page mount so the cell editor can offer an
  // "Apply shift template" UI prefill. Read-only; nothing here
  // touches Rota Style Apply, RSA or the materialiser.
  const [shiftTemplates, setShiftTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(null);

  // Undo/Redo V1 -- visible-grid stacks (max 20 steps). Cleared when
  // the active profile or team scope changes; refresh / navigation
  // clears them naturally because this is plain component state.
  const rsUndo = useGridUndo(20);
  const [rsUrBusy, setRsUrBusy] = useState(false);
  const [rsUrConfirm, setRsUrConfirm] = useState(null); // null | "undo" | "redo"

  // Target-week selector
  const [weekStart, setWeekStart] = useState(nextMondayISO());
  const [weekStatusLoading, setWeekStatusLoading] = useState(false);
  const [confirmGen, setConfirmGen] = useState(null);

  // Phase 2Z.ah - tiny "Pending drafts (N)" count next to the existing
  // button. Reuses the existing GET /drafts/rota?status=awaiting_review
  // endpoint, no new backend route. Defensive catch keeps the page
  // functional if the endpoint ever fails. Refreshed at mount and after
  // generate, so the count stays accurate without polling.
  // TP-AT.RS.8.8 (Feb 2026) -- gated on `user.role === "super_admin"`
  // because normal Workspace Admin managers should not be pushed
  // into the legacy Draft rotas workflow (modern automation writes
  // drafts directly into the Weekly roster and auto-publishes from
  // there). Kept for internal / support usage via super_admin only.
  const [pendingDraftCount, setPendingDraftCount] = useState(0);
  const refreshPendingDraftCount = useCallback(async () => {
    if (user?.role !== "super_admin") {
      setPendingDraftCount(0);
      return;
    }
    try {
      const { data } = await api.get("/drafts/rota?status=awaiting_review");
      const items = Array.isArray(data) ? data : (data?.periods || []);
      setPendingDraftCount(items.length);
    } catch {
      // Soft-fail - the button still works without a count.
      setPendingDraftCount(0);
    }
  }, [user?.role]);
  useEffect(() => { refreshPendingDraftCount(); }, [refreshPendingDraftCount]);

  // Phase 2Z.b — Team filter + Bulk Apply
  const [teamFilter, setTeamFilter] = useState("all");
  const [bulkOpen, setBulkOpen] = useState(false);
  // Phase 2Z.i — Bulk apply times + Rotate times drawers. Both reuse
  // the existing PUT /rota-style/profile/{id}/cells endpoint. No new
  // backend routes, no Pydantic changes.
  const [timesOpen, setTimesOpen] = useState(false);
  const [rotateOpen, setRotateOpen] = useState(false);
  // Phase 2Z.l C-lite — "Replace category in selected cells". Uses
  // the same PUT /rota-style/profile/{id}/cells endpoint as Bulk
  // apply / Rotate times — no new backend route, no Pydantic
  // changes. Pure cells only (single-assignment cells whose only
  // category is the "from" pick) so duplicates and split-shift
  // ambiguity are impossible in v1.
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [replaceSaving, setReplaceSaving] = useState(false);
  // Phase 2Z.l C-full — "Adjust mix by %" wrapper on top of the same
  // PUT /rota-style/profile/{id}/cells endpoint. Supports MIXED cells
  // (cell.assignments has > 1 slot) but SKIPS any cell where flipping
  // would create a duplicate (i.e. the target category is already
  // present in the cell). Skipping rather than deduping keeps v1
  // verifiable by hand. All time/break/note/cycle/employee fields are
  // preserved byte-for-byte; only one slot's category_id changes.
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustSaving, setAdjustSaving] = useState(false);
  // Phase 2Z.j — Change cycle modal. Uses the existing
  // POST /rota-style/profile + POST .../activate endpoints. The old
  // active profile is atomically archived by the activate endpoint —
  // we never call DELETE, never mutate populated cells.
  const [changeCycleOpen, setChangeCycleOpen] = useState(false);
  const [changingCycle, setChangingCycle] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);

  // Phase 2Z.n — Coverage Guard V1. Tri-state shield + read-only
  // evaluator + slim settings drawer. Zero backend changes — reuses
  // the existing /api/coverage-rules collection. The badge is the
  // ONLY page-level signal — no panels, no toasts, no banners.
  const [cgDrawerOpen, setCgDrawerOpen] = useState(false);
  const { rules: cgRules, reload: cgReload } = useCoverageGuard();
  const [cgTeams, setCgTeams] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/teams");
        setCgTeams(Array.isArray(data) ? data : []);
      } catch { /* graceful */ }
    })();
  }, []);

  // Phase 2Z.p — Relevant-week Dynamic Rotation preview drawer.
  // Display-only — no engine call, no write paths. Opens via the
  // "Preview Next Pattern" button rendered inside the Pattern
  // Engine card.
  const [dynRotPreviewOpen, setDynRotPreviewOpen] = useState(false);

  async function load() {
    setLoading(true);
    try {
      // TP-RS.team -- target a specific scope via ?team_id=, or
      // omit the param for the legacy company-wide profile. The
      // profiles list lets Workspace Admin switch scope from the
      // picker without a second round-trip.
      const profileUrl = selectedTeamId
        ? `/rota-style/profile?team_id=${encodeURIComponent(selectedTeamId)}`
        : "/rota-style/profile";
      const reqs = [
        api.get(profileUrl),
        // TP-Schedulable (Feb 2026) -- Rota Style Pattern Builder
        // must only show schedulable people. Permission-only admins
        // are filtered out by the backend.
        api.get("/employees?schedulable_only=true"),
        // Phase 2A Step 4 - load Work AND Away categories so the
        // Rota Style cell editor and the Bulk Apply Pattern drawer
        // can pick Break/Away as a normal assignment row. Work-only
        // tools (RMI, Replace Category, Coverage Guard rules) apply
        // their own kind filter at consumption time.
        api.get("/categories"),
      ];
      // Profiles-list endpoint exists from TP-RS.team onwards. Soft
      // fail keeps legacy tenants byte-stable if the route ever 404s.
      reqs.push(api.get("/rota-style/profiles").catch(() => ({ data: { profiles: [] } })));
      // TP-AT.RS.8.2 -- Shift Templates for the "Apply shift
      // template" UI prefill inside the cell editor. Company
      // scoped; every logged-in role can read.
      reqs.push(api.get("/shift-templates").catch(() => ({ data: [] })));
      const [pr, em, ca, pl, st] = await Promise.all(reqs);
      setProfile(pr.data.profile);
      setCells(pr.data.cells || []);
      setEmployees(em.data || em.data.items || []);
      setCategories(
        ((ca.data || ca.data.items || []) || [])
          .filter((c) => (c.kind === "work" || c.kind === "away")
                          && c.is_active !== false));
      setProfilesList(Array.isArray(pl?.data?.profiles) ? pl.data.profiles : []);
      setShiftTemplates(Array.isArray(st?.data) ? st.data : []);
      // Undo/Redo V1 -- return the fresh grid so save/delete handlers
      // can capture an accurate "after" cell snapshot.
      return { profile: pr.data.profile, cells: pr.data.cells || [] };
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, [selectedTeamId]);
  // Undo/Redo V1 -- switching profile (including cycle changes, which
  // create a new profile_id) or team scope invalidates every step.
  useEffect(() => { rsUndo.clear(); setRsUrConfirm(null);
    // eslint-disable-next-line
  }, [profile?.profile_id, selectedTeamId]);

  async function createProfile(cycleType = "weekly") {
    // TP-1 -- belt-and-braces: backend already returns 403 for
    // team_admin on POST /rota-style/profile. Friendly toast here so
    // a stray click doesn't show a generic error.
    if (readOnly) {
      toast.error("Only your Workspace Admin can create the Rota Style pattern.");
      return;
    }
    // Phase 2Z.h — the empty-state chooser passes the cycle type.
    // Default arg keeps any non-UI caller behaving exactly as today.
    const opt = CYCLE_TYPE_OPTIONS.find((o) => o.key === cycleType)
      || CYCLE_TYPE_OPTIONS[0];
    try {
      // TP-RS.team -- pass the current scope so a Team Admin (or
      // a Workspace Admin viewing a team scope) creates the right
      // kind of profile. Company-wide stays null for legacy parity.
      const body = {
        name: opt.key === "weekly" ? "Standard week"
                : `${opt.label} pattern`,
        cycle_type: opt.key,
        cycle_anchor_date: nextMondayISO(),
        rotation_policy: "stable",
      };
      if (selectedTeamId) body.team_id = selectedTeamId;
      const { data } = await api.post("/rota-style/profile", body);
      setProfile(data.profile);
      setCells([]);
      toast.success("Profile created. Start adding assignments to the grid.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Create failed");
    }
  }

  // Phase 2Z.j — Change cycle handler. Uses ONLY existing endpoints:
  //   1. POST /rota-style/profile   (creates new draft with chosen cycle)
  //   2. POST /rota-style/profile/{new_id}/activate
  //                                  (atomically archives old, activates new)
  // We never call DELETE. The old profile is preserved as `archived` so
  // the customer's data stays in MongoDB. If step 2 fails, the existing
  // active profile is left exactly as it was and the new (orphan draft)
  // profile sits invisible behind the active-only filter every other
  // handler applies.
  async function changeCycle(cycleType) {
    if (!profile) return;
    const opt = CYCLE_TYPE_OPTIONS.find((o) => o.key === cycleType)
      || CYCLE_TYPE_OPTIONS[0];
    setChangingCycle(true);
    let newProfileId = null;
    try {
      // TP-RS.team -- preserve the existing profile's team scope on
      // the replacement so changing cycle on a team-scoped profile
      // doesn't accidentally clone to company-wide.
      const body = {
        name: opt.key === "weekly" ? "Standard week"
                : `${opt.label} pattern`,
        cycle_type: opt.key,
        cycle_anchor_date: nextMondayISO(),
        rotation_policy: profile.rotation_policy || "stable",
        pattern_kind: "static",
      };
      if (profile.team_id) body.team_id = profile.team_id;
      const { data: created } = await api.post("/rota-style/profile", body);
      newProfileId = created.profile?.profile_id;
      if (!newProfileId) {
        throw new Error("Server did not return a profile_id.");
      }
      await api.post(`/rota-style/profile/${newProfileId}/activate`);
      toast.success("Cycle changed. Your previous Rota Style is kept inactive in your account.");
      setChangeCycleOpen(false);
      await load();
    } catch (err) {
      toast.error(
        formatError(err.response?.data?.detail)
          || "Could not change cycle. Your current Rota Style is unchanged.",
      );
    } finally {
      setChangingCycle(false);
    }
  }



  const cellsByEmpDay = useMemo(() => {
    const m = {};
    for (const c of cells) {
      const k = `${c.employee_id}|${c.cycle_day_index}`;
      (m[k] ||= []).push(c);
    }
    for (const k of Object.keys(m)) {
      m[k].sort((a, b) =>
        (a.start_time || "").localeCompare(b.start_time || ""),
      );
    }
    return m;
  }, [cells]);

  // Phase 2Z.b — team list (sorted, distinct) + filtered employee
  // view. Both derived in-memory, no new API.
  //
  // Phase 2Z.n hotfix — name resolution now consults the /teams
  // collection (already fetched for Coverage Guard as `cgTeams`).
  // The /employees endpoint historically returned `team_name: null`
  // even when the employee belonged to a properly-named team, so
  // the previous fallback (`e.team_name || e.team || "Unnamed team"`)
  // showed "Unnamed team" for every employee in a real team. The
  // authoritative name lives in `db.teams.name`.
  const teams = useMemo(() => {
    const nameById = new Map();
    for (const t of cgTeams || []) {
      if (t?.team_id) nameById.set(t.team_id, t.name || "Unnamed team");
    }
    const seen = new Map();
    for (const e of employees) {
      const tid = e.team_id || "";
      if (!seen.has(tid)) {
        const name = (tid && nameById.get(tid))
          || e.team_name
          || e.team
          || (tid ? "Unnamed team" : "No team");
        seen.set(tid, name);
      }
    }
    return Array.from(seen.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [employees, cgTeams]);

  const visibleEmployees = useMemo(() => {
    // TP-RS.team -- when viewing a team-scoped profile, the editor
    // grid only ever shows that team's employees. The team filter
    // chip set is locked to the same team and the bulk-apply /
    // rotate dialogs inherit the constraint via teamFilter.
    if (profile?.team_id) {
      return employees.filter((e) => (e.team_id || "") === profile.team_id);
    }
    if (teamFilter === "all") return employees;
    return employees.filter((e) => (e.team_id || "") === teamFilter);
  }, [employees, teamFilter, profile]);

  function openEditor(employeeId, dayIndex, existingCell = null) {
    // Phase 2Z.aj.1 - prefill start / end / break from the employee's
    // working pattern, but ONLY when opening a brand-new empty cell
    // (existingCell is null). Existing saved cells - including legacy
    // blank/full-day ones - are loaded verbatim. The Use employee
    // hours button (rendered inside the modal) is the user-driven
    // path for filling a blank existing cell. Falls back to the same
    // 09:00 / 17:00 / 60 defaults the Roster editor uses
    // (`RosterPage.jsx` `openCell`) so the two editors feel
    // identical.
    const emp = employees.find((e) => e.employee_id === employeeId);
    const wp = (existingCell === null && emp?.working_pattern) || {};
    const prefilledStart = existingCell === null
      ? (wp.default_start || "09:00")
      : (existingCell?.start_time || "");
    const prefilledEnd = existingCell === null
      ? (wp.default_end || "17:00")
      : (existingCell?.end_time || "");
    const prefilledBreak = existingCell === null
      ? (typeof wp.default_break_minutes === "number"
          ? wp.default_break_minutes
          : 60)
      : (existingCell?.break_minutes || 0);
    setEditing({
      employee_id: employeeId,
      cycle_day_index: dayIndex,
      cycle_length_days: profile?.cycle_length_days || 7,
      existing_cell_id: existingCell?.cell_id || null,
      start_time: prefilledStart,
      end_time: prefilledEnd,
      // Phase 2Z.aj - carry full assignment objects (including any
      // optional `minutes`) so the editor can show and round-trip
      // per-assignment durations. Legacy cells have no `minutes`
      // key and are treated as "equal split" by the editor.
      assignments: (existingCell?.assignments || []).map((a) => ({
        category_id: a.category_id,
        minutes: (typeof a.minutes === "number" && a.minutes > 0)
          ? a.minutes
          : null,
      })),
      break_minutes: prefilledBreak,
      break_start_time: existingCell?.break_start_time || "",
      // Phase 2Z.aj.1 - flag the editor that these times originate
      // from the working pattern so the modal can render the
      // "Using employee working pattern hours" helper. Hidden the
      // moment the user edits a time input.
      prefilled_from_pattern: existingCell === null
        && Boolean(wp.default_start || wp.default_end),
    });
  }

  async function saveCell(payload) {
    if (!profile) return;
    // TP-1 -- belt-and-braces: backend already returns 403 for
    // team_admin on PUT /rota-style/profile/{id}/cells. Friendly
    // toast here so the editor cannot send a doomed request.
    if (readOnly) {
      toast.error("Only your Workspace Admin can edit the Rota Style pattern.");
      return;
    }
    // -----------------------------------------------------------------
    // Phase 2Z.m manual cell editor same-day conflict guard (B+D).
    // Refuses to create a second cell on the same (employee, day)
    // because the publish path writes one shift document per day
    // using the outer time envelope — two cells silently inflate
    // shift hours (per the read-only audit). The toast surfaces the
    // safe interim workaround for hospitality split shifts: use one
    // wide cell with break_minutes set to the gap. Proper split-
    // shift support is queued as its own dedicated phase.
    //
    // Same-cell edits are exempt (excludeCellId).
    // -----------------------------------------------------------------
    const conflict = _findSameDayConflict(
      cells,
      payload.employee_id,
      payload.cycle_day_index,
      payload.existing_cell_id,
    );
    if (conflict) {
      const empName = (
        employees.find((e) => e.employee_id === payload.employee_id) || {}
      ).name || "This employee";
      // Phase 2Z.aj - customer-friendly copy. No em-dash, no internal
      // field names, no roadmap wording. Points the manager straight
      // at the existing block so they can edit the time, assignments
      // or break in-place rather than creating a duplicate.
      toast.error(
        `This day already has a Rota Style block for ${empName}. `
          + `Edit the existing block to change the time, assignments `
          + `or break. Rota Style currently supports one block per `
          + `employee per day.`,
        {
          action: {
            label: "Edit block",
            onClick: () => openEditor(
              payload.employee_id,
              payload.cycle_day_index,
              conflict,
            ),
          },
          duration: 12000,
        },
      );
      return;
    }
    // Undo/Redo V1 -- capture the slot before the write. For edits we
    // snapshot the exact cell being edited; for new cells the slot is
    // empty (the same-day conflict guard above enforces one block per
    // employee per day).
    const urBefore = snapRotaCell(
      payload.existing_cell_id
        ? (cells.find((c) => c.cell_id === payload.existing_cell_id) || null)
        : rsCellAtSlot(cells, payload.employee_id, payload.cycle_day_index));
    setSaving(true);
    try {
      const body = {
        upsert: [
          {
            employee_id: payload.employee_id,
            cycle_day_index: payload.cycle_day_index,
            start_time: payload.start_time || null,
            end_time: payload.end_time || null,
            // Phase 2Z.aj - assignments now carry an optional
            // per-row `minutes` value. Missing/zero/negative values
            // are dropped so the cell is stored exactly as legacy
            // (no `minutes` key) and the publish split falls back
            // to today's equal-split branch.
            assignments: (payload.assignments || []).map((a) => {
              const out = { category_id: a.category_id };
              const m = Number(a.minutes);
              if (Number.isFinite(m) && m > 0) out.minutes = Math.round(m);
              // TP-AT.RS -- forward the per-row clock-time windows
              // when both ends are set, so a manager's manual time
              // edits round-trip through save + reopen. The backend
              // RotaStyleAssignmentRef accepts these as optional
              // HH:mm strings; partial input is ignored upstream.
              if (a.start_time && a.end_time) {
                out.start_time = a.start_time;
                out.end_time = a.end_time;
              }
              return out;
            }),
            break_minutes: Number(payload.break_minutes) || 0,
            break_start_time: payload.break_start_time || null,
          },
        ],
      };
      // If we're editing an existing cell whose times changed, delete the
      // old row first because the upsert match key includes start/end.
      if (payload.existing_cell_id) {
        const old = cells.find(
          (c) => c.cell_id === payload.existing_cell_id,
        );
        if (
          old &&
          (old.start_time !== (payload.start_time || null) ||
            old.end_time !== (payload.end_time || null))
        ) {
          body.delete_cell_ids = [payload.existing_cell_id];
        }
      }
      await api.put(
        `/rota-style/profile/${profile.profile_id}/cells`,
        body,
      );
      const fresh = await load();
      setEditing(null);
      toast.success("Saved.");
      // Undo/Redo V1 -- push only when the cell genuinely changed and
      // the active profile is still the same one we edited.
      if (fresh?.profile?.profile_id === profile.profile_id) {
        const urAfter = snapRotaCell(
          rsCellAtSlot(fresh.cells, payload.employee_id, payload.cycle_day_index));
        if (projectRotaSnap(urBefore) !== projectRotaSnap(urAfter)) {
          rsUndo.push({
            profile_id: profile.profile_id,
            employee_id: payload.employee_id,
            cycle_day_index: payload.cycle_day_index,
            before: urBefore,
            after: urAfter,
          });
        }
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  }


  // Phase 2Z.c — toggle Static / Dynamic on the active profile. UI
  // optimistically updates so the explainer copy + button flip
  // instantly; the API call persists. On error we re-load to revert.
  async function savePatternKind(kind) {
    if (!profile) return;
    if ((profile.pattern_kind || "static") === kind) return;
    // Phase 2Z.p Stage 2.4 — the multi-week confirm dialog was
    // a guard rail against the legacy Week-1-only rotation bug.
    // That bug is fixed (Stage 3 alignment), so the dialog is no
    // longer needed. Removed wholesale; the Pattern Engine card
    // copy explains the new behaviour inline.
    setSaving(true);
    setProfile({ ...profile, pattern_kind: kind });
    try {
      await api.put(`/rota-style/profile/${profile.profile_id}`, {
        pattern_kind: kind,
      });
      toast.success(
        kind === "dynamic"
          ? "Rotation set to Dynamic. New drafts will rotate workday assignments."
          : "Rotation set to Static.",
      );
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Could not save");
      await load();
    } finally {
      setSaving(false);
    }
  }

  async function deleteCell(cell_id) {
    if (!profile) return;
    // Undo/Redo V1 -- capture the cell before removal so the manager
    // can bring the block back with one click.
    const urCell = cells.find((c) => c.cell_id === cell_id) || null;
    try {
      await api.put(`/rota-style/profile/${profile.profile_id}/cells`, {
        upsert: [],
        delete_cell_ids: [cell_id],
      });
      const fresh = await load();
      setEditing(null);
      toast.success("Removed.");
      if (urCell && fresh?.profile?.profile_id === profile.profile_id) {
        const urAfter = snapRotaCell(
          rsCellAtSlot(fresh.cells, urCell.employee_id, urCell.cycle_day_index));
        rsUndo.push({
          profile_id: profile.profile_id,
          employee_id: urCell.employee_id,
          cycle_day_index: urCell.cycle_day_index,
          before: snapRotaCell(urCell),
          after: urAfter,
        });
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Remove failed");
    }
  }

  // Undo/Redo V1 -- apply the top stack record. Concurrency guard:
  // re-fetch the active profile grid and compare the target slot with
  // the snapshot we expect; if someone changed it since, ask first
  // (compare-and-warn, no backend versioning in V1). On any backend
  // refusal (403 / 404 / plan gate / validation) the step stays
  // available so the manager is never silently stuck.
  const runRsHistory = async (mode, force = false) => {
    const rec = mode === "undo" ? rsUndo.peekUndo() : rsUndo.peekRedo();
    if (!rec || rsUrBusy || readOnly) return;
    setRsUrBusy(true);
    try {
      const profileUrl = selectedTeamId
        ? `/rota-style/profile?team_id=${encodeURIComponent(selectedTeamId)}`
        : "/rota-style/profile";
      const { data: fresh } = await api.get(profileUrl);
      if (fresh?.profile?.profile_id !== rec.profile_id) {
        toast.error("This pattern has changed. Undo is no longer available.");
        rsUndo.clear();
        return;
      }
      const currentCell = rsCellAtSlot(
        fresh?.cells || [], rec.employee_id, rec.cycle_day_index);
      if (!force) {
        const expected = mode === "undo" ? rec.after : rec.before;
        if (projectRotaSnap(snapRotaCell(currentCell)) !== projectRotaSnap(expected)) {
          setRsUrConfirm(mode);
          return;
        }
      }
      const target = mode === "undo" ? rec.before : rec.after;
      const body = buildRotaRestoreBody(
        rec.employee_id, rec.cycle_day_index, target, currentCell);
      if (body.upsert.length || body.delete_cell_ids.length) {
        await api.put(`/rota-style/profile/${rec.profile_id}/cells`, body);
      }
      if (mode === "undo") rsUndo.commitUndo(); else rsUndo.commitRedo();
      toast.success(mode === "undo" ? "Undo applied" : "Redo applied");
      await load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail)
        || "Could not apply the change. Your step is kept so you can retry.");
    } finally {
      setRsUrBusy(false);
    }
  };

  async function runGenerate(forceRegenerate = false) {
    setGenerating(true);
    setConfirmGen(null);
    try {
      const { data } = await api.post("/drafts/rota/run-from-style", {
        window_start: weekStart,
        force_regenerate: forceRegenerate,
      });
      toast.success(
        `Generated ${data.created_count} shifts. Opening for review…`,
      );
      navigate(data.review_link);
    } catch (err) {
      if (
        err.response?.status === 409 &&
        err.response.data?.detail?.existing_period_id
      ) {
        const detail = err.response.data.detail;
        if (
          window.confirm(
            "A draft rota already exists for that week. Replace it with a fresh one from your style?",
          )
        ) {
          try {
            const { data } = await api.post("/drafts/rota/run-from-style", {
              window_start: weekStart,
              force_regenerate: true,
            });
            toast.success(`Generated ${data.created_count} shifts.`);
            navigate(data.review_link);
          } catch (err2) {
            toast.error(
              formatError(err2.response?.data?.detail) || "Generate failed",
            );
          }
        } else {
          navigate(`/app/drafts/rota/${detail.existing_period_id}`);
        }
      } else {
        toast.error(
          formatError(err.response?.data?.detail) || "Generate failed",
        );
      }
    } finally {
      setGenerating(false);
    }
  }

  // Phase 2Z.b — Bulk Apply Pattern.
  // ONE atomic request to the existing
  // `PUT /rota-style/profile/{id}/cells` endpoint. Action types Add /
  // Replace / Only empty cells / Clear cells compose from the
  // endpoint's `upsert` + `delete_cell_ids` arrays. No new backend
  // routes. No multi-request loops. Server-side validation is
  // already in place; a 400 means nothing was written.
  //
  // TP-AT.RS.8.4 (Feb 2026) -- the drawer now sends `rows` (a
  // per-row assignment array with individual clock windows) in
  // place of the legacy `categoryIds + breakMinutes +
  // breakStartTime` shape. Each row lands verbatim as one
  // `RotaStyleAssignmentRef` (which already accepts optional
  // per-assignment `start_time`, `end_time`, `minutes`). Break /
  // Away rows contribute to the cell's shadow `break_minutes` and
  // `break_start_time` via the same `_p2aMirrorBreakFromPicked`
  // rule the cell editor uses.
  async function bulkApplyPattern(params) {
    if (!profile) return;
    const {
      employeeIds,
      dayIndices,
      startTime,
      endTime,
      rows,
      action, // "add" | "replace" | "only_empty" | "clear"
    } = params;
    setBulkSaving(true);
    try {
      // Reuse the same shadow-break mirror rule as the cell
      // editor so reports stay identical when a break row is on
      // the mix. Empty when no Away row is picked.
      const pickedForMirror = (rows || []).map((r) => ({
        category_id: r.category_id,
        minutes: Number(r.minutes) || 0,
        start_time: r.start_time || null,
        end_time: r.end_time || null,
      }));
      const mirroredBreak = _p2aMirrorBreakFromPicked(
        pickedForMirror, categories, startTime, endTime);
      const breakMinutes = Number(mirroredBreak.break_minutes || 0);
      const breakStartTime = mirroredBreak.break_start_time || "";
      const upsert = [];
      const delete_cell_ids = [];
      for (const eid of employeeIds) {
        for (const dayIdx of dayIndices) {
          const existing = cellsByEmpDay[`${eid}|${dayIdx}`] || [];
          if (action === "clear") {
            // Remove every cell on this (employee, day) slot.
            for (const c of existing) delete_cell_ids.push(c.cell_id);
            continue;
          }
          if (action === "only_empty" && existing.length > 0) {
            // Skip — slot is already populated.
            continue;
          }
          // TP-AT.RS.8.4 -- build assignments directly from rows,
          // preserving each row's own clock window. Merge existing
          // assignments only on the "add" branch, deduping by
          // category_id so double-adds don't happen.
          let mergedAssignments = (rows || []).map((r) => ({
            category_id: r.category_id,
            start_time: r.start_time || undefined,
            end_time: r.end_time || undefined,
            minutes: Number(r.minutes) > 0 ? Number(r.minutes) : undefined,
          }));
          if (action === "add" && existing.length > 0) {
            const seen = new Set(mergedAssignments.map((a) => a.category_id));
            for (const c of existing) {
              for (const a of c.assignments || []) {
                if (!seen.has(a.category_id)) {
                  mergedAssignments.push({
                    category_id: a.category_id,
                    start_time: a.start_time || undefined,
                    end_time: a.end_time || undefined,
                    minutes: a.minutes || undefined,
                  });
                  seen.add(a.category_id);
                }
              }
            }
            // If the existing cells have DIFFERENT times to the
            // requested time window, delete them so the new upsert
            // doesn't leave stale rows behind.
            for (const c of existing) {
              if (c.start_time !== startTime || c.end_time !== endTime) {
                delete_cell_ids.push(c.cell_id);
              }
            }
          }
          if (action === "replace" && existing.length > 0) {
            // Replace = drop every existing row on this slot first;
            // the upsert below will write a single fresh row.
            for (const c of existing) {
              if (c.start_time !== startTime || c.end_time !== endTime) {
                delete_cell_ids.push(c.cell_id);
              }
            }
          }
          if (!mergedAssignments.length) continue;
          upsert.push({
            employee_id: eid,
            cycle_day_index: dayIdx,
            start_time: startTime,
            end_time: endTime,
            assignments: mergedAssignments.map((a) => {
              const out = { category_id: a.category_id };
              if (a.start_time) out.start_time = a.start_time;
              if (a.end_time) out.end_time = a.end_time;
              if (a.minutes) out.minutes = a.minutes;
              return out;
            }),
            break_minutes: breakMinutes,
            break_start_time:
              breakMinutes > 0 ? (breakStartTime || null) : null,
          });
        }
      }
      if (upsert.length === 0 && delete_cell_ids.length === 0) {
        toast.info("Nothing to change with the current selection.");
        setBulkSaving(false);
        return;
      }
      await api.put(
        `/rota-style/profile/${profile.profile_id}/cells`,
        { upsert, delete_cell_ids },
      );
      toast.success(
        `Bulk apply done. ${upsert.length} cells written, ${delete_cell_ids.length} removed.`,
      );
      setBulkOpen(false);
      await load();
    } catch (err) {
      // Server validates atomically — nothing was written on 4xx.
      toast.error(
        formatError(err.response?.data?.detail) || "Bulk apply failed",
      );
    } finally {
      setBulkSaving(false);
    }
  }


  // Phase 2Z.i — Bulk apply times. Updates ONLY the time fields on
  // existing cells. Never creates cells (no assignment context). Never
  // touches assignments — every upsert replays the existing cell's
  // assignments verbatim. Skips slots with 0 cells (silent) or >1 cells
  // (split-shift; surfaces a single combined "Skipped N split-shift
  // slots — open them manually" toast). One atomic PUT.
// TP-AT.RS.4 -- shared equal-split helper used by bulk apply (pattern
// and times) so chip windows are always persisted on the cell. Without
// these explicit per-chip times, the seeder later collapses every
// assignment onto the cell's overall window and the roster shows the
// same time on every chip. The math mirrors `_redistributeTimeline`
// inside the cell editor.
function tprsBulkEqualSplitWindows(startTime, endTime, breakMinutes,
                                     breakStartTime, count) {
  const t2m = (t) => {
    if (!t || typeof t !== "string") return null;
    const m = t.match(/^(\d{1,2}):(\d{2})$/);
    if (!m) return null;
    return Number(m[1]) * 60 + Number(m[2]);
  };
  const m2t = (n) => {
    const h = Math.floor(n / 60);
    const mm = n % 60;
    return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
  };
  const sMin = t2m(startTime);
  const eMin = t2m(endTime);
  const brk = Number(breakMinutes) || 0;
  const brkStart = brk > 0 ? t2m(breakStartTime) : null;
  if (sMin == null || eMin == null || eMin <= sMin || !count) return [];
  const gross = eMin - sMin;
  const net = Math.max(0, gross - brk);
  if (net <= 0) return [];
  const baseShare = Math.floor(net / count);
  const remainder = net - baseShare * count;
  let cursor = sMin;
  const breakAt = brkStart != null && brkStart >= sMin && brkStart < eMin
    ? brkStart : null;
  let breakConsumed = false;
  const out = [];
  for (let i = 0; i < count; i++) {
    const share = baseShare + (i < remainder ? 1 : 0);
    const chipStart = cursor;
    let chipEnd = chipStart + share;
    if (!breakConsumed && breakAt != null
        && breakAt >= chipStart && breakAt < chipEnd) {
      chipEnd = chipEnd + brk;
      breakConsumed = true;
    } else if (!breakConsumed && breakAt == null
                && i === Math.floor(count / 2) && brk > 0) {
      chipEnd = chipEnd + brk;
      breakConsumed = true;
    }
    out.push({
      start_time: m2t(chipStart),
      end_time: m2t(Math.min(chipEnd, eMin)),
    });
    cursor = Math.min(chipEnd, eMin);
  }
  return out;
}


  async function bulkApplyTimes(params) {
    if (!profile) return;
    const {
      employeeIds, dayIndices,
      startTime, endTime,
    } = params;
    // Phase 2A Step 7 - Bulk Apply Times is non-magical: it only
    // updates start/end. Each cell preserves its own existing
    // `break_minutes` and `break_start_time` shadow fields. Away
    // assignment rows on the cell are left untouched. If a manager
    // wants to change a break they open the cell editor.
    setBulkSaving(true);
    try {
      const upsert = [];
      const delete_cell_ids = [];
      let skippedMissing = 0;
      let skippedSplit = 0;
      for (const eid of employeeIds) {
        for (const dayIdx of dayIndices) {
          const existing = cellsByEmpDay[`${eid}|${dayIdx}`] || [];
          if (existing.length === 0) {
            skippedMissing += 1;
            continue;
          }
          if (existing.length > 1) {
            skippedSplit += 1;
            continue;
          }
          const cell = existing[0];
          // Same (employee, cycle_day_index, start, end) → backend updates
          // the existing row in place (lines 22460-22474). Different
          // times → delete the old row first, then insert a fresh one
          // with the new key. Same delete+upsert pattern Bulk Apply
          // Pattern has been using safely for weeks.
          if (cell.start_time !== startTime || cell.end_time !== endTime) {
            delete_cell_ids.push(cell.cell_id);
          }
          // TP-AT.RS.4 -- re-derive per-chip windows so they survive
          // into the seeder. If the window did not change, the
          // existing chip windows (if any) are preserved; otherwise
          // we equal-split across the new window. Either way the
          // saved cell always carries explicit per-chip times.
          // Phase 2A Step 7 - preserve this cell's existing break
          // shadow fields exactly. Bulk Apply Times never mutates
          // breaks; Away rows remain in their original positions.
          const _p2aCellBreakMins = Number(cell.break_minutes) || 0;
          const _p2aCellBreakStart = cell.break_start_time || null;
          const assignmentsList = cell.assignments || [];
          const reuseExisting = cell.start_time === startTime
              && cell.end_time === endTime
              && assignmentsList.every((a) => a.start_time && a.end_time);
          const chipWindows = reuseExisting
            ? assignmentsList.map((a) => ({ start_time: a.start_time, end_time: a.end_time }))
            : tprsBulkEqualSplitWindows(
                startTime, endTime, _p2aCellBreakMins, _p2aCellBreakStart,
                assignmentsList.length);
          upsert.push({
            employee_id: cell.employee_id,
            cycle_day_index: cell.cycle_day_index,
            start_time: startTime,
            end_time: endTime,
            assignments: assignmentsList.map((a, i) => {
              const out = { category_id: a.category_id };
              if (chipWindows[i]) {
                out.start_time = chipWindows[i].start_time;
                out.end_time = chipWindows[i].end_time;
              }
              return out;
            }),
            note: cell.note,
            break_minutes: _p2aCellBreakMins,
            break_start_time: _p2aCellBreakMins > 0 ? _p2aCellBreakStart : null,
          });
        }
      }
      if (upsert.length === 0 && delete_cell_ids.length === 0) {
        toast.info(
          skippedMissing + skippedSplit > 0
            ? "No populated single-cell slots in this selection. Nothing to update."
            : "Nothing to change with the current selection.",
        );
        setBulkSaving(false);
        return;
      }
      await api.put(
        `/rota-style/profile/${profile.profile_id}/cells`,
        { upsert, delete_cell_ids },
      );
      const parts = [`Updated ${upsert.length} cell${upsert.length === 1 ? "" : "s"}.`];
      if (skippedMissing > 0) {
        parts.push(`${skippedMissing} empty slot${skippedMissing === 1 ? "" : "s"} skipped.`);
      }
      if (skippedSplit > 0) {
        parts.push(`${skippedSplit} split-shift slot${skippedSplit === 1 ? "" : "s"} skipped. Open them manually.`);
      }
      toast.success(parts.join(" "));
      setTimesOpen(false);
      await load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Bulk apply times failed");
    } finally {
      setBulkSaving(false);
    }
  }


  // Phase 2Z.i — Rotate times. Deterministically rotates start/end/
  // break fields between selected employees on each selected day.
  // Never creates cells. Never touches assignments. Skips days where
  // fewer than 2 employees have exactly 1 cell. One atomic PUT.
  async function rotateTimes(params) {
    if (!profile) return;
    const { employeeIds, dayIndices, direction, step } = params;
    setBulkSaving(true);
    try {
      const upsert = [];
      const delete_cell_ids = [];
      let updatedCells = 0;
      let skippedDays = 0;
      let skippedSplit = 0;
      const stepN = Math.max(1, Number(step) || 1);
      for (const dayIdx of dayIndices) {
        // Deterministic ordering — by the selected employeeIds order
        // (which itself comes from the alphabetical employee list).
        const pairs = [];
        let hadSplitOnThisDay = false;
        for (const eid of employeeIds) {
          const existing = cellsByEmpDay[`${eid}|${dayIdx}`] || [];
          if (existing.length === 1) pairs.push({ eid, cell: existing[0] });
          else if (existing.length > 1) hadSplitOnThisDay = true;
        }
        if (hadSplitOnThisDay) skippedSplit += 1;
        if (pairs.length < 2) {
          skippedDays += 1;
          continue;
        }
        const N = pairs.length;
        const shift = direction === "backward"
          ? ((N - (stepN % N)) % N)
          : (stepN % N);
        for (let i = 0; i < N; i++) {
          const sourceCell = pairs[(i + shift) % N].cell;   // donor of times
          const targetPair = pairs[i];                       // receiver of times
          const target = targetPair.cell;
          // If donor & target are the SAME cell (shift = 0), nothing to write.
          if (sourceCell.cell_id === target.cell_id) continue;
          // Time key changed → delete old cell, insert fresh one.
          if (target.start_time !== sourceCell.start_time
              || target.end_time !== sourceCell.end_time) {
            delete_cell_ids.push(target.cell_id);
          }
          upsert.push({
            employee_id: target.employee_id,
            cycle_day_index: target.cycle_day_index,
            start_time: sourceCell.start_time,
            end_time: sourceCell.end_time,
            // Assignments stay tied to the RECEIVING employee — never
            // rotated. Replayed verbatim from the target cell.
            assignments: (target.assignments || []).map((a) => ({
              category_id: a.category_id,
            })),
            note: target.note,
            break_minutes: sourceCell.break_minutes || 0,
            break_start_time: sourceCell.break_start_time || null,
          });
          updatedCells += 1;
        }
      }
      if (upsert.length === 0 && delete_cell_ids.length === 0) {
        toast.info(
          skippedDays > 0
            ? "No days had at least two single-cell employees. Nothing to rotate."
            : "Nothing to change with the current selection.",
        );
        setBulkSaving(false);
        return;
      }
      await api.put(
        `/rota-style/profile/${profile.profile_id}/cells`,
        { upsert, delete_cell_ids },
      );
      const parts = [`Rotated ${updatedCells} cell${updatedCells === 1 ? "" : "s"}.`];
      if (skippedDays > 0) {
        parts.push(`${skippedDays} day${skippedDays === 1 ? "" : "s"} skipped (need 2+ populated cells).`);
      }
      if (skippedSplit > 0) {
        parts.push(`${skippedSplit} day${skippedSplit === 1 ? "" : "s"} had split-shift cells. Open them manually.`);
      }
      toast.success(parts.join(" "));
      setRotateOpen(false);
      await load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Rotate times failed");
    } finally {
      setBulkSaving(false);
    }
  }


  // Phase 2Z.l C-lite — Replace category in selected cells. PURE
  // cells only: a cell is eligible only when it has exactly ONE
  // assignment AND that single assignment's category_id equals the
  // chosen `from_category_id`. Mixed cells (e.g. [Admin, Inbound])
  // are NEVER touched — they're surfaced in the drawer as "Skipped"
  // so the customer can decide manually. This keeps v1 ambiguity-free
  // and dedupe-impossible. All time fields, break fields and notes
  // are preserved byte-for-byte; only the single assignment's
  // category_id is swapped. One atomic PUT to the existing
  // /rota-style/profile/{id}/cells endpoint.
  async function replaceCategory(params) {
    if (!profile) return;
    const {
      from_category_id,
      to_category_id,
      employeeIds,      // [] = all visible employees
      dayIndices,       // [] = all cycle days
    } = params;
    if (!from_category_id || !to_category_id
        || from_category_id === to_category_id) {
      toast.error("Pick a different target category.");
      return;
    }
    setReplaceSaving(true);
    try {
      const empSet = employeeIds && employeeIds.length > 0
        ? new Set(employeeIds) : null;
      const daySet = dayIndices && dayIndices.length > 0
        ? new Set(dayIndices) : null;
      const upsert = [];
      let skippedMixed = 0;
      for (const c of cells) {
        if (empSet && !empSet.has(c.employee_id)) continue;
        if (daySet && !daySet.has(c.cycle_day_index)) continue;
        const assigns = c.assignments || [];
        const containsFrom = assigns.some(
          (a) => a.category_id === from_category_id);
        if (!containsFrom) continue;
        // PURE rule: exactly one assignment AND it is the "from"
        // category. Anything else is "mixed" and is left untouched.
        const isPure = assigns.length === 1
          && assigns[0].category_id === from_category_id;
        if (!isPure) {
          skippedMixed += 1;
          continue;
        }
        upsert.push({
          employee_id: c.employee_id,
          cycle_day_index: c.cycle_day_index,
          start_time: c.start_time,
          end_time: c.end_time,
          // Replace the single assignment, preserve nothing else.
          assignments: [{ category_id: to_category_id }],
          note: c.note,
          break_minutes: c.break_minutes || 0,
          break_start_time: c.break_start_time || null,
        });
      }
      if (upsert.length === 0) {
        toast.info(
          skippedMixed > 0
            ? `Nothing to replace. The ${skippedMixed} matching cell${skippedMixed === 1 ? " is" : "s are"} mixed (open them manually).`
            : "No matching cells in the selected scope.",
        );
        setReplaceSaving(false);
        return;
      }
      await api.put(
        `/rota-style/profile/${profile.profile_id}/cells`,
        { upsert, delete_cell_ids: [] },
      );
      const parts = [
        `Replaced category in ${upsert.length} cell${upsert.length === 1 ? "" : "s"}.`,
      ];
      if (skippedMixed > 0) {
        parts.push(
          `${skippedMixed} mixed cell${skippedMixed === 1 ? "" : "s"} skipped. Edit those manually.`,
        );
      }
      toast.success(parts.join(" "));
      setReplaceOpen(false);
      await load();
    } catch (err) {
      toast.error(
        formatError(err.response?.data?.detail) || "Replace category failed");
    } finally {
      setReplaceSaving(false);
    }
  }


  // Phase 2Z.l C-full + Phase 2Z.m RMI Hybrid C3 — Adjust mix by %.
  //
  // Customer picks: "Move minutes from X to Y by N% (within scope)".
  // The drawer's preview tags every selected cell with one of two
  // actions:
  //
  //   • `flip` — at least one assignment slot in the cell currently
  //     carries `from`; replace ONE such slot with `to`. Other
  //     assignments are preserved byte-for-byte. This is the legacy
  //     C-full / C1 / C2 behaviour.
  //
  //   • `add`  — the cell is single-slot AND that slot is exactly
  //     `from`. Flipping would erase all `from` coverage from the
  //     cell, so instead the engine APPENDS `to` as a second
  //     assignment alongside the existing `from`. Net minutes split
  //     50/50 at render-time via `_cellNetMins / assigns.length`.
  //
  // Both branches are guarded by the same duplicate-safety check
  // (`alreadyHasTo`) so a cell is never written with the target
  // category twice. The add branch ALSO re-validates that the cell
  // is genuinely single-slot from-only at write time, in case the
  // page state drifted between preview and apply.
  //
  // Apply ships ONE atomic PUT to the existing
  // /rota-style/profile/{id}/cells endpoint — no new backend route,
  // no schema change.
  async function adjustMix(params) {
    if (!profile) return;
    const {
      from_category_id,
      to_category_id,
      cells: requestedCells,  // [{ cell_id, action: "flip" | "add" }, ...]
    } = params;
    if (!from_category_id || !to_category_id
        || from_category_id === to_category_id) {
      toast.error("Pick two different categories.");
      return;
    }
    if (!requestedCells || requestedCells.length === 0) {
      toast.error("Nothing selected to change.");
      return;
    }
    setAdjustSaving(true);
    try {
      // cell_id -> action ("flip" | "add"). Anything that isn't
      // explicitly "add" falls through to "flip" (defence-in-depth
      // against a malformed payload).
      const actionByCellId = new Map();
      for (const r of requestedCells) {
        actionByCellId.set(
          r.cell_id, r.action === "add" ? "add" : "flip");
      }
      const upsert = [];
      for (const c of cells) {
        if (!actionByCellId.has(c.cell_id)) continue;
        const action = actionByCellId.get(c.cell_id);
        const assigns = c.assignments || [];
        // Duplicate-safety wraps BOTH branches: never write a cell
        // that would carry the target category twice.
        const alreadyHasTo = assigns.some(
          (a) => a.category_id === to_category_id);
        if (alreadyHasTo) continue;

        let nextAssignments;
        if (action === "add") {
          // C3 add branch — re-validate the cell is genuinely
          // single-slot from-only at write time. If state drifted
          // between preview and apply, fall through to skip rather
          // than mutate something the manager didn't see.
          const singleSlotFromOnly = (
            assigns.length === 1
            && assigns[0].category_id === from_category_id
          );
          if (!singleSlotFromOnly) continue;
          // Preserve the existing from-slot, append to as a 2nd
          // slot. Render-time math will split net minutes 50/50.
          nextAssignments = [
            ...assigns.map((a) => ({ category_id: a.category_id })),
            { category_id: to_category_id },
          ];
        } else {
          // Flip branch — unchanged byte-for-byte from C-full / C1
          // / C2. Replace exactly ONE slot (the first occurrence of
          // `from`) with `to`. Everything else copies through.
          const containsFrom = assigns.some(
            (a) => a.category_id === from_category_id);
          if (!containsFrom) continue;
          let flipped = false;
          nextAssignments = assigns.map((a) => {
            if (!flipped && a.category_id === from_category_id) {
              flipped = true;
              return { category_id: to_category_id };
            }
            return { category_id: a.category_id };
          });
        }

        // TP-AT.RS.4 -- persist per-chip windows so the seeder can
        // honour them. When the cell is single-chip the equal-split
        // collapses to the cell window (byte-stable); when it becomes
        // multi-chip via the "add" branch, the new chips get
        // staggered windows instead of all collapsing onto cell_start.
        const chipWindows = tprsBulkEqualSplitWindows(
          c.start_time, c.end_time, c.break_minutes || 0,
          c.break_start_time, nextAssignments.length);
        upsert.push({
          employee_id: c.employee_id,
          cycle_day_index: c.cycle_day_index,
          start_time: c.start_time,
          end_time: c.end_time,
          assignments: nextAssignments.map((a, i) => {
            const out = { category_id: a.category_id };
            if (chipWindows[i]) {
              out.start_time = chipWindows[i].start_time;
              out.end_time = chipWindows[i].end_time;
            }
            return out;
          }),
          note: c.note,
          break_minutes: c.break_minutes || 0,
          break_start_time: c.break_start_time || null,
        });
      }
      if (upsert.length === 0) {
        toast.info("Nothing to change. The selected cells no longer match.");
        setAdjustSaving(false);
        return;
      }
      await api.put(
        `/rota-style/profile/${profile.profile_id}/cells`,
        { upsert, delete_cell_ids: [] },
      );
      toast.success(
        `Adjusted mix in ${upsert.length} cell${upsert.length === 1 ? "" : "s"}.`,
      );
      setAdjustOpen(false);
      await load();
    } catch (err) {
      toast.error(
        formatError(err.response?.data?.detail) || "Adjust mix failed");
    } finally {
      setAdjustSaving(false);
    }
  }


  async function startGenerate() {
    // Fetch a just-in-time pre-flight: does the target week already
    // have live allocations OR an existing draft? If so, confirm
    // before generating. Publishing later only fills empty slots.
    setWeekStatusLoading(true);
    let status = null;
    try {
      const res = await api.get(
        `/drafts/rota/week-status?window_start=${weekStart}`,
      );
      status = res.data;
    } catch {
      status = null;
    } finally {
      setWeekStatusLoading(false);
    }
    if (status?.has_live_allocations) {
      setConfirmGen({
        kind: "live",
        liveCount: status.live_allocation_count,
        weekEnd: status.window_end,
      });
      return;
    }
    if (status?.existing_period) {
      setConfirmGen({
        kind: "existing",
        existingStatus: status.existing_period.status,
        weekEnd: status.window_end,
      });
      return;
    }
    runGenerate(false);
  }

  // Phase 2Z.n — Coverage Guard verdict for the current Rota Style
  // template. Computed BEFORE the loading / no-profile early returns
  // so the hook order stays stable across renders. Pure derivation;
  // no network call. Falls back to a 7-day cycle if the profile
  // isn't loaded yet (the badge just renders grey in that state).
  const cgVerdict = useMemo(
    () => evaluateRotaStyleCoverage({
      cells, employees, categories,
      rules: cgRules,
      cycle_days: profile?.cycle_length_days || 7,
      teams: cgTeams,
      // TP-CG.team — when viewing a team-scoped Rota Style profile,
      // pass the scope so rules tied to OTHER teams are skipped
      // entirely (no false "under" coverage from cross-team rules).
      scope_team_id: profile?.team_id || null,
    }),
    [cells, employees, categories, cgRules, profile?.cycle_length_days, profile?.team_id, cgTeams],
  );

  if (loading) {
    return (
      <div className="p-6 text-sm text-[#4B5773]">Loading rota style…</div>
    );
  }

  if (!profile) {
    if (readOnly) {
      // TP-1 -- team_admin lands here when their company has no
      // active Rota Style pattern. They cannot create one.
      return (
        <div className="p-6 max-w-3xl" data-testid="rota-style-empty-readonly">
          <div className="inline-flex items-center gap-1.5 mb-2 px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-[10px] uppercase tracking-widest text-slate-700 font-semibold">
            <Info className="h-3 w-3" />
            Read-only
          </div>
          <h1 className="text-2xl font-semibold text-[#0B1A36]">Rota Style Pattern</h1>
          <p className="text-sm text-[#4B5773] mt-2">
            Your Workspace Admin has not set up a Rota Style pattern
            yet. Once they do, you will see it here so you know what
            pattern will be applied to your team.
          </p>
        </div>
      );
    }
    // TP-RS.team.1 -- when the WS Admin clicks a team chip whose
    // scope has no profile, make the empty-state explicit: name the
    // team, explain that no Rota Style has been set up, and give a
    // one-click escape back to Company-wide. Without this the user
    // is dumped onto the generic 'Pick a cycle length' screen and
    // has no idea which scope they are creating for.
    const emptyScopeTeam = !isTeamAdmin && selectedTeamId
      ? cgTeams.find((t) => t.team_id === selectedTeamId)
      : null;
    return (
      <div className="p-6 max-w-3xl" data-testid="rota-style-empty">
        {/* TP-MultiTeamTA (Feb 2026) -- multi-team Team Admin sees a
            chip picker on empty state too, so they can switch
            between their assigned teams without going through the
            main grid first. Single-team TA still sees no picker. */}
        {isTeamAdmin && _taScope.length > 1 && (
          <div
            className="mb-4 flex items-center gap-2 flex-wrap"
            data-testid="rota-style-scope-picker-empty-ta"
          >
            <span className="text-[11px] uppercase tracking-widest text-[#4B5773] font-medium">
              Pattern scope
            </span>
            {_taScope.map((tid) => {
              const t = cgTeams.find((x) => x.team_id === tid);
              const isActive = selectedTeamId === tid;
              return (
                <button
                  key={tid}
                  type="button"
                  onClick={() => setSelectedTeamId(tid)}
                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
                    isActive
                      ? "bg-[#0B1A36] text-white border-[#0B1A36]"
                      : "bg-white text-[#0B1A36] border-slate-200 hover:bg-slate-50"
                  }`}
                  data-testid={`rota-style-scope-team-${tid}`}
                >
                  {t?.name || "Team"}
                  {profilesList.some((p) => p.team_id === tid) && (
                    <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-emerald-500"
                          aria-label="Active profile exists" />
                  )}
                </button>
              );
            })}
          </div>
        )}
        {/* TP-RS.team.1 -- scope picker stays visible on the empty
            state so WS Admin can switch back without re-navigating. */}
        {!isTeamAdmin && (
          <div
            className="mb-4 flex items-center gap-2 flex-wrap"
            data-testid="rota-style-scope-picker-empty"
          >
            <span className="text-[11px] uppercase tracking-widest text-[#4B5773] font-medium">
              Pattern scope
            </span>
            <button
              type="button"
              onClick={() => setSelectedTeamId(null)}
              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
                !selectedTeamId
                  ? "bg-[#0B1A36] text-white border-[#0B1A36]"
                  : "bg-white text-[#0B1A36] border-slate-200 hover:bg-slate-50"
              }`}
              data-testid="rota-style-scope-company-wide"
            >
              Company-wide
            </button>
            {cgTeams.map((t) => (
              <button
                key={t.team_id}
                type="button"
                onClick={() => setSelectedTeamId(t.team_id)}
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
                  selectedTeamId === t.team_id
                    ? "bg-[#0B1A36] text-white border-[#0B1A36]"
                    : "bg-white text-[#0B1A36] border-slate-200 hover:bg-slate-50"
                }`}
                data-testid={`rota-style-scope-team-${t.team_id}`}
              >
                {t.name}
                {profilesList.some((p) => p.team_id === t.team_id) && (
                  <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-emerald-500"
                        aria-label="Active profile exists" />
                )}
              </button>
            ))}
          </div>
        )}
        {emptyScopeTeam && (
          <div
            className="mb-5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 flex items-start gap-2"
            data-testid="rota-style-empty-team-callout"
          >
            <Info className="h-4 w-4 mt-0.5 text-amber-700 flex-shrink-0" />
            <div className="text-sm text-[#0B1A36] flex-1">
              <p className="font-medium mb-0.5">
                {emptyScopeTeam.name} has no Rota Style yet.
              </p>
              <p className="text-xs text-[#4B5773] leading-relaxed">
                This team has not set up its own pattern. Pick a cycle length below to create one for <strong>{emptyScopeTeam.name}</strong>, or switch back to your company-wide pattern.
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setSelectedTeamId(null)}
                  className="h-7 text-xs"
                  data-testid="rota-style-empty-back-to-company"
                >
                  Back to Company-wide
                </Button>
              </div>
            </div>
          </div>
        )}
        <div className="inline-flex items-center gap-1.5 mb-2 px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">
          <Repeat className="h-3 w-3" />
          Pattern cycle - reusable assignment template
        </div>
        <h1 className="text-2xl font-semibold text-[#0B1A36]">
          Rota Style Pattern Builder
          {emptyScopeTeam && (
            <span className="ml-2 text-base font-normal text-emerald-700">
              · {emptyScopeTeam.name}
            </span>
          )}
        </h1>
        <p className="text-sm text-[#4B5773] mt-2">
          {emptyScopeTeam ? (
            <>
              Build a reusable pattern for <strong>{emptyScopeTeam.name}</strong> only. The generator will use it when applying patterns for this team, then existing rules (leave, availability, skills) protect the rota from invalid decisions.
            </>
          ) : (
            <>
              Teach NextRota how your team normally allocates work. The
              generator will use this pattern first, then existing rules
              (leave, availability, skills) protect the rota from invalid
              decisions.
            </>
          )}
        </p>
        <div className="mt-5">
          <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold mb-2">
            {emptyScopeTeam
              ? `Pick a cycle length for ${emptyScopeTeam.name}`
              : "Pick a cycle length"}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2"
               data-testid="rota-style-cycle-chooser">
            {CYCLE_TYPE_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => createProfile(opt.key)}
                className="text-left rounded-lg border border-slate-200 bg-white px-3.5 py-3 hover:border-emerald-500 hover:bg-emerald-50/30 transition-colors"
                data-testid={`rota-style-cycle-${opt.key}`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[13px] font-semibold text-[#0B1A36]">
                    {opt.label}
                  </span>
                  <span className="text-[10px] text-[#94A3B8]">
                    {opt.days} days
                  </span>
                </div>
                <p className="text-[11px] text-[#4B5773] mt-1 leading-relaxed">
                  {opt.blurb}
                </p>
              </button>
            ))}
          </div>
          <p className="text-[11px] text-[#94A3B8] mt-3 leading-relaxed">
            Need different hours on Fridays or different Week A / Week B
            times? Pick <span className="font-medium">Biweekly</span>{" "}
            - every day in the cycle has its own start and end time.
          </p>
        </div>
      </div>
    );
  }

  const cycleLen = profile.cycle_length_days;

  // Compute the per-day ISO date for the currently selected target
  // week so the day headers can render "MON 26 OCT" style copy.
  const dayDates = (() => {
    if (!weekStart) return [];
    const base = new Date(weekStart + "T00:00:00");
    return Array.from({ length: cycleLen }).map((_, i) => {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      return d;
    });
  })();

  // ---- Derived summaries (no backend calls; pure read off cells). ----
  // Pattern Summary: simple counts so admins can sanity-check the
  // shape of their pattern at a glance.
  // TP-RS.team.1 -- when the loaded profile is team-scoped, the
  // summary stats must reflect only the visible (team) employees,
  // not the whole company. Otherwise the manager sees inflated
  // "empty workday" counts caused by employees who are out of
  // this profile's scope. Falls back to the full `employees` array
  // when no team scope is selected (legacy company-wide).
  const summaryEmployees = profile?.team_id ? visibleEmployees : employees;
  const totalAssignments = cells.reduce(
    (n, c) => n + ((c.assignments || []).length), 0);
  const filledCells = new Set(
    cells.map((c) => `${c.employee_id}|${c.cycle_day_index}`),
  ).size;
  const workdayLen = Math.min(cycleLen, 5);
  const workdaySlotsTotal = summaryEmployees.length * workdayLen;
  const workdayFilled = cells.filter(
    (c) => c.cycle_day_index < workdayLen
        && (c.assignments || []).length > 0
        && (profile?.team_id
          ? summaryEmployees.some((e) => e.employee_id === c.employee_id)
          : true),
  ).length;
  const workdayEmpty = Math.max(0, workdaySlotsTotal - workdayFilled);

  // Pattern Health: derived booleans only — never a score.
  const allWorkdaysHaveTime = (() => {
    for (const e of summaryEmployees) {
      // Phase 2Z.m Option B — a Full-day cell is considered "has time"
      // when the employee carries a working pattern that provides
      // daily_minutes (the timeless cell inherits its net minutes
      // from there). Cells with no assignments at all are still
      // flagged separately by `allCellsHaveAssignment`.
      const hasPattern = !!(e.working_pattern
        && Number(e.working_pattern.daily_minutes) > 0);
      for (let d = 0; d < workdayLen; d++) {
        const list = cellsByEmpDay[`${e.employee_id}|${d}`] || [];
        if (!list.length) return false;
        if (list.some((c) => !c.start_time || !c.end_time)) {
          if (!hasPattern) return false;
        }
      }
    }
    return summaryEmployees.length > 0;
  })();
  const allCellsHaveAssignment = cells.every(
    (c) => (c.assignments || []).length > 0,
  );
  const noEmptyWorkdays = workdayEmpty === 0 && summaryEmployees.length > 0;
  const weekendCovered = (() => {
    if (cycleLen <= 5) return null; // not applicable
    for (let d = 5; d < cycleLen; d++) {
      for (const e of summaryEmployees) {
        if ((cellsByEmpDay[`${e.employee_id}|${d}`] || []).length > 0) {
          return true;
        }
      }
    }
    return false; // genuinely empty — informational, not a hard fail
  })();

  // Assignment Mix — minutes-weighted (F2). For each cell we compute
  // its NET minutes (end - start - break for timed cells, or the
  // employee's `working_pattern.daily_minutes` for Full-day cells —
  // which is already NET paid/working minutes, so it is NOT further
  // reduced by `break_minutes`) and split that across the cell's
  // assignments evenly (same rule used by the publish split helper
  // for timed cells, so the panel matches what actually lands on
  // the live roster). Break is counted ONCE per cell by construction.
  // Read-only derivation from the in-memory `cells` already on the
  // page — no backend call.
  const mix = (() => {
    const _t2m = (t) => {
      if (!t || typeof t !== "string" || !t.includes(":")) return null;
      const [h, m] = t.split(":");
      return Number(h) * 60 + Number(m);
    };
    const empById = new Map(employees.map((e) => [e.employee_id, e]));
    const minutesByCategory = new Map();
    let totalMinutes = 0;
    for (const c of cells) {
      const assigns = c.assignments || [];
      if (assigns.length === 0) continue;
      const s = _t2m(c.start_time);
      const e = _t2m(c.end_time);
      let net = 0;
      if (s == null || e == null || e <= s) {
        // Full-day cell — inherit the employee's working-pattern
        // daily minutes. `daily_minutes` already represents NET
        // (paid/working) minutes, so do NOT subtract break_minutes
        // (that would double-deduct the break the working pattern
        // already accounts for).
        const emp = empById.get(c.employee_id);
        const daily = Number(
          (emp && emp.working_pattern && emp.working_pattern.daily_minutes)
          ?? 480);
        net = Math.max(0, daily);
      } else {
        const gross = e - s;
        const brk = Math.max(0, Math.min(Number(c.break_minutes) || 0,
                                           gross - 1));
        net = Math.max(0, gross - brk);
      }
      if (net === 0) continue;
      const perAssign = net / assigns.length;
      totalMinutes += net;
      for (const a of assigns) {
        const name = a.category_name || "(unnamed)";
        minutesByCategory.set(name,
          (minutesByCategory.get(name) || 0) + perAssign);
      }
    }
    if (totalMinutes === 0) return [];
    return Array.from(minutesByCategory.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name, mins]) => ({
        name,
        minutes: Math.round(mins),
        pct: Math.round((mins / totalMinutes) * 100),
        palette: chipPaletteFor(name),
      }));
  })();

  return (
    <div className="p-6 max-w-[1480px] mx-auto" data-testid="rota-style-page">
      {/* R3 (Feb 2026) - Guided Rota Style setup strip. Presentational
          only. Reads existing state; adds one GET to
          /api/onboarding/checklist for the R1 account_setup_complete
          boolean. Dismissible via a localStorage key namespaced by
          company_id. Contains no references to Automation V1 or leave
          automation - those live on the Automation page only. */}
      <RotaStyleSetupStrip billingMe={billingMe}
                           companyId={user?.company_id} />
      {/* TP-1 -- Team Admin read-only banner. Shown only when the
          current user is a team_admin AND TP-RS.team write access
          is OFF (legacy flag-off behaviour). With the flag on, the
          banner becomes a team-scope confirmation instead. */}
      {readOnly && (
        <div
          className="mb-3 flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
          data-testid="rota-style-readonly-banner"
        >
          <Info className="h-4 w-4 mt-0.5 text-slate-500 flex-shrink-0" />
          <div className="text-xs text-[#0B1A36]">
            <strong>Read-only.</strong> Your Workspace Admin manages
            the company Rota Style pattern. You can view it here so
            you know what pattern will be applied to your team.
          </div>
        </div>
      )}
      {isTeamAdmin && teamRotaStyleWrite && (
        <div
          className="mb-3 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2"
          data-testid="rota-style-team-scope-banner"
        >
          <Info className="h-4 w-4 mt-0.5 text-emerald-700 flex-shrink-0" />
          <div className="text-xs text-emerald-900">
            <strong>Team Rota Style.</strong> You can create and edit a pattern for your own team only. Other teams and the company-wide pattern remain managed by your Workspace Admin.
          </div>
        </div>
      )}
      {/* Phase 2Z.style.simplify — pattern-vs-rota clarity banner. */}
      <div
        className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
        data-testid="rota-style-pattern-banner"
      >
        <Info className="h-4 w-4 mt-0.5 text-amber-700 flex-shrink-0" />
        <div className="text-xs text-amber-900">
          <strong>You are editing a pattern, not the live rota.</strong> To create, edit or publish the real rota, open Roster.
          <button
            type="button"
            onClick={() => navigate("/app/roster")}
            className="ml-2 underline font-medium"
            data-testid="rota-style-banner-open-roster"
          >
            Open Roster
          </button>
        </div>
      </div>
      {/* TP-RS.team -- profile scope picker. Workspace Admin sees a
          chip per scope (Company-wide + each active team-scoped
          profile) and may click to switch. Team Admin sees a single
          locked chip naming their own team. Hidden when neither path
          is meaningful (e.g. team_admin without the flag, no profile
          loaded yet). */}
      {((isTeamAdmin && teamRotaStyleWrite) || (!isTeamAdmin && Array.isArray(profilesList))) && (
        <div
          className="mb-3 flex items-center gap-2 flex-wrap"
          data-testid="rota-style-scope-picker"
        >
          <span className="text-[11px] uppercase tracking-widest text-[#4B5773] font-medium">
            Pattern scope
          </span>
          {isTeamAdmin ? (
            _taScope.length <= 1 ? (
              <button
                type="button"
                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border bg-[#0B1A36] text-white border-[#0B1A36]"
                data-testid="rota-style-scope-team-locked"
                disabled
              >
                {(() => {
                  const team = cgTeams.find((t) => t.team_id === selectedTeamId);
                  return `Your team: ${team?.name || "Team"}`;
                })()}
              </button>
            ) : (
              // TP-MultiTeamTA (Feb 2026) -- multi-team admins get an
              // explicit chip per assigned team. Never company-wide.
              <>
                {_taScope.map((tid) => {
                  const t = cgTeams.find((x) => x.team_id === tid);
                  const isActive = selectedTeamId === tid;
                  return (
                    <button
                      key={tid}
                      type="button"
                      onClick={() => setSelectedTeamId(tid)}
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
                        isActive
                          ? "bg-[#0B1A36] text-white border-[#0B1A36]"
                          : "bg-white text-[#0B1A36] border-slate-200 hover:bg-slate-50"
                      }`}
                      data-testid={`rota-style-scope-team-${tid}`}
                    >
                      {t?.name || "Team"}
                      {profilesList.some((p) => p.team_id === tid) && (
                        <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-emerald-500"
                              aria-label="Active profile exists"
                              title="Active profile exists for this team" />
                      )}
                    </button>
                  );
                })}
              </>
            )
          ) : (
            <>
              <button
                type="button"
                onClick={() => setSelectedTeamId(null)}
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
                  !selectedTeamId
                    ? "bg-[#0B1A36] text-white border-[#0B1A36]"
                    : "bg-white text-[#0B1A36] border-slate-200 hover:bg-slate-50"
                }`}
                data-testid="rota-style-scope-company-wide"
              >
                Company-wide
              </button>
              {cgTeams.map((t) => (
                <button
                  key={t.team_id}
                  type="button"
                  onClick={() => setSelectedTeamId(t.team_id)}
                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
                    selectedTeamId === t.team_id
                      ? "bg-[#0B1A36] text-white border-[#0B1A36]"
                      : "bg-white text-[#0B1A36] border-slate-200 hover:bg-slate-50"
                  }`}
                  data-testid={`rota-style-scope-team-${t.team_id}`}
                >
                  {t.name}
                  {profilesList.some((p) => p.team_id === t.team_id) && (
                    <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-emerald-500"
                          aria-label="Active profile exists"
                          title="Active profile exists for this team" />
                  )}
                </button>
              ))}
            </>
          )}
        </div>
      )}
      {/* A1 — Header polish */}
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold text-[#0B1A36]" data-testid="rota-style-page-title">Rota Style Pattern Builder</h1>
            <p className="text-xs text-[#4B5773] mt-1 max-w-xl">
              Create reusable rota patterns for your team. Use them from the Roster page or let Automation apply them for you.
            </p>
            <Info className="h-3.5 w-3.5 text-[#94A3B8]" />
          </div>
          <p className="text-xs text-[#4B5773] mt-1.5 flex items-center gap-1.5 flex-wrap">
            <span className="font-medium text-[#0B1A36]">{profile.name}</span>
            {/* TP-RS.team.1 -- scope chip beside the title strip so
                the manager always knows which team's pattern they are
                editing. Defaults to "Company-wide" for legacy
                profiles, names the team for team-scoped ones. */}
            <span
              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${
                profile.team_id
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-slate-50 text-slate-600 border-slate-200"
              }`}
              data-testid="rota-style-title-scope-chip"
            >
              {profile.team_id
                ? `Team: ${(cgTeams.find((t) => t.team_id === profile.team_id) || {}).name || "Team"}`
                : "Company-wide"}
            </span>
            <span className="text-[#CBD5E1]">•</span>
            <span className="capitalize">{profile.cycle_type}</span>
            <span className="text-[#94A3B8]">({cycleLen} days)</span>
            <span className="text-[#CBD5E1]">•</span>
            <span>
              Anchor:{" "}
              <span className="text-[#0B1A36] font-medium">
                {isoToHuman(profile.cycle_anchor_date)}
              </span>
            </span>
          </p>
          {/* Phase 2Z.o — Cycle-week chip (display-only). Shows which
              week of the Rota Style cycle would be seeded if Generate
              were pressed right now. Pure derivation; no rotation
              simulation, no engine assumption. Always renders for any
              cycle length (1-day, 7-day, 14-day, 28-day, custom).
              Phase 2Z.p Stage 2.1 — driven by the user-selected
              `Generate for week starting` date field (not next-Monday
              default) so changing the date instantly re-targets which
              cycle-week the chip + preview drawer report on. */}
          {(() => {
            const cw = computeCycleWeek({
              profile, targetWeekStart: weekStart,
            });
            if (!cw) return null;
            return (
              <div
                className="mt-2 flex items-center gap-1.5 flex-wrap"
                data-testid="rota-style-cycle-week-chip-strip"
                data-cycle-week-of={cw.weekInCycle}
                data-cycle-total-weeks={cw.totalWeeks}
              >
                <span className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-medium mr-0.5">
                  Cycle position
                </span>
                {Array.from({ length: cw.totalWeeks }).map((_, i) => {
                  const isNext = i === cw.weekInCycle;
                  return (
                    <span
                      key={i}
                      data-testid={`rota-style-cycle-week-chip-${i}`}
                      data-cycle-week-next={isNext ? "true" : "false"}
                      className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium ${
                        isNext
                          ? "bg-[#0B1A36] text-white border-[#0B1A36] shadow-sm"
                          : "bg-white text-[#5775A6] border-slate-200"
                      }`}
                    >
                      Pattern week {i + 1}
                      {isNext && (
                        <span className="ml-1 text-[10px] font-semibold opacity-90">
                          ← Next
                        </span>
                      )}
                    </span>
                  );
                })}
                <span
                  className="text-[11px] text-[#4B5773] ml-1"
                  data-testid="rota-style-cycle-week-summary"
                >
                  Next generated week:{" "}
                  <span className="font-medium text-[#0B1A36]">
                    {cw.weekLabel}
                  </span>
                </span>
                {/* Phase 2Z.p Stage 2.4 — Preview button MOVED into
                    the Pattern Engine card below so it lives with
                    the on/off toggle and the explainer, not up here
                    next to the date input. */}
              </div>
            );
          })()}
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <div className="flex items-center gap-2">
            {!readOnly && (
              <UndoRedoButtons
                canUndo={rsUndo.canUndo}
                canRedo={rsUndo.canRedo}
                busy={rsUrBusy}
                onUndo={() => runRsHistory("undo")}
                onRedo={() => runRsHistory("redo")}
                testidPrefix="rota-style"
              />
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/app/roster")}
              data-testid="rota-style-open-roster-btn"
              className="text-xs"
            >
              Open Roster
            </Button>
            {hasCommandAccess(billingMe) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/app/automation")}
                data-testid="rota-style-configure-automation-btn"
                className="text-xs"
              >
                <Zap className="h-3.5 w-3.5 mr-1.5" />
                Configure Automation
              </Button>
            )}
            {user?.role === "super_admin" && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/app/drafts/rota")}
                data-testid="rota-style-pending-drafts-btn"
                className="text-xs"
              >
                <FileClock className="h-3.5 w-3.5 mr-1.5" />
                Pending drafts
                {pendingDraftCount > 0 && (
                  <span
                    className="ml-1.5 inline-flex items-center justify-center min-w-[1.25rem] px-1.5 h-4 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-900 border border-amber-300"
                    data-testid="rota-style-pending-drafts-count"
                  >
                    {pendingDraftCount}
                  </span>
                )}
              </Button>
            )}
          </div>
          {weekStatusLoading && (
            <span className="text-[11px] text-[#4B5773] flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              Checking week…
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-5 items-start">
        <div className="min-w-0">
          {/* Phase 2Z.b — Team filter + Bulk Apply toolbar */}
          <div className="flex items-center gap-3 flex-wrap mb-3">
            <label className="text-[11px] uppercase tracking-widest text-[#94A3B8] font-medium">
              Team filter
            </label>
            <select
              value={teamFilter}
              onChange={(e) => setTeamFilter(e.target.value)}
              className="text-xs px-2.5 py-1.5 rounded border border-slate-200 bg-white text-[#0B1A36] min-w-[160px]"
              data-testid="rota-style-team-filter"
            >
              <option value="all">{isTeamAdmin ? "All assigned teams" : "All teams"}</option>
              {teams.map((t) => (
                <option key={t.id || "no-team"} value={t.id || ""}>
                  {t.name}
                </option>
              ))}
            </select>
            <CoverageGuardBadge
              verdict={cgVerdict}
              onOpenSettings={() => setCgDrawerOpen(true)}
              testid="coverage-guard-badge-rota-style"
              size="md"
              dayLabels={Array.from({ length: cycleLen }).map((_, i) => dayLabel(i, cycleLen))}
              emptyHint="No Coverage Guard rules yet. Click Manage Coverage Guard to add one."
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBulkOpen(true)}
              disabled={visibleEmployees.length === 0}
              data-testid="rota-style-bulk-apply-open"
              className="ml-auto text-xs"
            >
              <Grid3x3 className="h-3.5 w-3.5 mr-1.5" />
              Bulk apply pattern
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setTimesOpen(true)}
              disabled={visibleEmployees.length === 0}
              data-testid="rota-style-bulk-apply-times-btn"
              className="text-xs"
            >
              <Clock className="h-3.5 w-3.5 mr-1.5" />
              Bulk apply times
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setRotateOpen(true)}
              disabled={visibleEmployees.length === 0}
              data-testid="rota-style-rotate-times-btn"
              className="text-xs"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              Rotate times
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setReplaceOpen(true)}
              disabled={visibleEmployees.length === 0
                         || cells.length === 0
                         || categories.length < 2}
              data-testid="rota-style-replace-category-btn"
              className="text-xs"
            >
              <Shuffle className="h-3.5 w-3.5 mr-1.5" />
              Replace category
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAdjustOpen(true)}
              disabled={visibleEmployees.length === 0
                         || cells.length === 0
                         || categories.length < 2}
              data-testid="rota-style-adjust-mix-btn"
              className="text-xs"
            >
              <BrainCircuit className="h-3.5 w-3.5 mr-1.5" />
              Rota Mix Intelligence
            </Button>
          </div>
          {/* Cell legend */}
          <p className="text-[11px] text-[#4B5773] mb-2">
            Click a cell to add or edit time and assignments.
          </p>
          <div className="overflow-auto border border-slate-200 rounded-lg bg-white">
            <table
              className="w-full text-sm border-collapse"
              data-testid="rota-style-grid"
            >
              <thead className="bg-slate-50/80">
                <tr>
                  <th className="text-left text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold px-3 py-2.5 border-b border-slate-200 w-[200px]">
                    Employee
                  </th>
                  {Array.from({ length: cycleLen }).map((_, i) => (
                    <th
                      key={i}
                      className="text-left text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold px-3 py-2.5 border-b border-slate-200"
                      data-testid={`rota-style-header-day-${i}`}
                    >
                      <div className="text-[#0B1A36] font-semibold">
                        {dayLabel(i, cycleLen, true)}
                      </div>
                      {dayDates[i] && (
                        <div className="text-[10px] text-[#94A3B8] font-normal normal-case mt-0.5">
                          {dayDates[i].toLocaleDateString("en-GB", {
                            day: "numeric", month: "short",
                          })}
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleEmployees.map((e) => {
                  // A2 — Employee row polish: avatar + name + team.
                  const av = avatarColorFor(e.name);
                  const team = e.team_name || e.team || "";
                  return (
                    <tr
                      key={e.employee_id}
                      className="border-b border-slate-100 hover:bg-slate-50/40"
                    >
                      <td className="px-3 py-3 align-top">
                        <div className="flex items-center gap-2.5">
                          <span
                            className={`h-8 w-8 rounded-full ${av} text-white text-[11px] font-semibold flex items-center justify-center shrink-0`}
                            data-testid={`rota-style-avatar-${e.employee_id}`}
                          >
                            {avatarInitials(e.name)}
                          </span>
                          <div className="min-w-0">
                            <div className="text-[13px] font-medium text-[#0B1A36] truncate">
                              {e.name}
                            </div>
                            {team && (
                              <div className="text-[11px] text-[#94A3B8] truncate">
                                {team}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      {Array.from({ length: cycleLen }).map((_, dayIdx) => {
                        const cellList =
                          cellsByEmpDay[`${e.employee_id}|${dayIdx}`] || [];
                        return (
                          <td
                            key={dayIdx}
                            className="px-2 py-2 align-top border-l border-slate-100 min-w-[150px]"
                            data-testid={`rota-style-cell-${e.employee_id}-${dayIdx}`}
                          >
                            <div className="space-y-1">
                              {cellList.map((c) => {
                                // A3 — Coloured assignment chips. One
                                // chip per category inside the cell
                                // button. The wrapper button itself
                                // stays the click target.
                                //
                                // Phase 2Z.m UX gap fix — if an
                                // assignment's underlying category was
                                // deactivated AFTER it was added, we
                                // keep the chip visible (no
                                // auto-removal) but show a small
                                // "Deactivated" pill so the manager
                                // knows the category is no longer
                                // offered in the picker and may
                                // behave oddly downstream.
                                const assigns = c.assignments || [];
                                return (
                                  <button
                                    key={c.cell_id}
                                    type="button"
                                    onClick={() => openEditor(e.employee_id, dayIdx, c)}
                                    className="block w-full text-left rounded-md border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 transition-colors px-2 py-1.5"
                                    data-testid={`rota-style-chip-${c.cell_id}`}
                                  >
                                    <div className="text-[11px] font-medium text-[#0B1A36]">
                                      {c.start_time
                                        ? `${c.start_time}-${c.end_time}`
                                        : "Full day"}
                                    </div>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {assigns.map((a, idx) => {
                                        const label = a.category_name;
                                        const p = chipPaletteFor(label);
                                        const cat = categories.find(
                                          (cc) => cc.category_id === a.category_id,
                                        );
                                        const isDeactivated = cat && cat.is_active === false;
                                        return (
                                          <span
                                            key={`${c.cell_id}-${idx}`}
                                            className={`text-[10px] font-medium px-1.5 py-0.5 rounded border inline-flex items-center gap-1 ${p.bg} ${p.border} ${p.text}`}
                                            title={isDeactivated ? "Category is deactivated" : undefined}
                                          >
                                            {label}
                                            {isDeactivated && (
                                              <span
                                                className="inline-block px-1 py-0 text-[8px] rounded bg-amber-100 text-amber-800 ring-1 ring-amber-200"
                                                data-testid={`rota-style-chip-deactivated-${c.cell_id}-${idx}`}
                                              >
                                                Deactivated
                                              </span>
                                            )}
                                          </span>
                                        );
                                      })}
                                    </div>
                                    {c.break_minutes ? (
                                      <div className="text-[10px] text-amber-700 flex items-center gap-0.5 mt-1">
                                        <Coffee className="h-2.5 w-2.5" />
                                        {c.break_minutes}m
                                        {c.break_start_time
                                          ? ` @ ${c.break_start_time}`
                                          : ""}
                                      </div>
                                    ) : null}
                                  </button>
                                );
                              })}
                              <button
                                type="button"
                                onClick={() => {
                                  // Phase 2Z.aj - when this day
                                  // already has a Rota Style block,
                                  // route the manager into the
                                  // existing block instead of trying
                                  // to add a second (which would
                                  // be refused by the same-day
                                  // conflict guard). Rota Style is
                                  // one block per employee per day.
                                  if (cellList.length) {
                                    openEditor(e.employee_id, dayIdx, cellList[0]);
                                  } else {
                                    openEditor(e.employee_id, dayIdx);
                                  }
                                }}
                                className="block w-full text-[10px] px-2 py-1 rounded-md border border-dashed border-slate-200 text-[#94A3B8] hover:border-slate-300 hover:text-[#4B5773] hover:bg-slate-50/60 transition-colors"
                                data-testid={`rota-style-add-${e.employee_id}-${dayIdx}`}
                              >
                                <Plus className="h-3 w-3 inline mr-1" />
                                {cellList.length ? "Edit block" : "Add"}
                              </button>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right rail — derived panels, all read-only off cells/employees. */}
        <aside className="space-y-4" data-testid="rota-style-right-rail">
          {/* Pattern Cycle + Static/Dynamic toggle. */}
          <section
            className="rounded-lg border border-slate-200 bg-white p-4"
            data-testid="rota-style-cycle-info"
          >
            <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold mb-2 flex items-center justify-between">
              <span>Pattern cycle</span>
              <button
                type="button"
                onClick={() => setChangeCycleOpen(true)}
                data-testid="rota-style-change-cycle-btn"
                className="text-[10px] font-medium text-emerald-700 hover:text-emerald-800 hover:underline normal-case tracking-normal flex items-center gap-1"
              >
                <Repeat className="h-3 w-3" />
                Change cycle…
              </button>
            </div>
            <p className="text-[12px] text-[#0B1A36] leading-relaxed">
              Your Rota Style repeats every cycle. Whatever you set here
              for{" "}
              <span className="font-medium capitalize">
                {profile.cycle_type}
              </span>{" "}
              ({cycleLen} days) is reused on every future cycle, anchored
              from{" "}
              <span className="font-medium">
                {isoToHuman(profile.cycle_anchor_date)}
              </span>
              .
            </p>

            <div
              className="mt-3 pt-3 border-t border-slate-100"
              data-testid="rota-style-pattern-engine-card"
            >
              {/* Phase 2Z.p Stage 2.4 — "Pattern Engine" framing.
                  Display polish only; the internal `pattern_kind`
                  values stay `static` / `dynamic` byte-for-byte
                  (locked by source-level test). */}
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold flex items-center gap-1.5">
                  <Wand2 className="h-3 w-3 text-indigo-500" />
                  Pattern Engine
                </div>
                <span className="text-[10px] uppercase tracking-widest font-semibold text-indigo-500/80">
                  Advanced
                </span>
              </div>
              <p
                className="text-[11px] text-[#4B5773] mb-2"
                data-testid="rota-style-pattern-engine-description"
              >
                Keeps this pattern repeating every cycle.
              </p>
              <div
                className="flex gap-1.5"
                data-testid="rota-style-pattern-kind"
                role="radiogroup"
                aria-label="Pattern Engine"
              >
                {[
                  { v: "static", label: "OFF" },
                  { v: "dynamic", label: "ON" },
                ].map((opt) => {
                  const active =
                    (profile.pattern_kind || "static") === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => savePatternKind(opt.v)}
                      disabled={saving}
                      role="radio"
                      aria-checked={active}
                      className={`text-[11px] px-2.5 py-1 rounded border transition-colors ${
                        active
                          ? "bg-emerald-600 text-white border-emerald-700"
                          : "bg-white text-[#0B1A36] border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid={`rota-style-pattern-kind-${opt.v}`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              {(profile.pattern_kind || "static") === "dynamic" ? (
                <div
                  className="mt-2 space-y-2"
                  data-testid="rota-style-dynamic-explainer"
                >
                  <p className="text-[11px] text-[#0B1A36] leading-relaxed">
                    <span className="font-semibold text-indigo-700">
                      Pattern Engine is active.
                    </span>{" "}
                    NextRota varies assignment patterns across active
                    work cells for the selected cycle week, while
                    Coverage Guard helps prevent changes that would
                    weaken coverage. Times, breaks and empty cells
                    stay fixed.
                  </p>
                  <div className="flex flex-wrap gap-2 pt-1">
                    {/* Preview button — moved here from the chip
                        strip per user request. Same drawer trigger,
                        renamed copy. */}
                    <button
                      type="button"
                      onClick={() => setDynRotPreviewOpen(true)}
                      data-testid="rota-style-dynamic-rotation-preview-btn"
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-indigo-200 bg-indigo-50 text-[11px] font-medium text-indigo-700 hover:bg-indigo-100 transition-colors"
                    >
                      <Eye className="h-3 w-3" />
                      Preview Next Pattern
                    </button>
                    <button
                      type="button"
                      onClick={() => setCgDrawerOpen(true)}
                      data-testid="rota-style-pattern-engine-cg-link"
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-slate-200 bg-white text-[11px] font-medium text-[#0B1A36] hover:border-slate-300 transition-colors"
                    >
                      <ShieldCheck className="h-3 w-3 text-emerald-600" />
                      Set Coverage Guard
                    </button>
                    <Link
                      to="/app/automation"
                      data-testid="rota-style-pattern-engine-autopilot-link"
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-slate-200 bg-white text-[11px] font-medium text-[#0B1A36] hover:border-slate-300 transition-colors"
                    >
                      <Zap className="h-3 w-3 text-indigo-600" />
                      Configure Autopilot
                    </Link>
                  </div>
                </div>
              ) : (
                <p
                  className="text-[11px] text-[#0B1A36] leading-relaxed mt-2"
                  data-testid="rota-style-static-explainer"
                >
                  <span className="font-semibold">Standard mode.</span>{" "}
                  Assignments stay exactly where you place them each
                  cycle.
                </p>
              )}
            </div>

            <p className="text-[11px] text-[#4B5773] leading-relaxed mt-3 pt-3 border-t border-slate-100">
              Time variants (e.g. Morning / Late) can rotate per employee
              via Rotating Shifts. That is a separate feature and does
              not change which assignments appear on which day in your
              Rota Style.
            </p>
          </section>

          {/* A4 — Pattern Summary */}
          <section
            className="rounded-lg border border-slate-200 bg-white p-4"
            data-testid="rota-style-pattern-summary"
          >
            <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold mb-3">
              Pattern summary
            </div>
            <ul className="space-y-2 text-[12px] text-[#0B1A36]">
              <SummaryRow icon={Users} label="Employees" value={summaryEmployees.length} />
              <SummaryRow icon={CalendarDays}
                           label={cycleLen <= 5
                             ? "Workdays in cycle"
                             : "Workdays (Mon to Fri)"}
                           value={workdaySlotsTotal} />
              <SummaryRow icon={ListChecks} label="Total assignments" value={totalAssignments} />
              <SummaryRow icon={Square}
                           label="Empty workday cells"
                           value={workdayEmpty}
                           accent={workdayEmpty === 0 ? "ok" : "warn"} />
            </ul>
          </section>

          {/* A5 — Pattern Health (booleans only). */}
          <section
            className="rounded-lg border border-slate-200 bg-white p-4"
            data-testid="rota-style-pattern-health"
          >
            <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold mb-3">
              Pattern health
            </div>
            <ul className="space-y-2 text-[12px]">
              <HealthRow ok={allWorkdaysHaveTime}
                          okText="All workdays have a shift time"
                          warnText="Some workday cells have no time set" />
              <HealthRow ok={noEmptyWorkdays}
                          okText={cycleLen <= 5
                            ? "No empty cells in the cycle"
                            : "No empty cells on Mon to Fri"}
                          warnText={`${workdayEmpty} workday cells are still empty`} />
              <HealthRow ok={allCellsHaveAssignment}
                          okText="Every cell has at least one assignment"
                          warnText="Some cells have a time but no assignment" />
              {weekendCovered !== null && (
                <HealthRow ok={weekendCovered}
                            okText="Weekend pattern has some cover"
                            warnText="Weekend pattern is empty"
                            informational />
              )}
            </ul>
          </section>

          {/* A6 — Assignment Mix (minutes-weighted, CSS bars only). */}
          <section
            className="rounded-lg border border-slate-200 bg-white p-4"
            data-testid="rota-style-assignment-mix"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold">
                Assignment mix
              </div>
              <div className="text-[9px] uppercase tracking-widest text-[#94A3B8] font-medium">
                By minutes
              </div>
            </div>
            {mix.length === 0 ? (
              <p className="text-[12px] text-[#94A3B8]">
                Add timed assignments to the grid to see your mix.
              </p>
            ) : (
              <ul className="space-y-2.5">
                {mix.map((m) => {
                  const hrs = Math.floor(m.minutes / 60);
                  const mins = m.minutes % 60;
                  const label = hrs > 0
                    ? `${hrs}h${mins > 0 ? ` ${mins}m` : ""}`
                    : `${mins}m`;
                  return (
                    <li
                      key={m.name}
                      className="text-[12px]"
                      data-testid={`rota-style-mix-row-${m.name}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-1.5 text-[#0B1A36] truncate">
                          <span className={`h-2 w-2 rounded-full ${m.palette.dot} shrink-0`} />
                          <span className="truncate">{m.name}</span>
                        </span>
                        <span
                          className="text-[#4B5773] tabular-nums"
                          title={`${m.minutes} minutes across this cycle`}
                        >
                          {m.pct}% <span className="text-[#94A3B8]">({label})</span>
                        </span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded mt-1 overflow-hidden">
                        <div
                          className={`h-full ${m.palette.dot} rounded`}
                          style={{ width: `${m.pct}%` }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </aside>
      </div>

      {editing && (
        <CellEditor
          editing={editing}
          employees={employees}
          categories={categories}
          shiftTemplates={shiftTemplates}
          saving={saving}
          onClose={() => setEditing(null)}
          onSave={saveCell}
          onDelete={deleteCell}
        />
      )}

      {bulkOpen && (
        <BulkApplyDrawer
          cycleLen={cycleLen}
          employees={visibleEmployees}
          categories={categories}
          teams={teams}
          teamFilter={teamFilter}
          shiftTemplates={shiftTemplates}
          saving={bulkSaving}
          onClose={() => setBulkOpen(false)}
          onApply={bulkApplyPattern}
        />
      )}

      {timesOpen && (
        <BulkApplyTimesDrawer
          cycleLen={cycleLen}
          employees={visibleEmployees}
          teams={teams}
          teamFilter={teamFilter}
          cellsByEmpDay={cellsByEmpDay}
          saving={bulkSaving}
          onClose={() => setTimesOpen(false)}
          onApply={bulkApplyTimes}
        />
      )}

      {rotateOpen && (
        <RotateTimesDrawer
          cycleLen={cycleLen}
          employees={visibleEmployees}
          teams={teams}
          teamFilter={teamFilter}
          cellsByEmpDay={cellsByEmpDay}
          saving={bulkSaving}
          onClose={() => setRotateOpen(false)}
          onApply={rotateTimes}
        />
      )}

      {replaceOpen && (
        <ReplaceCategoryDrawer
          cycleLen={cycleLen}
          employees={visibleEmployees}
          categories={categories}
          cells={cells}
          saving={replaceSaving}
          scopeLabel={profile?.team_id
            ? `All employees in ${(cgTeams.find((t) => t.team_id === profile.team_id) || {}).name || "this team"}`
            : null}
          onClose={() => setReplaceOpen(false)}
          onApply={replaceCategory}
        />
      )}

      {adjustOpen && (
        <AdjustMixDrawer
          cycleLen={cycleLen}
          employees={visibleEmployees}
          categories={categories}
          cells={cells}
          saving={adjustSaving}
          scopeLabel={profile?.team_id
            ? `All employees in ${(cgTeams.find((t) => t.team_id === profile.team_id) || {}).name || "this team"}`
            : null}
          onClose={() => setAdjustOpen(false)}
          onApply={adjustMix}
          cgRules={cgRules}
          cgTeams={cgTeams}
          scopeTeamId={profile?.team_id || null}
          onOpenCgSettings={() => setCgDrawerOpen(true)}
        />
      )}

      {changeCycleOpen && (
        <ChangeCycleModal
          currentCycleType={profile.cycle_type}
          cellCount={cells.length}
          saving={changingCycle}
          onClose={() => setChangeCycleOpen(false)}
          onConfirm={changeCycle}
        />
      )}

      {confirmGen && (
        <GenerateConfirmModal
          kind={confirmGen.kind}
          liveCount={confirmGen.liveCount}
          existingStatus={confirmGen.existingStatus}
          weekStart={weekStart}
          weekEnd={confirmGen.weekEnd}
          onCancel={() => setConfirmGen(null)}
          onConfirm={() => runGenerate(false)}
        />
      )}
      <CoverageGuardDrawer
        open={cgDrawerOpen}
        onOpenChange={setCgDrawerOpen}
        categories={categories}
        teams={cgTeams}
        rules={cgRules}
        onChanged={cgReload}
      />
      {/* Phase 2Z.p — Relevant-week Dynamic Rotation preview drawer. */}
      {dynRotPreviewOpen && (
        <DynamicRotationPreviewDrawer
          profile={profile}
          cells={cells}
          employees={employees}
          categories={categories}
          cgRules={cgRules}
          cgTeams={cgTeams}
          weekStart={weekStart}
          onClose={() => setDynRotPreviewOpen(false)}
        />
      )}
      {/* Undo/Redo V1 -- compare-and-warn confirmation. */}
      <UndoRedoConfirmDialog
        mode={rsUrConfirm}
        onCancel={() => setRsUrConfirm(null)}
        onProceed={() => { const m = rsUrConfirm; setRsUrConfirm(null); runRsHistory(m, true); }}
        testidPrefix="rota-style"
      />
    </div>
  );
}

// ---------- R3 (Feb 2026) - Rota Style setup strip ---------------
//
// Small guided-setup card rendered above the existing Rota Style
// tools. Presentational only. Reads:
//   - /api/onboarding/checklist for the R1 boolean
//     `account_setup_complete`
// Renders four items with the approved R3 copy (Coverage Guard, RMI,
// Rota Style pattern, Pattern Engine). Never mentions Automation V1
// or leave automation - those are described on the Automation page.
//
// Dismissible via a localStorage key namespaced by company_id so a
// super_admin toggling tenants does not accidentally hide the strip
// on tenants that have not dismissed it. Once dismissed, the strip
// stays hidden until the manager clears local storage. This is a
// deliberate UX choice; no new backend state is created.

function RotaStyleSetupStrip({ billingMe, companyId }) {
  const dismissKey = `nextrota_rota_style_setup_strip_dismissed:${companyId || "unknown"}`;
  const [dismissed, setDismissed] = useState(() => {
    try { return !!localStorage.getItem(dismissKey); } catch { return false; }
  });
  const [accountSetupComplete, setAccountSetupComplete] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/onboarding/checklist").then((r) => {
      if (!cancelled) {
        setAccountSetupComplete(!!r.data?.account_setup_complete);
      }
    }).catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, []);

  if (dismissed) return null;

  function dismiss() {
    try { localStorage.setItem(dismissKey, "1"); } catch { /* ignore */ }
    setDismissed(true);
  }

  const items = [
    {
      key: "coverage-guard",
      title: "Coverage Guard",
      body: "Set the cover you need by day, time and work area. NextRota highlights gaps before you publish.",
    },
    {
      key: "rmi",
      title: "RMI",
      body: "Give Rota Style adjustment commands using the parser or AI. For example, increase Sales cover on Monday or even out workload across teams. RMI uses Coverage Guard to help keep changes safe.",
    },
    {
      key: "rota-style-pattern",
      title: "Rota Style pattern",
      body: "Build a reusable weekly pattern for each team. Use it when your rota usually follows the same structure.",
    },
    {
      key: "pattern-engine",
      title: "Pattern Engine",
      body: "Choose how your Rota Style repeats. Standard keeps the same pattern each cycle. Advanced options can rotate times or patterns if your team needs it.",
    },
  ];

  return (
    <div className="mb-4 rounded-xl border border-[#E4EAF4] bg-gradient-to-br from-[#F5F8FE] to-[#EFF3FB] px-5 py-4"
         data-testid="rota-style-setup-strip">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-white shadow-sm border border-[#E4EAF4] flex items-center justify-center shrink-0">
          <Sparkles className="h-4 w-4 text-[#2563EB]" />
        </div>
        <div className="flex-1">
          <p className="text-[13px] font-semibold text-[#0B1A36]"
             data-testid="rota-style-setup-strip-title">
            Set up Rota Style
          </p>
          <p className="text-[12px] text-[#4B5773] mt-1"
             data-testid="rota-style-setup-strip-subtitle">
            Use advanced planning tools to build reusable rota patterns,
            check cover and adjust rosters faster.
          </p>
          {accountSetupComplete === false && (
            <p className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 mt-2 inline-flex items-center gap-1"
               data-testid="rota-style-setup-strip-account-incomplete">
              <Info className="h-3 w-3" />
              Complete your Account setup first to get the most out of
              Rota Style.
            </p>
          )}
        </div>
        <button type="button" onClick={dismiss}
                className="text-[#94A3B8] hover:text-[#0B1A36] text-[11px] uppercase tracking-widest font-semibold"
                data-testid="rota-style-setup-strip-dismiss">
          Dismiss
        </button>
      </div>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
           data-testid="rota-style-setup-strip-items">
        {items.map((it) => (
          <div key={it.key}
               data-testid={`rota-style-setup-strip-item-${it.key}`}
               className="rounded-lg border border-[#E4EAF4] bg-white p-3">
            <p className="text-[12px] font-semibold text-[#0B1A36]">
              {it.title}
            </p>
            <p className="text-[11px] text-[#4B5773] mt-1 leading-snug">
              {it.body}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}



function GenerateConfirmModal({
  kind,
  liveCount,
  existingStatus,
  weekStart,
  weekEnd,
  onCancel,
  onConfirm,
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-testid="rota-style-confirm-modal"
    >
      <div className="bg-white rounded-lg shadow-2xl w-[520px] max-w-[92vw] overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <h3 className="font-medium text-base text-[#0B1A36]">
            Heads up before generating
          </h3>
        </div>
        <div className="px-4 py-3 text-sm text-[#0B1A36] space-y-2">
          <p>
            You&apos;re generating from your Rota Style into the week
            starting <strong>{isoToHuman(weekStart)}</strong>
            {weekEnd ? <> through <strong>{isoToHuman(weekEnd)}</strong></> : null}.
          </p>
          {kind === "live" ? (
            <p className="text-[#4B5773]">
              This week already has <strong>{liveCount}</strong> live shift entries.
              The generator only writes draft suggestions. Nothing on
              your live roster will change yet. When you later publish the
              approved draft, NextRota will <strong>only fill empty days</strong>{" "}
              and will never overwrite an existing live allocation.
            </p>
          ) : (
            <p className="text-[#4B5773]">
              A <strong>{existingStatus}</strong> draft already exists for this week.
              If you continue, you&apos;ll be asked to confirm replacing it.
            </p>
          )}
        </div>
        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 flex items-center gap-2 justify-end">
          <Button
            size="sm"
            variant="outline"
            onClick={onCancel}
            data-testid="rota-style-confirm-cancel"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={onConfirm}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="rota-style-confirm-continue"
          >
            <CalendarPlus className="h-3.5 w-3.5 mr-1" />
            Continue and generate draft
          </Button>
        </div>
      </div>
    </div>
  );
}

function CellEditor({
  editing,
  employees,
  categories,
  shiftTemplates = [],
  saving,
  onClose,
  onSave,
  onDelete,
}) {
  const [start, setStart] = useState(editing.start_time || "");
  const [end, setEnd] = useState(editing.end_time || "");
  // Phase 2Z.aj.1 - tracks whether the times currently shown still
  // match the working-pattern prefill. Helper text hides as soon as
  // the manager edits either field, so it doesn't linger as a stale
  // claim. Existing cells never start as prefilled.
  const [prefilledFromPattern, setPrefilledFromPattern] = useState(
    Boolean(editing.prefilled_from_pattern),
  );
  // Phase 2Z.aj - `picked` is now an ordered list of
  // { category_id, minutes|null } so the cell editor can capture per
  // assignment durations. Legacy cells without minutes load as
  // { category_id, minutes: null } which preserves equal-split.
  // TP-AT -- each picked item now ALSO carries optional
  // start_time / end_time clock-time strings ("HH:mm"). When the
  // saved cell already has them, they load as-is. Legacy cells
  // (minutes-only or no minutes) auto-populate from the cell's
  // start/end window and minutes so the time pickers are immediately
  // discoverable, mirroring the Roster allocation modal behaviour.
  const [picked, setPicked] = useState(() => {
    const src = Array.isArray(editing.assignments) ? editing.assignments : [];
    if (!src.length) return [];
    let cursor = editing.start_time || "";
    return src.map((a) => {
      const mins = (typeof a.minutes === "number" && a.minutes > 0)
        ? a.minutes
        : null;
      let startT = a.start_time || null;
      let endT = a.end_time || null;
      if (!startT && cursor) startT = cursor;
      if (startT && !endT && mins) {
        endT = _atAddMinutes(startT, mins) || null;
      }
      if (endT) cursor = endT;
      return {
        category_id: a.category_id,
        minutes: mins,
        start_time: startT,
        end_time: endT,
      };
    });
  });
  // TP-AT -- Advanced toggle hides the raw minutes input and +/-
  // controls. Default off so managers see clock-time pickers as
  // the primary surface.
  const [showAdvancedMinutes, setShowAdvancedMinutes] = useState(false);
  // TP-AT (Rota Style) -- run the auto-distribute once on mount so
  // legacy cells (and freshly-loaded mixes) display the same
  // equal-split timeline that `_sequenceFor` already paints next to
  // each row. No state pollution because user-driven changes happen
  // only after this initial sync.
  const _didInitTimeline = useRef(false);
  const [breakMinutes, setBreakMinutes] = useState(
    String(editing.break_minutes || 0),
  );
  const [breakStart, setBreakStart] = useState(editing.break_start_time || "");
  const emp = employees.find((e) => e.employee_id === editing.employee_id);
  // Phase 2Z.h — week-aware label (e.g. "Mon W2" on biweekly).
  // For cycleLen <= 7 this resolves to the original "Mon" / "Tue" copy.
  const dayLabelText = dayLabel(editing.cycle_day_index,
                                  editing.cycle_length_days || 7);

  function toggle(catId) {
    setPicked((p) => {
      const idx = p.findIndex((a) => a.category_id === catId);
      if (idx >= 0) {
        const next = [...p];
        next.splice(idx, 1);
        // TP-AT -- after removing a chip, redistribute the
        // remaining shift time across the remaining chips so the
        // timeline stays consistent.
        return _redistributeTimeline(next);
      }
      // Phase 2Z.al.4 - if any existing chip already has manual
      // minutes set, default the new chip to the remaining
      // unallocated minutes so the totals match what the manager
      // expects. If no chip has manual minutes yet, leave the new
      // chip as null so the editor stays in equal-split mode.
      const anySet = p.some((a) => {
        const r = Number(a?.minutes);
        return Number.isFinite(r) && r > 0;
      });
      let prefill = null;
      if (anySet) {
        const gross = (start && end)
          ? Math.max(0, (t2m(end) || 0) - (t2m(start) || 0))
          : 0;
        const brk = Math.max(0, Number(breakMinutes) || 0);
        const net = (brk && brk < gross) ? (gross - brk) : gross;
        const allotted = p.reduce((n, a) => {
          const r = Number(a?.minutes);
          return n + (Number.isFinite(r) && r > 0 ? r : 0);
        }, 0);
        const remaining = net - allotted;
        if (remaining > 0) prefill = remaining;
      }
      const withNew = [...p, {
        category_id: catId,
        minutes: prefill,
        start_time: null,
        end_time: null,
      }];
      // TP-AT (Rota Style) -- auto-distribute remaining shift time
      // across all chips that don't have an explicit duration. New
      // chips get an equal share of (cell net minutes - explicit
      // minutes from manually-set chips), accounting for the break
      // window. Mirrors `_sequenceFor` so the chip times stay
      // consistent with the sequence label.
      return _redistributeTimeline(withNew);
    });
  }

  // TP-AT (Rota Style) -- compute clock-time windows for every chip
  // in the mix, equal-splitting the remaining net shift minutes
  // across chips whose `minutes` is null, and using the explicit
  // `minutes` value otherwise. Inserts the break at break_start_time
  // (or immediately after the first share) so chip windows skip the
  // break correctly.
  function _redistributeTimeline(arr) {
    if (!arr || !arr.length) return arr;
    const sStart = t2m(start);
    const sEnd = t2m(end);
    if (sStart == null || sEnd == null || sEnd <= sStart) {
      return arr.map((a) => ({ ...a, start_time: null, end_time: null }));
    }
    const gross = sEnd - sStart;
    const brk = Math.max(0, Number(breakMinutes) || 0);
    const breakLen = (brk > 0 && brk < gross) ? brk : 0;
    const net = gross - breakLen;
    if (net <= 0) {
      return arr.map((a) => ({ ...a, start_time: null, end_time: null }));
    }
    const explicit = arr.reduce((n, a) => {
      const r = Number(a?.minutes);
      return n + (Number.isFinite(r) && r > 0 ? r : 0);
    }, 0);
    const autoCount = arr.filter((a) => {
      const r = Number(a?.minutes);
      return !Number.isFinite(r) || r <= 0;
    }).length;
    const autoTotal = Math.max(0, net - explicit);
    const perAuto = autoCount > 0 ? Math.floor(autoTotal / autoCount) : 0;
    const remainder = autoCount > 0 ? (autoTotal % autoCount) : 0;
    const perRow = arr.map((a, i) => {
      const r = Number(a?.minutes);
      if (Number.isFinite(r) && r > 0) return r;
      // Distribute the leftover minute(s) onto the first auto chips.
      // Use a running counter so the share is consistent regardless
      // of which positions are auto vs explicit.
      let autoIdx = 0;
      for (let j = 0; j < i; j += 1) {
        const rj = Number(arr[j]?.minutes);
        if (!Number.isFinite(rj) || rj <= 0) autoIdx += 1;
      }
      return perAuto + (autoIdx < remainder ? 1 : 0);
    });
    const breakAt = breakStart ? (t2m(breakStart) || null) : null;
    let cursor = sStart;
    let breakInserted = !breakLen;
    return arr.map((a, i) => {
      let segStart = cursor;
      if (!breakInserted && breakAt != null && segStart >= breakAt) {
        cursor += breakLen;
        segStart = cursor;
        breakInserted = true;
      }
      let segEnd = segStart + perRow[i];
      if (!breakInserted && breakAt != null && segStart < breakAt && segEnd > breakAt) {
        segEnd += breakLen;
        breakInserted = true;
      } else if (!breakInserted && breakAt == null && i === 0) {
        // No explicit break_start_time -- insert break after first share.
        segEnd += breakLen;
        breakInserted = true;
      }
      if (segEnd > sEnd) segEnd = sEnd;
      cursor = segEnd;
      const m2h = (m) => {
        const total = ((m % (24 * 60)) + 24 * 60) % (24 * 60);
        return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
      };
      return {
        ...a,
        start_time: m2h(segStart),
        end_time: m2h(segEnd),
      };
    });
  }

  // TP-AT (Rota Style) -- on first render of the cell editor,
  // sync the chip times to the equal-split timeline so the visible
  // pickers always match the sequence label above them. Subsequent
  // user edits (manual time picks, chip add/remove) take over from
  // there.
  useEffect(() => {
    if (_didInitTimeline.current) return;
    if (!picked.length) { _didInitTimeline.current = true; return; }
    const hasWindowLocal = Boolean(start) && Boolean(end);
    if (!hasWindowLocal) { _didInitTimeline.current = true; return; }
    // If any saved row already carries an explicit start_time AND
    // end_time, treat the load as 'manually authored' and trust it;
    // do NOT re-sequence on mount or the manager's saved windows
    // would be silently overwritten by the equal-split timeline.
    const anyAuthored = picked.some((r) => r && r.start_time && r.end_time);
    if (anyAuthored) { _didInitTimeline.current = true; return; }
    setPicked((p) => _redistributeTimeline(p));
    _didInitTimeline.current = true;
  }, [start, end, breakMinutes, breakStart]);

  // TP-AT -- write start_time or end_time on an assignment row.
  // Mirrors RosterPage `setEntryTime`: when only one side is set
  // and the entry already has a duration, auto-fill the missing
  // side; recompute minutes locally when both ends are present.
  function setTimeAt(idx, field, val) {
    setPicked((p) => {
      const next = [...p];
      const cur = next[idx] || {};
      const updated = { ...cur, [field]: val || null };
      const otherField = field === "start_time" ? "end_time" : "start_time";
      const curMins = Number(cur.minutes || 0);
      if (val && !updated[otherField] && curMins > 0) {
        const sign = field === "start_time" ? +1 : -1;
        const filled = _atAddMinutes(val, sign * curMins);
        if (filled) updated[otherField] = filled;
      }
      if (updated.start_time && updated.end_time) {
        const m = _atEntryMins({
          start: updated.start_time,
          end: updated.end_time,
          shiftStart: start,
          shiftEnd: end,
          breakMinutes: Number(breakMinutes) || 0,
          breakStart: breakStart || null,
        });
        if (m != null && m > 0) updated.minutes = m;
      }
      next[idx] = updated;
      return next;
    });
  }

  // TP-AT.RS.8.3 (Feb 2026) -- per-row category picker. Matches
  // the Roster cell editor UX: managers change any row's
  // activity individually. Empty string is legal at edit time
  // (Save is blocked until every row has a category). Used by
  // the row's `<select>` in the assignment mix below.
  function setCategoryAt(idx, catId) {
    setPicked((p) => {
      const next = [...p];
      if (idx < 0 || idx >= next.length) return next;
      next[idx] = { ...next[idx], category_id: catId || "" };
      return next;
    });
  }

  // Phase 2Z.aj - update one assignment's minutes by index. Empty
  // input clears the override (back to equal-split fallback).
  function setMinutesAt(idx, raw) {
    setPicked((p) => {
      const next = [...p];
      const trimmed = String(raw || "").trim();
      if (!trimmed) {
        next[idx] = { ...next[idx], minutes: null };
      } else {
        const m = parseInt(trimmed, 10);
        next[idx] = {
          ...next[idx],
          minutes: Number.isFinite(m) && m > 0 ? m : null,
        };
      }
      return next;
    });
  }

  function t2m(t) {
    if (!t) return null;
    const [h, m] = t.split(":");
    return parseInt(h, 10) * 60 + parseInt(m, 10);
  }

  // Phase 2Z.aj - calculations for the mix panel. All read-only.
  const _hasWindow = Boolean(start) && Boolean(end);
  const _grossMins = _hasWindow
    ? Math.max(0, (t2m(end) || 0) - (t2m(start) || 0))
    : 0;
  // Phase 2A Step 4 - the cell editor is now entries-based. Every
  // row (Work AND Away) consumes minutes from the gross window, so
  // the "net" available for assignment is the GROSS wall time. The
  // legacy `breakMinutes` state is preserved only for the
  // save-mirror fallback when no Away row exists.
  const _breakMins = 0;
  const _netMins = _hasWindow
    ? Math.max(0, _grossMins - (_breakMins < _grossMins ? _breakMins : 0))
    : 0;
  const _equalSplit = (picked.length && _netMins)
    ? Math.floor(_netMins / picked.length)
    : 0;
  const _equalRemainder = (picked.length && _netMins)
    ? _netMins % picked.length
    : 0;
  const _allottedMins = picked.reduce(
    (n, a) => {
      // Phase 2Z.al.3 - coerce defensively via Number() so the total
      // always reflects the literal value sitting in the input field.
      // Any non-finite or non-positive value contributes zero so an
      // empty / cleared input doesn't poison the sum.
      const raw = Number(a?.minutes);
      return n + (Number.isFinite(raw) && raw > 0 ? raw : 0);
    },
    0,
  );
  const _anyMinutesSet = picked.some(
    (a) => {
      const raw = Number(a?.minutes);
      return Number.isFinite(raw) && raw > 0;
    },
  );
  const _allMinutesSet = picked.length > 0
    && picked.every((a) => {
      const raw = Number(a?.minutes);
      return Number.isFinite(raw) && raw > 0;
    });
  // TP-AT.RS.2 -- when break_start_time is unset, the break has no
  // explicit slot, so each chip's `entryMinutesFromTimes` returns
  // raw wall minutes (no deduction). Comparing those wall sums
  // against `_netMins` therefore double-counts the break and fires
  // a phantom 'exceed' warning even though the layout is correct
  // (e.g. Finance 09:00-15:00 + Operations 15:00-17:00 with a 60m
  // implicit break inside Finance). When this case holds AND at
  // least one chip carries an explicit window, compare against
  // gross instead -- gaps between chip windows naturally absorb
  // the break, and a back-to-back layout means the break lives
  // inside one of the chip windows.
  const _anyChipHasTimes = picked.some((a) => a.start_time && a.end_time);
  const _breakEmbedded = _hasWindow
    && _breakMins > 0
    && !breakStart
    && _anyChipHasTimes;
  const _effectiveTarget = _breakEmbedded ? _grossMins : _netMins;
  const _customHonoured = _hasWindow
    && _allMinutesSet
    && _allottedMins === _effectiveTarget;
  const _exceedsBy = _hasWindow && _allottedMins > _effectiveTarget
    ? _allottedMins - _effectiveTarget
    : 0;
  const _unallocated = _hasWindow
    && _anyMinutesSet
    && _allottedMins < _effectiveTarget
    ? _effectiveTarget - _allottedMins
    : 0;

  function fmtDuration(mins) {
    // TP-AT -- friendly format first, raw minutes hidden behind
    // a hover tooltip. Matches the Roster allocation modal which
    // shows "2h 30m" as the primary surface and keeps minutes
    // behind an Advanced toggle.
    if (!mins) return "0m";
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h && m) return `${h}h ${m}m`;
    if (h) return `${h}h`;
    return `${m}m`;
  }
  function _addMins(hhmm, mins) {
    if (!hhmm) return "";
    const total = (t2m(hhmm) || 0) + mins;
    const h = Math.floor(total / 60) % 24;
    const m = total % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  }

  // Sequenced start/end per assignment based on order, shift start,
  // break window. Break is inserted at break_start_time if provided,
  // otherwise immediately after the first assignment's gross share.
  // Pure presentation - the backend computes minutes independently.
  function _sequenceFor(idx) {
    if (!_hasWindow || !picked.length) return null;
    const eachUsesCustom = _customHonoured;
    const perRow = picked.map((a, i) => {
      if (eachUsesCustom) return a.minutes;
      return _equalSplit + (i < _equalRemainder ? 1 : 0);
    });
    const totalRow = perRow.reduce((n, m) => n + m, 0);
    if (totalRow <= 0) return null;
    let cursor = t2m(start) || 0;
    const breakAt = breakStart ? (t2m(breakStart) || null) : null;
    const breakLen = (_breakMins > 0 && _breakMins < _grossMins)
      ? _breakMins
      : 0;
    let breakInserted = !breakLen;
    const segments = [];
    for (let i = 0; i < perRow.length; i += 1) {
      let segStart = cursor;
      let segEnd = cursor + perRow[i];
      if (!breakInserted) {
        const insertAt = (breakAt !== null && breakAt >= segStart
                          && breakAt < segEnd)
          ? breakAt
          : null;
        if (insertAt !== null) {
          // Split this segment around the break.
          const extra = breakLen;
          // Push the break by extending the segment end.
          segEnd = segEnd + extra;
          breakInserted = true;
        } else if (breakAt === null && i === 0) {
          // No explicit break-start - place after segment 0.
          segEnd = segEnd + breakLen;
          breakInserted = true;
        }
      }
      segments.push([segStart, segEnd - (segments.length ? 0 : 0)]);
      cursor = segEnd;
    }
    if (idx < 0 || idx >= segments.length) return null;
    const [s, e] = segments[idx];
    const toHHMM = (mins) => {
      const total = mins;
      const h = Math.floor(total / 60) % 24;
      const m = total % 60;
      return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    };
    return `${toHHMM(s)} to ${toHHMM(e)}`;
  }

  function submit() {
    if (!picked.length) {
      toast.error("Pick at least one assignment.");
      return;
    }
    // TP-AT.RS.8.2 (Feb 2026) -- reject blank Work rows before
    // send. Template-applied blank Work rows arrive with
    // `category_id: ""`; the backend `RotaStyleAssignmentRef`
    // model rejects them with 422 anyway, but a friendly toast
    // is clearer for the manager.
    if (picked.some(p => !p.category_id)) {
      toast.error(
        "Pick a Work activity for every blank row before saving.");
      return;
    }
    if ((start && !end) || (!start && end)) {
      toast.error(
        "Provide both start and end, or leave both empty for full day.",
      );
      return;
    }
    // Phase 2A Step 4 - legacy validation of `breakMinutes` /
    // `breakStart` removed. The visible Break inputs no longer
    // exist; shadow fields are mirrored from Away rows on save by
    // `_p2aMirrorBreakFromPicked`, so no separate range check is
    // needed here.
    // Phase 2Z.aj - block save when assignment minutes exceed the
    // shift's net minutes. Under-allocation and "no minutes set" are
    // permitted (we fall back to equal split) so the manager can save
    // an in-progress mix without being held up.
    if (_exceedsBy > 0) {
      toast.error(
        `Assignments add up to ${_allottedMins} min, but only `
          + `${_effectiveTarget} min are available in this shift. `
          + `Reduce a duration to continue.`,
      );
      return;
    }
    onSave({
      employee_id: editing.employee_id,
      cycle_day_index: editing.cycle_day_index,
      existing_cell_id: editing.existing_cell_id,
      start_time: start,
      end_time: end,
      // Phase 2Z.aj - pass through the ordered list with optional
      // per-assignment minutes. saveCell drops any non-positive
      // value, which keeps legacy payload shape on simple cells.
      assignments: picked.map((a) => ({
        category_id: a.category_id,
        minutes: (typeof a.minutes === "number" && a.minutes > 0)
          ? a.minutes
          : null,
        // TP-AT -- send per-assignment clock-time window when both
        // ends are set. Backend drops partial pairs silently so
        // legacy cells stay byte-identical.
        start_time: (a.start_time && a.end_time) ? a.start_time : null,
        end_time: (a.start_time && a.end_time) ? a.end_time : null,
      })),
      // Phase 2A Step 4 - shadow break fields mirrored from Away
      // rows. When NO Away row exists, `break_minutes` is 0 and
      // `break_start_time` is empty so legacy consumers cannot
      // double-deduct an old break. When an Away row exists, the
      // first Away row's start_time and the total Away minutes are
      // written as shadows for downstream consumers (Coverage
      // Guard, Reports, MyRoster) that have not yet migrated.
      ..._p2aMirrorBreakFromPicked(picked, categories, start, end),
    });
  }

  // Phase 2A Step 4 - `breakDisabled` was the gate for the (now
  // removed) visible Break inputs; kept for any downstream reads
  // but unused in the JSX below.
  const breakDisabled = !start || !end;
  void breakDisabled;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-testid="rota-style-editor-modal"
    >
      <div className="bg-white rounded-lg shadow-2xl w-[560px] max-w-[92vw] overflow-hidden max-h-[92vh] flex flex-col">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-medium text-base text-[#0B1A36]">
            {emp?.name} · {dayLabelText}
          </h3>
          <button
            type="button"
            onClick={onClose}
            data-testid="rota-style-editor-close"
            className="text-[#4B5773] hover:text-[#0B1A36]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-4 py-3 space-y-3 text-sm overflow-y-auto">
          {/* TP-AT.RS.8.2 (Feb 2026) -- Apply Shift Template in
              Rota Style cell editor. UI-only prefill: picking a
              template sets start / end / break AND populates the
              `picked` list with the same Work / Break / blank
              Work shape the Roster cell editor produces. Blank
              Work rows carry `category_id: ""` so the existing
              save validation catches them before send. Byte
              stable fallback when no template is chosen. This
              does NOT touch Rota Style Apply, the seeder, RSA
              or the materialiser. */}
          {shiftTemplates.length > 0 && (
            <div className="rounded-md bg-[#F0F5FF] border border-[#DBE7FE] p-2 flex items-center gap-2 flex-wrap"
                 data-testid="rota-style-editor-shift-tpl-picker">
              <span className="text-[10px] uppercase tracking-widest text-[#1D4ED8] font-semibold">Apply template</span>
              <select
                data-testid="rota-style-editor-shift-tpl-select"
                className="h-8 rounded-md border border-slate-200 bg-white text-xs px-2 min-w-[220px]"
                value=""
                onChange={(e) => {
                  const id = e.target.value;
                  if (!id) return;
                  const tpl = shiftTemplates.find(t => t.shift_template_id === id);
                  if (!tpl) return;
                  setStart(tpl.start_time || "");
                  setEnd(tpl.end_time || "");
                  setBreakMinutes(String(tpl.break_minutes || 0));
                  setBreakStart(tpl.break_start_time || "");
                  // Convert template entries into `picked` rows.
                  // deriveBlankWorkGapEntries fills the envelope
                  // with blank Work rows (`category_id: ""`) so
                  // the manager can pick the correct Work
                  // activity for their business before saving.
                  const tplEntries = (tpl.entries || []).map(en => ({
                    category_id: en.category_id || "",
                    minutes: Number(en.minutes),
                    start_time: en.start_time || null,
                    end_time: en.end_time || null,
                  }));
                  const derived = deriveBlankWorkGapEntries(
                    tpl.start_time, tpl.end_time, tplEntries);
                  setPicked(derived.map(r => ({
                    category_id: r.category_id || "",
                    minutes: Number(r.minutes) > 0 ? Number(r.minutes) : null,
                    start_time: r.start_time || null,
                    end_time: r.end_time || null,
                  })));
                  // Reset the select back to placeholder so a
                  // second pick fires onChange again.
                  e.target.value = "";
                }}
              >
                <option value="">Pick a saved template...</option>
                {shiftTemplates
                  .filter(t => t.is_active !== false)
                  .map(t => (
                    <option key={t.shift_template_id} value={t.shift_template_id}
                            data-testid={`rota-style-editor-shift-tpl-opt-${t.shift_template_id}`}>
                      {t.name} ({t.start_time}-{t.end_time})
                    </option>
                  ))}
              </select>
              <span className="text-[10px] text-[#4B5773]">Prefills the shape. Pick each blank Work row&apos;s activity individually below.</span>
            </div>
          )}
          {/* TP-AT.RS.8.3 (Feb 2026) -- the earlier global "Work
              activity for blank Work rows" panel was removed.
              Managers pick each blank Work row's activity via
              the row's own selector below, matching the
              single-cell Roster editor UX. `submit()` still
              refuses to save until every picked row has a
              category_id (the backend requires it anyway). */}
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-[#4B5773] mb-1">
              Assignments (one or more)
            </label>
            <div
              className="flex flex-wrap gap-1.5"
              data-testid="rota-style-editor-categories"
            >
              {/* Phase 2Z.m UX gap fix — deactivated categories are
                  hidden from the picker so they cannot be added as
                  NEW assignments. If a cell already references one
                  (because it was added before deactivation), we keep
                  the chip visible so the manager can deselect it. */}
              {categories
                .filter((c) => c.is_active !== false
                  || picked.some((p) => p.category_id === c.category_id))
                .map((c) => {
                  const isPicked = picked.some(
                    (p) => p.category_id === c.category_id,
                  );
                  return (
                    <button
                      type="button"
                      key={c.category_id}
                      onClick={() => toggle(c.category_id)}
                      className={`text-xs px-2 py-1 rounded border ${
                        isPicked
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-white border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid={`rota-style-cat-${c.category_id}`}
                    >
                      {c.name}
                      {/* Phase 2A Step 4 - small Away pill mirrors
                          the Roster cell editor convention so a
                          manager can distinguish Away (e.g. Break)
                          from Work categories in the picker. */}
                      {c.kind === "away" && (
                        <span
                          className="ml-1 inline-block px-1 py-0.5 text-[8px] rounded bg-violet-100 text-violet-800 ring-1 ring-violet-200 align-middle"
                          data-testid={`rota-style-cat-away-${c.category_id}`}
                        >
                          Away
                        </span>
                      )}
                      {c.is_active === false && isPicked && (
                        <span
                          className="ml-1 inline-block px-1 py-0.5 text-[8px] rounded bg-amber-100 text-amber-800 ring-1 ring-amber-200 align-middle"
                          data-testid={`rota-style-cat-deactivated-${c.category_id}`}
                        >
                          Deactivated
                        </span>
                      )}
                    </button>
                  );
                })}
            </div>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-[#4B5773] mb-1">
              Time window (leave blank for a full day)
            </label>
            <div className="flex items-center gap-2">
              <Input
                type="time"
                value={start}
                onChange={(e) => {
                  setStart(e.target.value);
                  // Phase 2Z.aj.1 - user edited the time so the
                  // "Using employee working pattern hours" helper
                  // is no longer accurate.
                  if (prefilledFromPattern) setPrefilledFromPattern(false);
                }}
                className="text-xs"
                data-testid="rota-style-editor-start"
              />
              <span className="text-[#4B5773] text-xs">to</span>
              <Input
                type="time"
                value={end}
                onChange={(e) => {
                  setEnd(e.target.value);
                  if (prefilledFromPattern) setPrefilledFromPattern(false);
                }}
                className="text-xs"
                data-testid="rota-style-editor-end"
              />
            </div>
            {/* Phase 2Z.aj.1 - subtle helper text shown on a brand-new
                cell that was just prefilled from the employee's
                working pattern. Hides the moment the manager edits
                either field. */}
            {prefilledFromPattern && (start || end) && (
              <p
                className="text-[10px] text-[#4B5773] mt-1"
                data-testid="rota-style-editor-prefill-helper"
              >
                Using employee working pattern hours. Adjust or clear
                if you want a different window.
              </p>
            )}
            {/* Phase 2Z.aj.1 - explicit opt-in for legacy/blank cells.
                Only rendered when the editor is sitting on an
                EXISTING cell whose start/end are both blank AND the
                employee has working-pattern hours configured. Click
                only updates local state - never calls an API and
                never auto-saves. */}
            {editing.existing_cell_id
              && !start
              && !end
              && (emp?.working_pattern?.default_start
                  || emp?.working_pattern?.default_end) && (
              <button
                type="button"
                onClick={() => {
                  const wp = emp?.working_pattern || {};
                  setStart(wp.default_start || "09:00");
                  setEnd(wp.default_end || "17:00");
                  setBreakMinutes(String(
                    typeof wp.default_break_minutes === "number"
                      ? wp.default_break_minutes
                      : 60,
                  ));
                  setPrefilledFromPattern(true);
                }}
                className="mt-1 text-[10px] text-emerald-700 hover:text-emerald-800 hover:underline font-medium inline-flex items-center gap-1"
                data-testid="rota-style-editor-use-employee-hours"
              >
                Use employee hours
              </button>
            )}
          </div>
          {/* Phase 2A Step 4 - visible Break (min) + Break start
              inputs removed. Break is now added as a normal Away
              assignment row from the picker above. Shadow fields
              `break_minutes` / `break_start_time` are mirrored on
              save from the first Away row so downstream consumers
              (Coverage Guard, Reports, MyRoster) stay byte-stable. */}
          {/* Phase 2Z.aj - per-assignment durations panel. Optional;
              if all rows have minutes summing to the shift's net
              minutes we honour them on publish, otherwise we fall
              back to today's equal-split behaviour. */}
          {picked.length > 0 && (
            <div
              className="rounded-md border border-slate-200 bg-slate-50/60 p-3"
              data-testid="rota-style-editor-mix"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-widest text-[#4B5773] font-semibold">
                  Assignment mix
                </div>
                {_hasWindow && (
                  <div
                    className="text-[11px] text-[#4B5773]"
                    data-testid="rota-style-editor-mix-total"
                  >
                    {_anyMinutesSet ? (
                      <>
                        <span
                          className={
                            _customHonoured
                              ? "text-emerald-700 font-medium"
                              : (_exceedsBy
                                  ? "text-rose-700 font-medium"
                                  : "text-amber-700 font-medium")
                          }
                        >
                          {fmtDuration(_allottedMins)}
                        </span>
                        <span className="text-[#94A3B8]">
                          {" "}of {fmtDuration(_effectiveTarget)} shift
                        </span>
                      </>
                    ) : (
                      <span className="text-[#94A3B8]">
                        Equal split of {fmtDuration(_effectiveTarget)}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="space-y-1.5">
                {picked.map((a, idx) => {
                  const cat = categories.find(
                    (c) => c.category_id === a.category_id,
                  );
                  const fallbackMins = _hasWindow && picked.length
                    ? _equalSplit + (idx < _equalRemainder ? 1 : 0)
                    : 0;
                  const effectiveMins = (typeof a.minutes === "number"
                                          && a.minutes > 0)
                    ? a.minutes
                    : fallbackMins;
                  const sequenceLabel = _sequenceFor(idx);
                  return (
                    <div
                      key={`row-${idx}`}
                      className="flex items-center gap-2 text-xs bg-white border border-slate-200 rounded-md px-2 py-1.5"
                      data-testid={`rota-style-editor-mix-row-${a.category_id || "blank"}-${idx}`}
                    >
                      <span className="text-[10px] text-[#94A3B8] w-4">
                        {idx + 1}.
                      </span>
                      {/* TP-AT.RS.8.3 -- per-row category picker.
                          Replaces the earlier read-only span so
                          managers can pick each blank Work row's
                          activity individually (matching the
                          Roster cell editor). Break/Away rows
                          also editable in case the manager needs
                          to swap Break for Meeting etc. Placeholder
                          "Select activity" makes blank rows
                          obvious. Save is blocked until every
                          row has a category (see submit()). */}
                      <select
                        value={a.category_id || ""}
                        onChange={(e)=>setCategoryAt(idx, e.target.value)}
                        className={`h-8 rounded-md border text-xs px-2 flex-1 min-w-[140px] ${a.category_id ? "border-slate-200 bg-white" : "border-amber-300 bg-amber-50 text-amber-900 font-medium"}`}
                        data-testid={`rota-style-editor-mix-cat-${idx}`}
                      >
                        <option value="">Select activity...</option>
                        {(categories || [])
                          .filter(c => c.is_active !== false)
                          .map(c => (
                            <option key={c.category_id} value={c.category_id}
                                    data-row-kind={c.kind}>
                              {c.name}{c.kind === "away" ? "  (Away)" : ""}
                            </option>
                          ))}
                      </select>
                      {/* TP-AT -- per-assignment clock-time pickers.
                          Primary surface for managers. Falls back
                          to disabled inputs when the cell has no
                          time window yet (Set start/end first). */}
                      <Input
                        type="time"
                        value={a.start_time || ""}
                        disabled={!_hasWindow}
                        onChange={(e)=>setTimeAt(idx, "start_time", e.target.value)}
                        className="text-xs w-[96px]"
                        data-testid={`rota-style-editor-mix-start-${a.category_id}`}
                      />
                      <span className="text-[10px] text-[#94A3B8]">-</span>
                      <Input
                        type="time"
                        value={a.end_time || ""}
                        disabled={!_hasWindow}
                        onChange={(e)=>setTimeAt(idx, "end_time", e.target.value)}
                        className="text-xs w-[96px]"
                        data-testid={`rota-style-editor-mix-end-${a.category_id}`}
                      />
                      <span
                        data-testid={`rota-style-editor-mix-duration-${a.category_id}`}
                        className={`text-[10px] px-2 py-1 rounded-full ${
                          effectiveMins > 0
                            ? "bg-[#DBE7FE] text-[#1D4ED8]"
                            : "bg-slate-100 text-slate-400"
                        }`}
                      >
                        {_hasWindow
                          ? (effectiveMins > 0 ? _atFormatHM(effectiveMins) : "-")
                          : "set time"}
                      </span>
                      {showAdvancedMinutes && (
                      <div className="flex items-center gap-0.5">
                        <button
                          type="button"
                          onClick={() => {
                            const cur = Number(a.minutes);
                            const base = (Number.isFinite(cur) && cur > 0)
                              ? cur
                              : (fallbackMins || 0);
                            const next = Math.max(1, base - 15);
                            setMinutesAt(idx, String(next));
                          }}
                          disabled={!_hasWindow}
                          className="text-xs w-5 h-7 rounded border border-slate-200 bg-white text-[#0B1A36] hover:bg-slate-50 disabled:opacity-40 leading-none"
                          data-testid={`rota-style-editor-mix-minus-${a.category_id}`}
                          aria-label="Decrease by 15 minutes"
                        >
                          −
                        </button>
                        <Input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          placeholder={fallbackMins ? String(fallbackMins) : "min"}
                          value={a.minutes ?? ""}
                          onChange={(e) => {
                            // Phase 2Z.al.5 - manual typing only.
                            // Strip everything but digits so the
                            // controlled input never holds a stray
                            // character that would confuse the sum.
                            const digits = String(e.target.value || "")
                              .replace(/\D+/g, "");
                            setMinutesAt(idx, digits);
                          }}
                          className="text-xs w-[64px] text-center"
                          disabled={!_hasWindow}
                          data-testid={`rota-style-editor-mix-minutes-${a.category_id}`}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const cur = Number(a.minutes);
                            const base = (Number.isFinite(cur) && cur > 0)
                              ? cur
                              : (fallbackMins || 0);
                            const next = base + 15;
                            setMinutesAt(idx, String(next));
                          }}
                          disabled={!_hasWindow}
                          className="text-xs w-5 h-7 rounded border border-slate-200 bg-white text-[#0B1A36] hover:bg-slate-50 disabled:opacity-40 leading-none"
                          data-testid={`rota-style-editor-mix-plus-${a.category_id}`}
                          aria-label="Increase by 15 minutes"
                        >
                          +
                        </button>
                      </div>
                      )}
                      <span
                        className="text-[10px] text-[#4B5773] w-[110px] text-right hidden"
                        data-testid={`rota-style-editor-mix-helper-${a.category_id}`}
                      >
                        {_hasWindow ? fmtDuration(effectiveMins) : "Set a time first"}
                      </span>
                      <span
                        className="text-[10px] text-[#94A3B8] w-[110px] text-right tabular-nums"
                        data-testid={`rota-style-editor-mix-window-${a.category_id}`}
                      >
                        {sequenceLabel || ""}
                      </span>
                    </div>
                  );
                })}
              </div>
              {_hasWindow && _customHonoured && (
                <p
                  className="mt-2 text-[10px] text-emerald-700"
                  data-testid="rota-style-editor-mix-status-ok"
                >
                  Each assignment will use the exact duration you set.
                </p>
              )}
              {_hasWindow && _unallocated > 0 && (
                <p
                  className="mt-2 text-[10px] text-amber-700"
                  data-testid="rota-style-editor-mix-status-unallocated"
                >
                  {fmtDuration(_unallocated)} unallocated. Until every
                  assignment has a duration that sums to the shift,
                  we will split the shift evenly across assignments.
                </p>
              )}
              {_hasWindow && _exceedsBy > 0 && (
                <p
                  className="mt-2 text-[10px] text-rose-700"
                  data-testid="rota-style-editor-mix-status-exceeds"
                >
                  Assignments exceed available shift time by{" "}
                  {fmtDuration(_exceedsBy)}. Reduce a duration to save.
                </p>
              )}
              {!_hasWindow && (
                <p
                  className="mt-2 text-[10px] text-[#4B5773]"
                  data-testid="rota-style-editor-mix-status-needs-time"
                >
                  Set a start and end time to enter per-assignment durations.
                </p>
              )}
              {/* TP-AT -- overlap warning + Advanced toggle. The
                  Roster modal uses the same pattern. */}
              {_hasWindow && (() => {
                const overlaps = _atOverlaps(picked);
                return (
                  <div className="mt-2 flex items-center justify-between gap-2">
                    {overlaps.length > 0 ? (
                      <span
                        data-testid="rota-style-editor-mix-overlap"
                        className="text-[10px] text-rose-700"
                      >
                        Overlap detected between {overlaps.length === 1
                          ? "two assignments"
                          : `${overlaps.length} pairs`}.
                      </span>
                    ) : <span />}
                    <button
                      type="button"
                      onClick={()=>setShowAdvancedMinutes(v=>!v)}
                      data-testid="rota-style-editor-mix-toggle-advanced"
                      className="text-[10px] text-[#4B5773] underline-offset-2 hover:underline"
                    >
                      {showAdvancedMinutes ? "Hide minutes" : "Advanced: show minutes"}
                    </button>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 flex items-center gap-2 justify-end">
          {editing.existing_cell_id && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onDelete(editing.existing_cell_id)}
              className="text-red-600 hover:text-red-700 mr-auto"
              data-testid="rota-style-editor-delete"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" />
              Remove
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={onClose}
            data-testid="rota-style-editor-cancel"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={submit}
            disabled={saving || _exceedsBy > 0}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="rota-style-editor-save"
          >
            {saving ? (
              <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5 mr-1" />
            )}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}


// ---- Right-rail helper sub-components -------------------------------

function SummaryRow({ icon: Icon, label, value, accent }) {
  const accentClass =
    accent === "warn"
      ? "text-amber-700"
      : accent === "ok"
        ? "text-emerald-700"
        : "text-[#0B1A36]";
  return (
    <li className="flex items-center justify-between gap-2">
      <span className="flex items-center gap-2 text-[#4B5773]">
        <Icon className="h-3.5 w-3.5 text-[#94A3B8]" />
        <span>{label}</span>
      </span>
      <span className={`font-semibold tabular-nums ${accentClass}`}>
        {value}
      </span>
    </li>
  );
}

function HealthRow({ ok, okText, warnText, informational }) {
  if (ok) {
    return (
      <li className="flex items-start gap-2 text-[#0B1A36]">
        <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-emerald-600 shrink-0" />
        <span>{okText}</span>
      </li>
    );
  }
  const colour = informational ? "text-[#4B5773]" : "text-amber-800";
  const iconColour = informational ? "text-[#94A3B8]" : "text-amber-500";
  return (
    <li className={`flex items-start gap-2 ${colour}`}>
      <AlertTriangle className={`h-3.5 w-3.5 mt-0.5 ${iconColour} shrink-0`} />
      <span>{warnText}</span>
    </li>
  );
}


// ---- Phase 2Z.b — Bulk Apply bottom drawer ---------------------------

const BULK_ACTIONS = [
  {
    key: "add",
    label: "Add",
    hint: "Add assignments alongside anything already in the cell.",
  },
  {
    key: "replace",
    label: "Replace",
    hint: "Replace the existing assignments on the selected cells.",
  },
  {
    key: "only_empty",
    label: "Only empty cells",
    hint: "Apply only where cells are currently empty.",
  },
  {
    key: "clear",
    label: "Clear cells",
    hint: "Remove the selected Rota Style cells entirely (time + assignments).",
  },
];

const DAY_LABELS_LONG = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function BulkApplyDrawer({
  cycleLen,
  employees,
  categories,
  teams,
  teamFilter,
  shiftTemplates = [],
  saving,
  onClose,
  onApply,
}) {
  // Local selection state — never touches the parent until Apply.
  const [empPick, setEmpPick] = useState(
    () => new Set(employees.map((e) => e.employee_id)),
  );
  const [empSearch, setEmpSearch] = useState("");
  const [internalTeam, setInternalTeam] = useState(teamFilter);
  const [dayPick, setDayPick] = useState(() => {
    // Phase 2Z.h — for weekly profiles (cycleLen ≤ 7) the default
    // selection is byte-for-byte unchanged: Mon-Fri of the single
    // week. For multi-week profiles, default to ALL workdays in the
    // cycle (skip cycle-weekend indexes 5, 6, 12, 13, …) so customers
    // don't accidentally Bulk Apply to Week 1 only.
    if (cycleLen <= 7) {
      return new Set(Array.from({ length: Math.min(cycleLen, 5) }, (_, i) => i));
    }
    const set = new Set();
    for (let i = 0; i < cycleLen; i++) {
      if (i % 7 < 5) set.add(i);
    }
    return set;
  });
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("17:00");
  // TP-AT.RS.8.4 (Feb 2026) -- unified per-row assignment model.
  // `rows` replaces the old multi-checkbox `catPick` Set so this
  // drawer now uses the SAME row shape as the Roster cell editor
  // and Roster Bulk Apply Shift. Each row carries:
  //   category_id (string | ""), _wanted_kind ("work" | "away"),
  //   start_time, end_time (HH:mm), minutes.
  // Blank Work rows (category_id === "" + _wanted_kind === "work")
  // are produced by `deriveBlankWorkGapEntries` when a template is
  // applied; the manager fills each row's activity picker
  // individually. `applyAll` blocks send until every row has a
  // category (or the action is `clear`).
  const [rows, setRows] = useState([]);
  const [action, setAction] = useState("add");

  // Employees, scoped to the team currently picked inside the drawer
  // (defaults to the team filter on the page).
  const scopedEmployees = useMemo(() => {
    let list = employees;
    if (internalTeam !== "all") {
      list = list.filter((e) => (e.team_id || "") === internalTeam);
    }
    if (empSearch.trim()) {
      const q = empSearch.trim().toLowerCase();
      list = list.filter((e) => (e.name || "").toLowerCase().includes(q));
    }
    return list;
  }, [employees, internalTeam, empSearch]);

  const allowsAssignments = action !== "clear";
  const requiresTime = action !== "clear";

  // Derived preview line.
  const N_emp = empPick.size;
  const N_day = dayPick.size;
  const N_cells = N_emp * N_day;
  const preview = (() => {
    if (N_cells === 0) return "Select at least one employee and one day.";
    if (action === "clear") {
      return `You're about to remove cells on ${N_emp} employees × ${N_day} days = ${N_cells} cells.`;
    }
    const verb = action === "replace" ? "replace"
      : action === "only_empty" ? "fill empty cells on"
      : "add to";
    return `You're about to ${verb} ${N_emp} employees × ${N_day} days = ${N_cells} cells.`;
  })();

  function toggleEmp(id) {
    setEmpPick((p) => {
      const n = new Set(p);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }
  function toggleDay(i) {
    setDayPick((p) => {
      const n = new Set(p);
      n.has(i) ? n.delete(i) : n.add(i);
      return n;
    });
  }

  // TP-AT.RS.8.4 (Feb 2026) -- per-row helpers. Match the shape
  // used by RosterPage's BulkApplyDialog so the manager sees a
  // consistent editor across every "shift shape" surface.
  function _rowMinsFromWindow(start, end) {
    if (!start || !end) return 0;
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    let mins = (eh * 60 + em) - (sh * 60 + sm);
    if (mins < 0) mins += 24 * 60; // overnight
    return Math.max(0, mins);
  }
  function updateRow(i, field, value) {
    setRows((prev) => prev.map((r, idx) => {
      if (idx !== i) return r;
      const next = { ...r, [field]: value };
      if (field === "start_time" || field === "end_time") {
        next.minutes = _rowMinsFromWindow(next.start_time, next.end_time);
      }
      return next;
    }));
  }
  function removeRow(i) {
    setRows((prev) => prev.filter((_, idx) => idx !== i));
  }
  function addRow(kindHint) {
    // Break/Away rows can preselect the seeded Break category
    // (matches the Roster cell editor `addEntry("away")` path).
    let preselectedCatId = "";
    if (kindHint === "away") {
      const brk = (categories || []).find(
        (c) => c.kind === "away" && (c.code === "break" || (c.name || "").toLowerCase() === "break")
      );
      preselectedCatId = brk ? brk.category_id : "";
    }
    setRows((prev) => [
      ...prev,
      {
        category_id: preselectedCatId,
        start_time: startTime || "",
        end_time: endTime || "",
        minutes: _rowMinsFromWindow(startTime, endTime),
        _wanted_kind: kindHint,
      },
    ]);
  }
  function applyTemplate(id) {
    if (!id) return;
    const tpl = shiftTemplates.find((t) => t.shift_template_id === id);
    if (!tpl) return;
    setStartTime(tpl.start_time || startTime);
    setEndTime(tpl.end_time || endTime);
    const tplEntries = (tpl.entries || []).map((en) => ({
      category_id: en.category_id || "",
      minutes: Number(en.minutes),
      start_time: en.start_time || null,
      end_time: en.end_time || null,
    }));
    const derived = deriveBlankWorkGapEntries(
      tpl.start_time, tpl.end_time, tplEntries);
    setRows(derived.map((r) => ({
      category_id: r.category_id || "",
      minutes: Number(r.minutes) > 0 ? Number(r.minutes) : 0,
      start_time: r.start_time || "",
      end_time: r.end_time || "",
      _wanted_kind: r._wanted_kind,
    })));
  }

  function applyAll() {
    if (empPick.size === 0 || dayPick.size === 0) {
      toast.error("Pick at least one employee and one day.");
      return;
    }
    if (requiresTime && (!startTime || !endTime)) {
      toast.error("Enter a start and end time.");
      return;
    }
    if (allowsAssignments) {
      if (rows.length === 0) {
        toast.error("Add at least one assignment row.");
        return;
      }
      // TP-AT.RS.8.4 -- refuse to send when any row is still blank
      // (template-derived `_wanted_kind === "work"` with no
      // category, or any manually added row that was never
      // filled). Mirrors the Roster cell editor + Roster Bulk
      // Apply Shift guard toasts.
      if (rows.some((r) => !r.category_id)) {
        toast.error(
          "Pick an activity for every blank Work row before applying.");
        return;
      }
    }
    onApply({
      employeeIds: Array.from(empPick),
      dayIndices: Array.from(dayPick).sort((a, b) => a - b),
      startTime: requiresTime ? startTime : "09:00",
      endTime: requiresTime ? endTime : "17:00",
      // TP-AT.RS.8.4 -- forward the per-row assignments so the
      // parent handler can write cell.assignments verbatim (each
      // row preserves its own start_time / end_time / minutes).
      // Legacy `categoryIds` field is dropped -- the parent now
      // reads `rows` for the row-mode payload.
      rows,
      action,
    });
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/30"
      onClick={onClose}
      data-testid="rota-style-bulk-drawer"
    >
      <div
        className="bg-white rounded-t-xl shadow-2xl w-full max-w-[1200px] max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div className="flex items-center gap-2">
            <Grid3x3 className="h-4 w-4 text-[#4B5773]" />
            <h3 className="font-semibold text-base text-[#0B1A36]">
              Bulk apply pattern
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#4B5773] hover:text-[#0B1A36]"
            data-testid="rota-style-bulk-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-5 p-5">
          {/* 1. Employees */}
          <section data-testid="rota-style-bulk-employees">
            <SectionLabel n={1} title="Employees" />
            <select
              value={internalTeam}
              onChange={(e) => setInternalTeam(e.target.value)}
              className="text-xs px-2.5 py-1.5 rounded border border-slate-200 bg-white w-full mb-2"
            >
              <option value="all">All teams</option>
              {teams.map((t) => (
                <option key={t.id || "no-team"} value={t.id || ""}>
                  {t.name}
                </option>
              ))}
            </select>
            <Input
              placeholder="Search employees…"
              value={empSearch}
              onChange={(e) => setEmpSearch(e.target.value)}
              className="text-xs mb-2"
            />
            <div className="flex items-center justify-between text-[11px] text-[#4B5773] mb-2">
              <span>
                {empPick.size}/{scopedEmployees.length} selected
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="text-[11px] text-emerald-700 hover:underline"
                  onClick={() =>
                    setEmpPick(new Set(scopedEmployees.map((e) => e.employee_id)))
                  }
                  data-testid="rota-style-bulk-emp-all"
                >
                  Select all
                </button>
                <button
                  type="button"
                  className="text-[11px] text-[#4B5773] hover:underline"
                  onClick={() => setEmpPick(new Set())}
                  data-testid="rota-style-bulk-emp-clear"
                >
                  Clear
                </button>
              </div>
            </div>
            <div className="border border-slate-200 rounded max-h-[260px] overflow-y-auto bg-slate-50/30">
              {scopedEmployees.length === 0 ? (
                <div className="text-[11px] text-[#94A3B8] px-2.5 py-3">
                  No employees match this filter.
                </div>
              ) : (
                scopedEmployees.map((e) => (
                  <label
                    key={e.employee_id}
                    className="flex items-center gap-2 px-2.5 py-1.5 text-[12px] text-[#0B1A36] hover:bg-white cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={empPick.has(e.employee_id)}
                      onChange={() => toggleEmp(e.employee_id)}
                      data-testid={`rota-style-bulk-emp-${e.employee_id}`}
                    />
                    <span className="truncate">{e.name}</span>
                  </label>
                ))
              )}
            </div>
          </section>

          {/* 2. Action */}
          <section data-testid="rota-style-bulk-action">
            <SectionLabel n={2} title="Action" />
            <div className="space-y-1.5">
              {BULK_ACTIONS.map((a) => (
                <label
                  key={a.key}
                  className={`block rounded-md border p-2.5 cursor-pointer transition-colors ${
                    action === a.key
                      ? "border-emerald-300 bg-emerald-50/40"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid={`rota-style-bulk-action-${a.key}`}
                >
                  <div className="flex items-start gap-2">
                    <input
                      type="radio"
                      name="bulk-action"
                      checked={action === a.key}
                      onChange={() => setAction(a.key)}
                      className="mt-0.5"
                    />
                    <div>
                      <div className="text-[12px] font-medium text-[#0B1A36]">
                        {a.label}
                      </div>
                      <div className="text-[11px] text-[#4B5773] mt-0.5">
                        {a.hint}
                      </div>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </section>

          {/* 3. Time + Days */}
          <section data-testid="rota-style-bulk-time-days">
            <SectionLabel n={3} title="Time" />
            <div
              className={`grid grid-cols-2 gap-2 ${
                requiresTime ? "" : "opacity-40 pointer-events-none"
              }`}
            >
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] mb-1">
                  Start
                </div>
                <Input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="text-xs"
                  data-testid="rota-style-bulk-start-time"
                />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] mb-1">
                  End
                </div>
                <Input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="text-xs"
                  data-testid="rota-style-bulk-end-time"
                />
              </div>
            </div>
            {/* Phase 2A Step 6 - visible Break (min) and Break start
                inputs removed. Managers add Break as a normal Away
                category in the assignments picker below; shadow
                fields are mirrored on apply. */}

            <SectionLabel n={4} title="Days" className="mt-4" />
            <div className="flex gap-2 flex-wrap">
              <PresetDays
                cycleLen={cycleLen}
                onPick={(set) => setDayPick(set)}
              />
            </div>
            <div className="flex gap-1.5 flex-wrap mt-2">
              {Array.from({ length: cycleLen }).map((_, i) => {
                const label = dayLabel(i, cycleLen);
                const on = dayPick.has(i);
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => toggleDay(i)}
                    className={`text-[11px] px-2 py-1 rounded border ${
                      on
                        ? "bg-emerald-600 text-white border-emerald-700"
                        : "bg-white text-[#0B1A36] border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`rota-style-bulk-day-${i}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </section>

          {/* 5. Assignments + Preview */}
          <section data-testid="rota-style-bulk-assignments">
            <SectionLabel n={5} title="Assignments" />
            {/* TP-AT.RS.8.4 (Feb 2026) -- Apply template picker.
                Prefills the shift envelope + a row per template
                entry. Break rows come pre-filled with their Away
                category; blank Work gaps land as `_wanted_kind:
                "work"` + empty `category_id` and the manager picks
                each row's activity individually below. Byte-safe:
                only visible when the tenant has active templates. */}
            {shiftTemplates.length > 0 && allowsAssignments && (
              <div className="rounded-md bg-[#F0F5FF] border border-[#DBE7FE] p-2 mb-2 flex items-center gap-2 flex-wrap"
                   data-testid="rota-style-bulk-shift-tpl-picker">
                <span className="text-[10px] uppercase tracking-widest text-[#1D4ED8] font-semibold">Apply template</span>
                <select
                  data-testid="rota-style-bulk-shift-tpl-select"
                  className="h-8 rounded-md border border-slate-200 bg-white text-xs px-2 flex-1 min-w-[180px]"
                  value=""
                  onChange={(e) => {
                    applyTemplate(e.target.value);
                    e.target.value = "";
                  }}
                >
                  <option value="">Pick a saved template...</option>
                  {shiftTemplates.filter((t) => t.is_active !== false).map((t) => (
                    <option key={t.shift_template_id} value={t.shift_template_id}
                            data-testid={`rota-style-bulk-shift-tpl-opt-${t.shift_template_id}`}>
                      {t.name} ({t.start_time}-{t.end_time})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div
              className={`space-y-2 ${
                allowsAssignments ? "" : "opacity-40 pointer-events-none"
              }`}
              data-testid="rota-style-bulk-rows"
            >
              {rows.length === 0 && allowsAssignments && (
                <div className="text-[11px] text-[#94A3B8] px-2.5 py-3 border border-dashed border-slate-200 rounded-md text-center">
                  No rows yet. Apply a template above, or add a row below.
                </div>
              )}
              {rows.map((r, i) => {
                const mins = Number(r.minutes || 0);
                return (
                  <div key={i} className="bg-white rounded-md border border-slate-200 p-2"
                       data-testid={`rota-style-bulk-row-${i}`}>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[10px] text-[#94A3B8] w-4 shrink-0">{i + 1}.</span>
                      <select
                        value={r.category_id || ""}
                        onChange={(e) => updateRow(i, "category_id", e.target.value)}
                        data-testid={`rota-style-bulk-row-cat-${i}`}
                        className={`h-8 rounded-md border text-xs px-2 flex-1 min-w-[140px] ${
                          r.category_id
                            ? "border-slate-200 bg-white"
                            : "border-amber-300 bg-amber-50 text-amber-900 font-medium"
                        }`}
                      >
                        <option value="">
                          {r._wanted_kind === "work"
                            ? "Select work activity..."
                            : r._wanted_kind === "away"
                              ? "Select away activity..."
                              : "Select activity..."}
                        </option>
                        {(categories || [])
                          .filter((c) => c.is_active !== false || c.category_id === r.category_id)
                          .filter((c) => r._wanted_kind ? c.kind === r._wanted_kind : true)
                          .map((c) => (
                            <option key={c.category_id} value={c.category_id}>
                              {c.name}{c.kind === "away" ? "  (Away)" : ""}
                            </option>
                          ))}
                      </select>
                      <Input
                        type="time"
                        value={r.start_time || ""}
                        onChange={(e) => updateRow(i, "start_time", e.target.value)}
                        data-testid={`rota-style-bulk-row-start-${i}`}
                        aria-label="Start time"
                        className="h-8 w-[96px] text-xs"
                      />
                      <span className="text-[10px] text-[#94A3B8]">-</span>
                      <Input
                        type="time"
                        value={r.end_time || ""}
                        onChange={(e) => updateRow(i, "end_time", e.target.value)}
                        data-testid={`rota-style-bulk-row-end-${i}`}
                        aria-label="End time"
                        className="h-8 w-[96px] text-xs"
                      />
                      <span
                        data-testid={`rota-style-bulk-row-duration-${i}`}
                        className={`text-[10px] px-2 py-1 rounded-full whitespace-nowrap ${
                          mins > 0 ? "bg-[#DBE7FE] text-[#1D4ED8]" : "bg-slate-100 text-slate-400"
                        }`}
                      >
                        {mins > 0 ? `${Math.floor(mins / 60)}h${mins % 60 ? ` ${mins % 60}m` : ""}` : "set time"}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeRow(i)}
                        data-testid={`rota-style-bulk-row-remove-${i}`}
                        className="text-[#94A3B8] hover:text-red-600"
                        aria-label="Remove row"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
              {allowsAssignments && (
                <div className="flex gap-2 pt-1">
                  <Button
                    type="button" size="sm" variant="outline"
                    onClick={() => addRow("work")}
                    data-testid="rota-style-bulk-add-work"
                    className="text-xs"
                  >
                    <Plus className="h-3 w-3 mr-1" /> Add work
                  </Button>
                  <Button
                    type="button" size="sm" variant="outline"
                    onClick={() => addRow("away")}
                    data-testid="rota-style-bulk-add-away"
                    className="text-xs"
                  >
                    <Plus className="h-3 w-3 mr-1" /> Add break / away
                  </Button>
                </div>
              )}
            </div>

            <div className="mt-3 rounded-md border border-slate-200 bg-slate-50/60 p-2.5">
              <div
                className="text-[11px] text-[#0B1A36] leading-snug"
                data-testid="rota-style-bulk-preview"
              >
                {preview}
              </div>
            </div>
          </section>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50/60 flex items-center gap-2 justify-end sticky bottom-0">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={saving}
            data-testid="rota-style-bulk-cancel"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={applyAll}
            disabled={saving || N_cells === 0}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="rota-style-bulk-apply"
          >
            {saving ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
            )}
            Apply pattern
          </Button>
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ n, title, className = "" }) {
  return (
    <div className={`text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold mb-2 ${className}`}>
      {n}. {title}
    </div>
  );
}

// Phase 2Z.i — Bulk apply times drawer. Reuses cellsByEmpDay rather
// than refetching; never reads assignments from the UI; never creates
// new cells (skip-only). Same atomic PUT endpoint as the existing
// Bulk Apply Pattern.
function BulkApplyTimesDrawer({
  cycleLen, employees, teams, teamFilter,
  cellsByEmpDay, saving, onClose, onApply,
}) {
  const [empPick, setEmpPick] = useState(
    () => new Set(employees.map((e) => e.employee_id)),
  );
  const [empSearch, setEmpSearch] = useState("");
  const [internalTeam, setInternalTeam] = useState(teamFilter);
  const [dayPick, setDayPick] = useState(() => {
    if (cycleLen <= 7) {
      return new Set(Array.from({ length: Math.min(cycleLen, 5) }, (_, i) => i));
    }
    const s = new Set();
    for (let i = 0; i < cycleLen; i++) if (i % 7 < 5) s.add(i);
    return s;
  });
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("17:00");
  // Phase 2A Step 7 - local break state removed alongside the
  // visible inputs. Bulk Apply Times never writes break shadow
  // fields; the parent handler preserves each cell's existing
  // break verbatim.

  const scopedEmployees = useMemo(() => {
    let list = employees;
    if (internalTeam !== "all") {
      list = list.filter((e) => (e.team_id || "") === internalTeam);
    }
    if (empSearch.trim()) {
      const q = empSearch.trim().toLowerCase();
      list = list.filter((e) => (e.name || "").toLowerCase().includes(q));
    }
    return list;
  }, [employees, internalTeam, empSearch]);

  // Counts that will be written — pre-flight against cellsByEmpDay so
  // the customer sees a realistic preview before they click Apply.
  const preview = useMemo(() => {
    let willUpdate = 0;
    let skipEmpty = 0;
    let skipSplit = 0;
    for (const eid of empPick) {
      for (const dayIdx of dayPick) {
        const existing = cellsByEmpDay[`${eid}|${dayIdx}`] || [];
        if (existing.length === 0) skipEmpty += 1;
        else if (existing.length > 1) skipSplit += 1;
        else willUpdate += 1;
      }
    }
    return { willUpdate, skipEmpty, skipSplit };
  }, [empPick, dayPick, cellsByEmpDay]);

  const N_day = dayPick.size;

  function toggleEmp(id) {
    setEmpPick((p) => {
      const n = new Set(p);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }
  function toggleDay(i) {
    setDayPick((p) => {
      const n = new Set(p);
      n.has(i) ? n.delete(i) : n.add(i);
      return n;
    });
  }

  function applyAll() {
    if (empPick.size === 0 || dayPick.size === 0) {
      toast.error("Pick at least one employee and one day.");
      return;
    }
    if (!startTime || !endTime) {
      toast.error("Enter a start and end time.");
      return;
    }
    if (preview.willUpdate === 0) {
      toast.error("No populated single-cell slots in this selection.");
      return;
    }
    const ok = window.confirm(
      `You are about to update ${preview.willUpdate} cell${preview.willUpdate === 1 ? "" : "s"} across ${N_day} day${N_day === 1 ? "" : "s"}. Assignments will be preserved.`,
    );
    if (!ok) return;
    onApply({
      employeeIds: Array.from(empPick),
      dayIndices: Array.from(dayPick).sort((a, b) => a - b),
      startTime, endTime,
      // Phase 2A Step 7 - break fields intentionally omitted; the
      // parent handler preserves each cell's existing shadow break
      // verbatim. Non-magical behaviour by design.
    });
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/30"
      onClick={onClose}
      data-testid="rota-style-bulk-apply-times-drawer"
    >
      <div
        className="bg-white rounded-t-xl shadow-2xl w-full max-w-[1000px] max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-[#4B5773]" />
            <h3 className="font-semibold text-base text-[#0B1A36]">
              Bulk apply times
            </h3>
          </div>
          <button
            type="button" onClick={onClose}
            className="text-[#4B5773] hover:text-[#0B1A36]"
            data-testid="rota-style-bulk-apply-times-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="text-[11px] text-[#4B5773] px-5 pt-3 leading-relaxed">
          Updates the start/end and break fields on the selected cells only.
          Assignments stay exactly as they are. Empty slots and split-shift
          slots are skipped.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 p-5">
          {/* 1. Employees */}
          <section data-testid="rota-style-bulk-apply-times-employees">
            <SectionLabel n={1} title="Employees" />
            <select
              value={internalTeam}
              onChange={(e) => setInternalTeam(e.target.value)}
              className="text-xs px-2.5 py-1.5 rounded border border-slate-200 bg-white w-full mb-2"
            >
              <option value="all">All teams</option>
              {teams.map((t) => (
                <option key={t.id || "no-team"} value={t.id || ""}>
                  {t.name}
                </option>
              ))}
            </select>
            <Input
              placeholder="Search employees…"
              value={empSearch}
              onChange={(e) => setEmpSearch(e.target.value)}
              className="text-xs mb-2"
            />
            <div className="flex items-center justify-between text-[11px] text-[#4B5773] mb-2">
              <span>{empPick.size}/{scopedEmployees.length} selected</span>
              <div className="flex gap-2">
                <button type="button"
                  className="text-[11px] text-emerald-700 hover:underline"
                  onClick={() => setEmpPick(new Set(scopedEmployees.map((e) => e.employee_id)))}>
                  Select all
                </button>
                <button type="button"
                  className="text-[11px] text-[#4B5773] hover:underline"
                  onClick={() => setEmpPick(new Set())}>
                  Clear
                </button>
              </div>
            </div>
            <div className="border border-slate-200 rounded max-h-[260px] overflow-y-auto bg-slate-50/30">
              {scopedEmployees.length === 0 ? (
                <div className="text-[11px] text-[#94A3B8] px-2.5 py-3">
                  No employees match this filter.
                </div>
              ) : (
                scopedEmployees.map((e) => (
                  <label key={e.employee_id}
                    className="flex items-center gap-2 px-2.5 py-1.5 text-[12px] text-[#0B1A36] hover:bg-white cursor-pointer">
                    <input type="checkbox"
                      checked={empPick.has(e.employee_id)}
                      onChange={() => toggleEmp(e.employee_id)} />
                    <span className="truncate">{e.name}</span>
                  </label>
                ))
              )}
            </div>
          </section>

          {/* 2. Days */}
          <section data-testid="rota-style-bulk-apply-times-days">
            <SectionLabel n={2} title="Days" />
            <div className="flex flex-wrap gap-1.5 mb-2">
              <PresetDays cycleLen={cycleLen} onPick={(s) => setDayPick(s)} />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {Array.from({ length: cycleLen }).map((_, i) => {
                const on = dayPick.has(i);
                return (
                  <button key={i} type="button" onClick={() => toggleDay(i)}
                    className={`text-[11px] px-2 py-1 rounded border ${on ? "border-emerald-500 bg-emerald-50 text-emerald-900" : "border-slate-200 bg-white text-[#0B1A36]"}`}>
                    {dayLabel(i, cycleLen)}
                  </button>
                );
              })}
            </div>
          </section>

          {/* 3. Times */}
          <section data-testid="rota-style-bulk-apply-times-fields">
            <SectionLabel n={3} title="Times" />
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#94A3B8]">Start</label>
                  <Input type="time" value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="text-xs"
                    data-testid="rota-style-bulk-apply-times-start" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#94A3B8]">End</label>
                  <Input type="time" value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="text-xs"
                    data-testid="rota-style-bulk-apply-times-end" />
                </div>
              </div>
              {/* Phase 2A Step 7 - visible Break fields removed.
                  Bulk Apply Times now updates start/end only. Cell
                  Away rows and shadow `break_minutes` are left
                  untouched - non-magical behaviour by design. If a
                  manager needs to change a break, they open the
                  cell editor or use Bulk Apply Pattern. */}
            </div>
          </section>
        </div>

        <div className="px-5 py-3.5 border-t border-slate-200 bg-slate-50/60 flex items-center gap-3 sticky bottom-0">
          <div className="text-[11px] text-[#4B5773] flex-1 leading-relaxed"
               data-testid="rota-style-bulk-apply-times-preview">
            Will update <span className="font-medium text-[#0B1A36]">{preview.willUpdate}</span> cell{preview.willUpdate === 1 ? "" : "s"}.
            {preview.skipEmpty > 0 && (
              <> Skipping <span className="font-medium">{preview.skipEmpty}</span> empty slot{preview.skipEmpty === 1 ? "" : "s"}.</>
            )}
            {preview.skipSplit > 0 && (
              <> Skipping <span className="font-medium">{preview.skipSplit}</span> split-shift slot{preview.skipSplit === 1 ? "" : "s"}. Open them manually.</>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={applyAll} disabled={saving}
                   data-testid="rota-style-bulk-apply-times-apply">
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
            Apply
          </Button>
        </div>
      </div>
    </div>
  );
}


// Phase 2Z.i — Rotate times drawer. Deterministically rotates start/
// end/break fields between selected employees on selected days. Never
// touches assignments. Never creates cells. Skips days with <2 single-
// cell employees. One atomic PUT through the existing endpoint.
function RotateTimesDrawer({
  cycleLen, employees, teams, teamFilter,
  cellsByEmpDay, saving, onClose, onApply,
}) {
  const [empPick, setEmpPick] = useState(
    () => new Set(employees.map((e) => e.employee_id)),
  );
  const [empSearch, setEmpSearch] = useState("");
  const [internalTeam, setInternalTeam] = useState(teamFilter);
  const [dayPick, setDayPick] = useState(() => {
    if (cycleLen <= 7) {
      return new Set(Array.from({ length: Math.min(cycleLen, 5) }, (_, i) => i));
    }
    const s = new Set();
    for (let i = 0; i < cycleLen; i++) if (i % 7 < 5) s.add(i);
    return s;
  });
  const [direction, setDirection] = useState("forward"); // forward | backward
  const [step, setStep] = useState(1);

  const scopedEmployees = useMemo(() => {
    let list = employees;
    if (internalTeam !== "all") {
      list = list.filter((e) => (e.team_id || "") === internalTeam);
    }
    if (empSearch.trim()) {
      const q = empSearch.trim().toLowerCase();
      list = list.filter((e) => (e.name || "").toLowerCase().includes(q));
    }
    return list;
  }, [employees, internalTeam, empSearch]);

  // Pre-flight preview — counts the cells that will actually be
  // written. Same skip rules as the handler (need ≥2 single-cell
  // employees per day) so the customer can trust the number.
  const preview = useMemo(() => {
    let willRotate = 0;
    let skippedDays = 0;
    let skippedSplit = 0;
    const empOrdered = Array.from(empPick);
    const stepN = Math.max(1, Number(step) || 1);
    for (const dayIdx of dayPick) {
      let pairsCount = 0;
      let hadSplit = false;
      for (const eid of empOrdered) {
        const ex = cellsByEmpDay[`${eid}|${dayIdx}`] || [];
        if (ex.length === 1) pairsCount += 1;
        else if (ex.length > 1) hadSplit = true;
      }
      if (hadSplit) skippedSplit += 1;
      if (pairsCount < 2) { skippedDays += 1; continue; }
      const shift = stepN % pairsCount;
      // If shift is 0 (e.g., step % N == 0), no cells move.
      if (shift === 0) continue;
      willRotate += pairsCount;
    }
    return { willRotate, skippedDays, skippedSplit };
  }, [empPick, dayPick, cellsByEmpDay, step]);

  function toggleEmp(id) {
    setEmpPick((p) => {
      const n = new Set(p);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }
  function toggleDay(i) {
    setDayPick((p) => {
      const n = new Set(p);
      n.has(i) ? n.delete(i) : n.add(i);
      return n;
    });
  }

  function applyAll() {
    if (empPick.size < 2 || dayPick.size === 0) {
      toast.error("Pick at least two employees and one day.");
      return;
    }
    if (preview.willRotate === 0) {
      toast.error("Nothing will rotate with the current selection.");
      return;
    }
    const ok = window.confirm(
      `You are about to rotate times on ${preview.willRotate} cell${preview.willRotate === 1 ? "" : "s"} across ${dayPick.size - preview.skippedDays} day${(dayPick.size - preview.skippedDays) === 1 ? "" : "s"}. Assignments will be preserved.`,
    );
    if (!ok) return;
    onApply({
      employeeIds: Array.from(empPick),
      dayIndices: Array.from(dayPick).sort((a, b) => a - b),
      direction,
      step: Math.max(1, Number(step) || 1),
    });
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/30"
      onClick={onClose}
      data-testid="rota-style-rotate-times-drawer"
    >
      <div
        className="bg-white rounded-t-xl shadow-2xl w-full max-w-[1000px] max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div className="flex items-center gap-2">
            <RotateCcw className="h-4 w-4 text-[#4B5773]" />
            <h3 className="font-semibold text-base text-[#0B1A36]">
              Rotate times
            </h3>
          </div>
          <button
            type="button" onClick={onClose}
            className="text-[#4B5773] hover:text-[#0B1A36]"
            data-testid="rota-style-rotate-times-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="text-[11px] text-[#4B5773] px-5 pt-3 leading-relaxed">
          Rotates existing start/end and break times between selected
          employees on each selected day. Assignments stay tied to each
          employee. Days with fewer than two populated cells and
          split-shift slots are skipped.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 p-5">
          {/* 1. Employees */}
          <section data-testid="rota-style-rotate-times-employees">
            <SectionLabel n={1} title="Employees" />
            <select
              value={internalTeam}
              onChange={(e) => setInternalTeam(e.target.value)}
              className="text-xs px-2.5 py-1.5 rounded border border-slate-200 bg-white w-full mb-2"
            >
              <option value="all">All teams</option>
              {teams.map((t) => (
                <option key={t.id || "no-team"} value={t.id || ""}>
                  {t.name}
                </option>
              ))}
            </select>
            <Input
              placeholder="Search employees…"
              value={empSearch}
              onChange={(e) => setEmpSearch(e.target.value)}
              className="text-xs mb-2"
            />
            <div className="flex items-center justify-between text-[11px] text-[#4B5773] mb-2">
              <span>{empPick.size}/{scopedEmployees.length} selected</span>
              <div className="flex gap-2">
                <button type="button"
                  className="text-[11px] text-emerald-700 hover:underline"
                  onClick={() => setEmpPick(new Set(scopedEmployees.map((e) => e.employee_id)))}>
                  Select all
                </button>
                <button type="button"
                  className="text-[11px] text-[#4B5773] hover:underline"
                  onClick={() => setEmpPick(new Set())}>
                  Clear
                </button>
              </div>
            </div>
            <div className="border border-slate-200 rounded max-h-[260px] overflow-y-auto bg-slate-50/30">
              {scopedEmployees.length === 0 ? (
                <div className="text-[11px] text-[#94A3B8] px-2.5 py-3">
                  No employees match this filter.
                </div>
              ) : (
                scopedEmployees.map((e) => (
                  <label key={e.employee_id}
                    className="flex items-center gap-2 px-2.5 py-1.5 text-[12px] text-[#0B1A36] hover:bg-white cursor-pointer">
                    <input type="checkbox"
                      checked={empPick.has(e.employee_id)}
                      onChange={() => toggleEmp(e.employee_id)} />
                    <span className="truncate">{e.name}</span>
                  </label>
                ))
              )}
            </div>
          </section>

          {/* 2. Days */}
          <section data-testid="rota-style-rotate-times-days">
            <SectionLabel n={2} title="Days" />
            <div className="flex flex-wrap gap-1.5 mb-2">
              <PresetDays cycleLen={cycleLen} onPick={(s) => setDayPick(s)} />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {Array.from({ length: cycleLen }).map((_, i) => {
                const on = dayPick.has(i);
                return (
                  <button key={i} type="button" onClick={() => toggleDay(i)}
                    className={`text-[11px] px-2 py-1 rounded border ${on ? "border-emerald-500 bg-emerald-50 text-emerald-900" : "border-slate-200 bg-white text-[#0B1A36]"}`}>
                    {dayLabel(i, cycleLen)}
                  </button>
                );
              })}
            </div>
          </section>

          {/* 3. Rotation */}
          <section data-testid="rota-style-rotate-times-controls">
            <SectionLabel n={3} title="Rotation" />
            <div className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#94A3B8]">Direction</label>
                <div className="flex gap-2 mt-1">
                  <button type="button"
                    onClick={() => setDirection("forward")}
                    data-testid="rota-style-rotate-times-direction-forward"
                    className={`flex-1 text-[12px] px-2.5 py-1.5 rounded border ${direction === "forward" ? "border-emerald-500 bg-emerald-50 text-emerald-900" : "border-slate-200 bg-white text-[#0B1A36]"}`}>
                    Forward
                  </button>
                  <button type="button"
                    onClick={() => setDirection("backward")}
                    data-testid="rota-style-rotate-times-direction-backward"
                    className={`flex-1 text-[12px] px-2.5 py-1.5 rounded border ${direction === "backward" ? "border-emerald-500 bg-emerald-50 text-emerald-900" : "border-slate-200 bg-white text-[#0B1A36]"}`}>
                    Backward
                  </button>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#94A3B8]">Step</label>
                <Input type="number" min={1} max={50}
                  value={step}
                  onChange={(e) => setStep(parseInt(e.target.value, 10) || 1)}
                  className="text-xs"
                  data-testid="rota-style-rotate-times-step" />
                <p className="text-[10px] text-[#94A3B8] mt-1 leading-relaxed">
                  Each cell takes the times of the employee {step} position{Number(step) === 1 ? "" : "s"} {direction === "forward" ? "after" : "before"} it.
                </p>
              </div>
            </div>
          </section>
        </div>

        <div className="px-5 py-3.5 border-t border-slate-200 bg-slate-50/60 flex items-center gap-3 sticky bottom-0">
          <div className="text-[11px] text-[#4B5773] flex-1 leading-relaxed"
               data-testid="rota-style-rotate-times-preview">
            Will rotate <span className="font-medium text-[#0B1A36]">{preview.willRotate}</span> cell{preview.willRotate === 1 ? "" : "s"}.
            {preview.skippedDays > 0 && (
              <> Skipping <span className="font-medium">{preview.skippedDays}</span> day{preview.skippedDays === 1 ? "" : "s"} (need 2+ populated cells).</>
            )}
            {preview.skippedSplit > 0 && (
              <> {preview.skippedSplit} day{preview.skippedSplit === 1 ? "" : "s"} had split-shift cells. Open them manually.</>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={applyAll} disabled={saving}
                   data-testid="rota-style-rotate-times-apply">
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5 mr-1.5" />}
            Rotate
          </Button>
        </div>
      </div>
    </div>
  );
}

// Phase 2Z.p — Relevant-week Dynamic Rotation preview drawer.
//
// Read-only. Renders a per-employee × per-day before/after table
// for the cycle-week that the next Generate would seed. Pure
// derivation off the existing `rotateRelevantWeekCells` helper —
// no api.put, no api.post, no setCells, no engine call. Closing
// the drawer is the only way out; there is NO commit button.
//
// `dynShift` matches the engine's deterministic step:
//   delta_days = (next_monday - anchor).days
//   dyn_shift  = delta_days // cycle_len   (number of full cycle
//                iterations elapsed)
// The helper modulos this by the active-day count itself, so the
// chip's "Week N of M" always lines up with what Stage 3 will
// emit.
function DynamicRotationPreviewDrawer({
  profile, cells, employees, categories, cgRules, cgTeams,
  weekStart, onClose,
}) {
  const catById = useMemo(() => {
    const m = new Map();
    for (const c of (categories || [])) m.set(c.category_id, c);
    return m;
  }, [categories]);

  // Cycle-week + dyn_shift derivation. Pure JS — mirrors the
  // engine's deterministic step exactly. `weekStart` is the
  // user-selected "Generate for week starting" ISO date, so
  // changing the date field instantly re-targets the preview.
  const { cw, dynShift, weekRange, activeDaysByEmp } = useMemo(() => {
    const empty = { cw: null, dynShift: 0, weekRange: null, activeDaysByEmp: new Map() };
    const parseISO = (iso) => {
      if (!iso || typeof iso !== "string") return null;
      const [yy, mm, dd] = iso.split("-").map(Number);
      if (!yy || !mm || !dd) return null;
      return new Date(Date.UTC(yy, mm - 1, dd));
    };
    const target = parseISO(weekStart) || nextMondayUTC();
    const cwOut = computeCycleWeek({ profile, targetWeekStart: target });
    if (!cwOut) return empty;
    const cycleLen = Math.max(1, Number(profile?.cycle_length_days) || 7);
    const anchor = parseISO(profile?.cycle_anchor_date);
    if (!anchor) return { ...empty, cw: cwOut };
    const deltaDays = Math.floor(
      (target.getTime() - anchor.getTime()) / (1000 * 60 * 60 * 24),
    );
    // The engine uses `delta_days // cycle_len` for the rotation
    // step (number of full cycles elapsed). We mirror that here so
    // the preview's rotation matches what Stage 3 will emit.
    const shift = Math.floor(deltaDays / cycleLen);
    const wr = weekDayRange(cwOut.weekInCycle);
    // Active rotation cells = days inside the week range that have
    // exactly one cell with at least one assignment for the employee.
    // Matches the helper's filter byte-for-byte — empty days stay
    // empty, multi-cell days stay static. Pure tally per employee.
    const adByEmp = new Map();
    for (const e of (employees || [])) {
      const dayCells = new Map();
      for (const c of (cells || [])) {
        if (c.employee_id !== e.employee_id) continue;
        if (typeof c.cycle_day_index !== "number") continue;
        if (c.cycle_day_index < wr.start || c.cycle_day_index >= wr.end) continue;
        const list = dayCells.get(c.cycle_day_index) || [];
        list.push(c);
        dayCells.set(c.cycle_day_index, list);
      }
      const active = [];
      for (let d = wr.start; d < wr.end; d += 1) {
        const list = dayCells.get(d) || [];
        if (list.length !== 1) continue;
        if ((list[0].assignments || []).length === 0) continue;
        active.push(d);
      }
      adByEmp.set(e.employee_id, active);
    }
    return {
      cw: cwOut, dynShift: shift, weekRange: wr, activeDaysByEmp: adByEmp,
    };
  }, [profile, cells, employees, weekStart]);

  // Phase 2Z.p Stage 4 — Coverage Guard row-level advisory.
  //
  // For each employee, compare baseline (no rotation) vs after-this-
  // row-only rotation. If the rotation makes ANY Coverage Guard rule
  // STRICTLY WORSE for an `coverage_id` (severity climbs), flag the
  // row as "skip-advised" so the preview shows an amber Skip chip.
  //
  // Phase 2Z.p Stage 4a — bug fix: the previous check compared
  // against `r.status === "red"`, but `evaluateRotaStyleCoverage`
  // returns per-rule status as one of `"ok" | "risk" | "under"`
  // (the `"red"` colour exists only on the OVERALL badge). The old
  // check therefore never matched → every row was marked safe.
  // Replaced with a numeric severity comparison so all three
  // transitions are detected:
  //   ok → risk        worse (severity climbs 0→1)
  //   ok → under       worse (severity climbs 0→2)
  //   risk → under     worse (severity climbs 1→2)
  //   improvements / same-status / risk→ok / under→ok all SAFE.
  const cgAdvisoryByEmp = useMemo(() => {
    const out = new Map();
    const rules = cgRules || [];
    if (!cw || rules.length === 0) return out;
    const cycleLen = cw.cycleLengthDays;
    // TP-CG.team — same scope rule as the page-level badge: drop
    // cross-team rules when this profile is team-scoped.
    const scopeTeamId = profile?.team_id || null;
    const baseline = evaluateRotaStyleCoverage({
      cells, employees, categories, rules,
      cycle_days: cycleLen, teams: cgTeams,
      scope_team_id: scopeTeamId,
    });
    // severity: ok=0, risk=1, under=2. Higher = worse.
    const sev = (s) => (s === "under" ? 2 : s === "risk" ? 1 : 0);
    const baselineSev = new Map();
    for (const r of (baseline.perRule || [])) {
      const cid = r?.rule?.coverage_id;
      if (cid) baselineSev.set(cid, sev(r.status));
    }
    for (const e of (employees || [])) {
      const rotated = rotateRelevantWeekCells({
        cells, employeeId: e.employee_id,
        weekInCycle: cw.weekInCycle, dynShift,
      });
      const after = evaluateRotaStyleCoverage({
        cells: rotated, employees, categories, rules,
        cycle_days: cycleLen, teams: cgTeams,
        scope_team_id: scopeTeamId,
      });
      const worse = (after.perRule || []).some((r) => {
        const cid = r?.rule?.coverage_id;
        if (!cid) return false;
        const before = baselineSev.get(cid) ?? 0;
        return sev(r.status) > before;
      });
      out.set(e.employee_id, worse ? "skip-advised" : "safe");
    }
    return out;
  }, [cw, dynShift, cells, employees, categories, cgRules, cgTeams, profile?.team_id]);

  const preview = useMemo(() => {
    if (!cw) return { rows: [] };
    return buildRotationPreview({
      cells, employees, weekInCycle: cw.weekInCycle, dynShift,
    });
  }, [cw, dynShift, cells, employees]);

  if (!cw) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-end justify-center bg-black/30"
        onClick={onClose}
        data-testid="rota-style-dynamic-rotation-preview-drawer"
      >
        <div
          className="bg-white rounded-t-xl shadow-2xl w-full max-w-[700px] p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="text-sm text-[#4B5773]">
            Preview unavailable. Profile has no cycle anchor or
            cycle length set.
          </p>
          <div className="mt-3 flex justify-end">
            <Button size="sm" variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const rowsWithChange = preview.rows.filter((r) => r.anyChange);
  const skipAdvisedCount = Array.from(cgAdvisoryByEmp.values())
    .filter((v) => v === "skip-advised").length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/30"
      onClick={onClose}
      data-testid="rota-style-dynamic-rotation-preview-drawer"
    >
      <div
        className="bg-white rounded-t-xl shadow-2xl w-full max-w-[1100px] max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div className="flex items-center gap-2">
            <Eye className="h-4 w-4 text-[#4B5773]" />
            <h3 className="font-semibold text-base text-[#0B1A36]">
              Preview Next Pattern:{" "}
              <span className="text-indigo-700">{cw.weekLabel}</span>
            </h3>
          </div>
          <button
            type="button" onClick={onClose}
            className="text-[#4B5773] hover:text-[#0B1A36]"
            data-testid="rota-style-dynamic-rotation-preview-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="text-[11px] text-[#4B5773] px-5 pt-3 leading-relaxed">
          Read-only preview of what the next Generate would rotate
          inside <span className="font-medium">{cw.weekLabel}</span>.
          Rotation is bounded to this week&apos;s active cells,
          assignments only. Times, breaks, employees, day positions
          and empty days stay exactly as they are.
        </p>

        <div className="px-5 py-3 grid grid-cols-2 md:grid-cols-5 gap-3 text-[11px]"
             data-testid="rota-style-dynamic-rotation-preview-summary">
          <SummaryTile label="Rows changing" value={rowsWithChange.length}
            testid="rota-style-dynrot-summary-rows-changing" />
          {/* Phase 2Z.p Stage 2.3 — display the EFFECTIVE rotation
              step, not the raw `delta_days // cycle_len` value.
              Raw step can balloon to +100 / +6769 over years —
              meaningless to customers. The engine always modulos
              by the active-day count internally, so the effective
              step (`raw % activeCount`) is what actually moves
              cells. Per-employee active counts can differ; we
              display against the MAX so the surface matches the
              "Active rotation cells: up to N" tile. Engine maths
              unchanged. */}
          <SummaryTile label="Rotation step"
            value={(() => {
              if (dynShift === 0) return "Static this cycle";
              const counts = Array.from(activeDaysByEmp.values()).map((a) => a.length);
              const maxN = counts.length ? Math.max(...counts) : 0;
              if (maxN < 2) return "No effective movement";
              const effective = ((dynShift % maxN) + maxN) % maxN;
              if (effective === 0) return "No effective movement";
              return `+${effective} of ${maxN} active cells`;
            })()}
            testid="rota-style-dynrot-summary-step" />
          {/* Phase 2Z.p Stage 2.1 — split the confusing single tile
              into TWO: the bounding cycle-day range (informational)
              and the count of active rotation cells (the actual
              cells the helper will rotate). The active count is
              the MAX across all employees so a manager can see at
              a glance how many cells participate. Per-row detail
              renders in the table below. */}
          <SummaryTile
            label="Cycle day range"
            value={weekRange ? `${weekRange.start}-${weekRange.end - 1}` : "-"}
            testid="rota-style-dynrot-summary-cycle-day-range" />
          <SummaryTile
            label="Active rotation cells"
            value={(() => {
              const counts = Array.from(activeDaysByEmp.values()).map((a) => a.length);
              const maxN = counts.length ? Math.max(...counts) : 0;
              return maxN === 0 ? "None" : `up to ${maxN}`;
            })()}
            testid="rota-style-dynrot-summary-active-cells" />
          <SummaryTile label="Coverage Guard advisory"
            value={skipAdvisedCount === 0 ? "All safe" : `${skipAdvisedCount} row${skipAdvisedCount === 1 ? "" : "s"} skip-advised`}
            tone={skipAdvisedCount === 0 ? "ok" : "warn"}
            testid="rota-style-dynrot-summary-cg-advisory" />
        </div>

        {dynShift === 0 && (
          <div className="mx-5 mb-3 p-3 rounded border border-slate-200 bg-slate-50 text-[11px] text-[#4B5773]"
               data-testid="rota-style-dynrot-static-notice">
            Rotation step is 0 for this week. Nothing would change.
            Cells are passed through verbatim.
          </div>
        )}

        <div className="px-5 pb-5">
          <table className="w-full text-[11px] border border-slate-200 rounded overflow-hidden"
                 data-testid="rota-style-dynamic-rotation-preview-table">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-2.5 py-1.5 font-medium text-[#4B5773]">
                  Employee
                </th>
                <th className="text-left px-2.5 py-1.5 font-medium text-[#4B5773]">
                  CG
                </th>
                {weekRange && Array.from({ length: 7 }).map((_, i) => {
                  const dayIdx = weekRange.start + i;
                  return (
                    <th key={dayIdx}
                        className="text-left px-2.5 py-1.5 font-medium text-[#4B5773]">
                      D{dayIdx}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {preview.rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-2.5 py-3 text-center text-[#94A3B8]">
                    No employees in this profile.
                  </td>
                </tr>
              ) : preview.rows.map((row) => {
                const advisory = cgAdvisoryByEmp.get(row.employee_id);
                const skipAdvised = advisory === "skip-advised";
                return (
                  <tr key={row.employee_id}
                      data-testid={`rota-style-dynrot-row-${row.employee_id}`}
                      data-cg-advisory={advisory || "n/a"}
                      className={skipAdvised ? "bg-amber-50/60" : ""}>
                    <td className="px-2.5 py-1.5 border-t border-slate-200 font-medium text-[#0B1A36]">
                      {row.employee_name}
                    </td>
                    <td className="px-2.5 py-1.5 border-t border-slate-200">
                      {skipAdvised ? (
                        <span
                          data-testid={`rota-style-dynrot-cg-skip-${row.employee_id}`}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 text-[10px] font-medium"
                          title="Rotation would make at least one Coverage Guard rule worse for this row. Stage 3 will skip this row."
                        >
                          <ShieldCheck className="h-3 w-3" />
                          Skip
                        </span>
                      ) : (
                        <span className="text-[10px] text-emerald-700">OK</span>
                      )}
                    </td>
                    {row.dayDeltas.map((d) => (
                      <td key={d.cycle_day_index}
                          className={`px-2.5 py-1.5 border-t border-slate-200 align-top ${d.changed ? "bg-indigo-50/50" : ""}`}>
                        {d.empty ? (
                          <span className="text-[#CBD5E1]">-</span>
                        ) : (
                          <div className="space-y-0.5">
                            {d.changed ? (
                              <>
                                <div className="text-[10px] text-[#94A3B8] line-through">
                                  {d.before.map((id) => catById.get(id)?.name || id).join(", ")}
                                </div>
                                <div className="text-[11px] font-medium text-indigo-700">
                                  {d.after.map((id) => catById.get(id)?.name || id).join(", ")}
                                </div>
                              </>
                            ) : (
                              <div className="text-[11px] text-[#0B1A36]">
                                {d.after.map((id) => catById.get(id)?.name || id).join(", ")}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="px-5 py-3.5 border-t border-slate-200 bg-slate-50/60 flex items-center justify-between sticky bottom-0">
          <div className="text-[11px] text-[#4B5773] leading-relaxed">
            This is a preview only. No cells were changed. Use the
            Generate button on the page header to seed next week&apos;s
            draft using these rotated assignments.
          </div>
          <Button size="sm" variant="outline" onClick={onClose}
                  data-testid="rota-style-dynamic-rotation-preview-close-btn">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

function SummaryTile({ label, value, tone, testid }) {
  const toneClass = tone === "warn"
    ? "border-amber-200 bg-amber-50"
    : "border-slate-200 bg-white";
  return (
    <div
      data-testid={testid}
      className={`rounded border ${toneClass} px-2.5 py-2`}
    >
      <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-medium">
        {label}
      </div>
      <div className="text-[13px] font-semibold text-[#0B1A36] mt-0.5"
           data-testid={`${testid}-value`}>
        {value}
      </div>
    </div>
  );
}



// Phase 2Z.j — Change cycle modal. Pure UI; calls back to the parent
// which uses the existing POST /profile + POST /profile/{id}/activate
// pair. Never deletes or mutates the populated cells of the old
// profile. The destructive confirmation gates the write when the
// current profile has saved cells.
function ChangeCycleModal({
  currentCycleType, cellCount, saving, onClose, onConfirm,
}) {
  const [picked, setPicked] = useState(null);

  function attempt(cycleType) {
    if (cycleType === currentCycleType) {
      toast.info("That is already your current cycle.");
      return;
    }
    const opt = CYCLE_TYPE_OPTIONS.find((o) => o.key === cycleType);
    const label = opt ? opt.label : cycleType;
    let ok;
    if (cellCount === 0) {
      ok = window.confirm(
        `Switch your Rota Style to a ${label} cycle? This will replace your current empty Rota Style.`,
      );
    } else {
      ok = window.confirm(
        `You already have ${cellCount} saved Rota Style cell${cellCount === 1 ? "" : "s"}. `
        + `Changing cycle will start a new empty Rota Style with the ${label} cycle. `
        + "Your current Rota Style will be kept inactive in your account and will "
        + "no longer be used for new generated drafts. Continue?",
      );
    }
    if (!ok) return;
    setPicked(cycleType);
    onConfirm(cycleType);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      onClick={onClose}
      data-testid="rota-style-change-cycle-modal"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-[640px] max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Repeat className="h-4 w-4 text-[#4B5773]" />
            <h3 className="font-semibold text-base text-[#0B1A36]">
              Change cycle
            </h3>
          </div>
          <button
            type="button" onClick={onClose}
            className="text-[#4B5773] hover:text-[#0B1A36]"
            data-testid="rota-style-change-cycle-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 pt-3 text-[12px] text-[#4B5773] leading-relaxed">
          {cellCount === 0 ? (
            <p>
              Pick a new cycle length. Your current Rota Style is empty,
              so this is a safe swap.
            </p>
          ) : (
            <p>
              You already have{" "}
              <span className="font-medium text-[#0B1A36]">
                {cellCount} saved cell{cellCount === 1 ? "" : "s"}
              </span>{" "}
              in your current Rota Style. Picking a new cycle starts a
              new empty Rota Style with the selected cycle. Your current
              Rota Style is kept inactive in your account for
              reference. Published rosters and existing drafts are not
              affected.
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-5"
             data-testid="rota-style-change-cycle-chooser">
          {CYCLE_TYPE_OPTIONS.map((opt) => {
            const isCurrent = opt.key === currentCycleType;
            const isPicked = picked === opt.key && saving;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => attempt(opt.key)}
                disabled={saving || isCurrent}
                className={`text-left rounded-lg border px-3.5 py-3 transition-colors ${isCurrent ? "border-slate-200 bg-slate-50/60 cursor-not-allowed opacity-60" : "border-slate-200 bg-white hover:border-emerald-500 hover:bg-emerald-50/30"}`}
                data-testid={`rota-style-change-cycle-${opt.key}`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[13px] font-semibold text-[#0B1A36]">
                    {opt.label}
                  </span>
                  <span className="text-[10px] text-[#94A3B8]">
                    {opt.days} days{isCurrent ? " · current" : ""}
                  </span>
                </div>
                <p className="text-[11px] text-[#4B5773] mt-1 leading-relaxed">
                  {opt.blurb}
                </p>
                {isPicked && (
                  <p className="text-[11px] text-emerald-700 mt-2 flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Switching…
                  </p>
                )}
              </button>
            );
          })}
        </div>

        <div className="px-5 py-3.5 border-t border-slate-200 bg-slate-50/60 flex items-center justify-end gap-3">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}


function PresetDays({ cycleLen, onPick }) {
  function set(idxs) {
    return new Set(idxs.filter((i) => i >= 0 && i < cycleLen));
  }
  // Phase 2Z.h — Cycle-aware presets.
  //   Weekly (cycleLen <= 7):  Mon – Fri · Weekend · Entire cycle
  //                            (exactly as today — byte-for-byte).
  //   Multi-week (cycleLen > 7): one "Mon-Fri W{n}" per week of the
  //                              cycle + an "All weekdays" preset
  //                              + the existing "Entire cycle" catch-all.
  const items = [];
  if (cycleLen <= 7) {
    items.push({ key: "monfri", label: "Mon to Fri",
                  value: [0, 1, 2, 3, 4] });
    items.push({ key: "weekend", label: "Weekend",
                  value: [5, 6] });
  } else {
    const weeks = Math.ceil(cycleLen / 7);
    for (let w = 0; w < weeks; w++) {
      items.push({
        key: `monfri-w${w + 1}`,
        label: `Mon to Fri W${w + 1}`,
        value: [0, 1, 2, 3, 4].map((d) => w * 7 + d),
      });
    }
    const allWeekdays = [];
    for (let i = 0; i < cycleLen; i++) {
      if (i % 7 < 5) allWeekdays.push(i);
    }
    items.push({ key: "all-weekdays", label: "All weekdays",
                  value: allWeekdays });
  }
  items.push({
    key: "all",
    label: "Entire cycle",
    value: Array.from({ length: cycleLen }, (_, i) => i),
  });
  return items.map((it) => (
    <button
      key={it.key}
      type="button"
      onClick={() => onPick(set(it.value))}
      className="text-[11px] px-2 py-1 rounded border border-slate-200 bg-white hover:border-slate-300 text-[#0B1A36]"
      data-testid={`rota-style-bulk-day-preset-${it.key}`}
    >
      {it.label}
    </button>
  ));
}


// ---------- Phase 2Z.l C-lite — Replace category drawer ----------
// Pure-cells-only first version. A cell is eligible only when:
//   • employee is in the chosen scope (or no employee filter)
//   • cycle_day_index is in the chosen scope (or no day filter)
//   • the cell has exactly ONE assignment AND it is `from_category`
// Mixed cells (e.g. [Admin, Inbound]) are SURFACED in the preview as
// "Skipped — mixed" but NEVER mutated. This keeps v1 ambiguity-free
// and dedupe-impossible. All time/break fields and notes are
// preserved byte-for-byte by the apply handler.
function ReplaceCategoryDrawer({
  cycleLen, employees, categories, cells, saving, scopeLabel, onClose, onApply,
}) {
  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");
  // Scope pickers — kept minimal in v1. Empty set = "no filter (all)".
  const [empPick, setEmpPick] = useState(() => new Set());
  const [dayPick, setDayPick] = useState(() => new Set());
  const [empSearch, setEmpSearch] = useState("");

  const scopedEmployees = useMemo(() => {
    if (!empSearch.trim()) return employees;
    const q = empSearch.trim().toLowerCase();
    return employees.filter(
      (e) => (e.name || "").toLowerCase().includes(q));
  }, [employees, empSearch]);

  // Deterministic preview — exactly mirrors the handler's eligibility
  // rules so the customer can trust the numbers before clicking Apply.
  const preview = useMemo(() => {
    if (!fromId || !toId || fromId === toId) {
      return { pure: [], mixed: [], totalMatching: 0 };
    }
    const empSet = empPick.size > 0 ? empPick : null;
    const daySet = dayPick.size > 0 ? dayPick : null;
    const pure = [];
    const mixed = [];
    for (const c of cells) {
      if (empSet && !empSet.has(c.employee_id)) continue;
      if (daySet && !daySet.has(c.cycle_day_index)) continue;
      const assigns = c.assignments || [];
      const containsFrom = assigns.some(
        (a) => a.category_id === fromId);
      if (!containsFrom) continue;
      const isPure = assigns.length === 1
        && assigns[0].category_id === fromId;
      (isPure ? pure : mixed).push(c);
    }
    // Stable ordering for the preview list.
    const sortKey = (c) =>
      `${c.cycle_day_index.toString().padStart(2, "0")}|`
      + `${(c.start_time || "")}|${c.employee_id}`;
    pure.sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
    mixed.sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
    return {
      pure, mixed,
      totalMatching: pure.length + mixed.length,
    };
  }, [fromId, toId, cells, empPick, dayPick]);

  function toggleEmp(id) {
    setEmpPick((p) => {
      const n = new Set(p);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }
  function toggleDay(i) {
    setDayPick((p) => {
      const n = new Set(p);
      n.has(i) ? n.delete(i) : n.add(i);
      return n;
    });
  }

  const fromCat = categories.find((c) => c.category_id === fromId);
  const toCat = categories.find((c) => c.category_id === toId);

  // Empty employee picker = no filter (all employees in scope).
  // Empty day picker  = no filter (all cycle days in scope).
  const hasValidPick = fromId && toId && fromId !== toId;
  const willChange = preview.pure.length;
  const canApply = hasValidPick && willChange > 0 && !saving;

  function applyAll() {
    if (!canApply) return;
    const ok = window.confirm(
      `Replace ${fromCat?.name || "-"} with ${toCat?.name || "-"} in `
      + `${willChange} cell${willChange === 1 ? "" : "s"}? `
      + (preview.mixed.length > 0
          ? `${preview.mixed.length} mixed cell${preview.mixed.length === 1 ? "" : "s"} will be skipped.`
          : "")
    );
    if (!ok) return;
    onApply({
      from_category_id: fromId,
      to_category_id: toId,
      employeeIds: Array.from(empPick),
      dayIndices: Array.from(dayPick),
    });
  }

  const dayLabels = Array.from({ length: cycleLen }, (_, i) => {
    if (cycleLen <= 7) {
      return ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][i % 7];
    }
    const week = Math.floor(i / 7) + 1;
    const wd = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][i % 7];
    return `W${week} ${wd}`;
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/30"
      onClick={onClose}
      data-testid="rota-style-replace-category-drawer"
    >
      <div
        className="bg-white rounded-t-xl shadow-2xl w-full max-w-[1000px] max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="flex items-center gap-2">
              <Shuffle className="h-4 w-4 text-[#5775A6]" />
              <h2 className="text-[15px] font-semibold text-[#0B1A36]">
                Replace category
              </h2>
            </div>
            <p className="text-[11px] text-[#4B5773] mt-0.5">
              Swap one category for another in matching Rota Style cells.
              Times, breaks and notes are preserved. Mixed cells are
              skipped. Open them manually if you need them changed.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-slate-100"
            data-testid="rota-style-replace-category-close"
          >
            <X className="h-4 w-4 text-[#94A3B8]" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-5">
          {/* From / To pickers */}
          {/* Phase 2B - Replace Category is Work-only in V1. Away
              categories (e.g. Break) cannot be picked as From or To
              so a manager cannot accidentally replace an Away row
              via this tool. RMI engine is already Work-only via
              Phase 2A; this guard closes the manual entry path. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold mb-1.5">
                Replace
              </label>
              <select
                value={fromId}
                onChange={(e) => setFromId(e.target.value)}
                className="w-full text-[13px] border border-slate-300 rounded px-2.5 py-1.5 bg-white"
                data-testid="rota-style-replace-from-select"
              >
                <option value="">Pick a category</option>
                {categories
                  .filter((c) => c.kind === "work")
                  .map((c) => (
                    <option key={c.category_id} value={c.category_id}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold mb-1.5">
                With
              </label>
              <select
                value={toId}
                onChange={(e) => setToId(e.target.value)}
                className="w-full text-[13px] border border-slate-300 rounded px-2.5 py-1.5 bg-white"
                data-testid="rota-style-replace-to-select"
              >
                <option value="">Pick a category</option>
                {categories
                  .filter((c) => c.kind === "work"
                                  && c.category_id !== fromId)
                  .map((c) => (
                    <option key={c.category_id} value={c.category_id}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {/* Scope: employees (optional) */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold">
                Employees
              </label>
              <span className="text-[10px] text-[#94A3B8]">
                {empPick.size === 0
                  ? (scopeLabel || "All employees")
                  : `${empPick.size} selected`}
              </span>
            </div>
            <Input
              placeholder="Search…"
              value={empSearch}
              onChange={(e) => setEmpSearch(e.target.value)}
              className="text-[12px] h-8 mb-2"
              data-testid="rota-style-replace-emp-search"
            />
            <div className="max-h-[160px] overflow-y-auto border border-slate-200 rounded p-2 grid grid-cols-2 gap-1.5">
              {scopedEmployees.length === 0 ? (
                <p className="text-[12px] text-[#94A3B8] col-span-2 px-1">
                  No employees match.
                </p>
              ) : (
                scopedEmployees.map((e) => (
                  <label
                    key={e.employee_id}
                    className="flex items-center gap-1.5 text-[12px] text-[#0B1A36] cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={empPick.has(e.employee_id)}
                      onChange={() => toggleEmp(e.employee_id)}
                      data-testid={`rota-style-replace-emp-${e.employee_id}`}
                    />
                    <span className="truncate">{e.name}</span>
                  </label>
                ))
              )}
            </div>
          </div>

          {/* Scope: cycle days (optional) */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold">
                Cycle days
              </label>
              <span className="text-[10px] text-[#94A3B8]">
                {dayPick.size === 0
                  ? "All days"
                  : `${dayPick.size} selected`}
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {dayLabels.map((label, idx) => {
                const on = dayPick.has(idx);
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => toggleDay(idx)}
                    className={`text-[11px] px-2 py-1 rounded border ${
                      on
                        ? "bg-[#0B1A36] text-white border-[#0B1A36]"
                        : "bg-white text-[#0B1A36] border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`rota-style-replace-day-${idx}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Preview */}
          <div
            className="rounded-lg border border-slate-200 bg-[#F8FAFC] p-3"
            data-testid="rota-style-replace-preview"
          >
            {!hasValidPick ? (
              <p className="text-[12px] text-[#94A3B8]">
                Pick two different categories to see a preview.
              </p>
            ) : preview.totalMatching === 0 ? (
              <p className="text-[12px] text-[#94A3B8]">
                No cells in the selected scope contain
                {" "}
                <strong className="text-[#0B1A36]">
                  {fromCat?.name}
                </strong>.
              </p>
            ) : (
              <div className="space-y-2.5">
                <div className="text-[12px] text-[#0B1A36]">
                  <strong>{willChange}</strong> pure cell
                  {willChange === 1 ? "" : "s"} will change from{" "}
                  <strong>{fromCat?.name}</strong> to{" "}
                  <strong>{toCat?.name}</strong>.
                  {preview.mixed.length > 0 && (
                    <span className="text-[#B4530B]">
                      {" "}
                      {preview.mixed.length} mixed cell
                      {preview.mixed.length === 1 ? "" : "s"} will be skipped.
                    </span>
                  )}
                </div>
                {willChange > 0 && (
                  <details className="text-[11px]">
                    <summary className="cursor-pointer text-[#5775A6]">
                      Show cells that will change
                    </summary>
                    <ul
                      className="mt-2 max-h-[160px] overflow-y-auto bg-white border border-slate-200 rounded p-2 space-y-0.5"
                      data-testid="rota-style-replace-preview-pure-list"
                    >
                      {preview.pure.map((c) => {
                        const empName = employees.find(
                          (e) => e.employee_id === c.employee_id)?.name
                          || c.employee_id;
                        return (
                          <li
                            key={c.cell_id}
                            className="text-[#0B1A36] tabular-nums"
                          >
                            {dayLabels[c.cycle_day_index]} ·{" "}
                            {c.start_time || "all-day"}
                            {c.end_time ? `-${c.end_time}` : ""} ·{" "}
                            {empName}
                          </li>
                        );
                      })}
                    </ul>
                  </details>
                )}
                {preview.mixed.length > 0 && (
                  <details className="text-[11px]">
                    <summary className="cursor-pointer text-[#B4530B]">
                      Show skipped mixed cells
                    </summary>
                    <ul
                      className="mt-2 max-h-[140px] overflow-y-auto bg-white border border-slate-200 rounded p-2 space-y-0.5"
                      data-testid="rota-style-replace-preview-mixed-list"
                    >
                      {preview.mixed.map((c) => {
                        const empName = employees.find(
                          (e) => e.employee_id === c.employee_id)?.name
                          || c.employee_id;
                        const otherNames = (c.assignments || [])
                          .filter((a) => a.category_id !== fromId)
                          .map((a) => a.category_name || "?")
                          .join(", ");
                        return (
                          <li
                            key={c.cell_id}
                            className="text-[#0B1A36] tabular-nums"
                          >
                            {dayLabels[c.cycle_day_index]} ·{" "}
                            {c.start_time || "all-day"}
                            {c.end_time ? `-${c.end_time}` : ""} ·{" "}
                            {empName}
                            {otherNames && (
                              <span className="text-[#94A3B8]">
                                {" "}also has {otherNames}
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            data-testid="rota-style-replace-category-cancel"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={applyAll}
            disabled={!canApply}
            data-testid="rota-style-replace-category-apply"
          >
            {saving ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
            )}
            Apply
          </Button>
        </div>
      </div>
    </div>
  );
}


// ---------- Phase 2Z.l C-full — Rota Mix Intelligence ----------
//
// UI/UX upgrade around the existing safe Adjust Mix engine. Same
// internal state (toId / fromId / deltaPct), same handler
// (adjustMix), same atomic PUT. Adds a deterministic local command
// parser, dynamic suggestion chips, a live minutes-weighted mix
// snapshot and a side-by-side current-vs-projected preview table.
//
// **No LLM, no external API, no backend change.** The parser is a
// small regex + category-name matcher that runs in the browser. If
// it cannot map the command unambiguously onto two real categories
// + a percentage, it stops and shows "Could not safely interpret" —
// no silent guessing, no apply until the customer reviews the
// structured fields.

/**
 * Deterministic local parser. Returns:
 *   {
 *     ok: boolean,
 *     action: "increase" | "reduce" | "move" | null,
 *     to_category_id: string | null,
 *     from_category_id: string | null,
 *     delta_pct: number | null,
 *     confidence: "high" | "medium" | "low",
 *     message: string,
 *   }
 *
 * Category matching is case-insensitive against the live
 * `categories` list — no demo names are ever hardcoded.
 * Ambiguous matches (substring hits more than one category) and
 * missing parts produce a "low" or "fail" outcome with a message;
 * the caller MUST keep the apply button disabled in that case.
 */


// --------------------------------------------------------------------
// Phase 2Z.m manual cell editor same-day conflict guard — pure helper.
// Returns the FIRST existing cell on the same (employee, cycle_day)
// other than the one being edited, or null when the day is clear.
//
// V2 (B+D hybrid) — the guard now blocks ANY second same-day cell,
// not just time-overlapping ones, because the publish path writes
// ONE shift document per (emp, work_date) using the outer time
// envelope. Two disjoint cells (e.g. 09:00–17:00 Inbound + 20:30–
// 22:30 Admin) would publish as a single 09:00–22:30 shift envelope
// with 211 min of ghost hours between the blocks. Until proper
// split-shift support ships as its own dedicated phase, the safest
// V1 model is one cell per (emp, day) — hospitality customers can
// represent a split shift with one wide cell and break_minutes set
// to the gap (e.g. 09:00–21:00 with break_minutes=240).
// --------------------------------------------------------------------
function _findSameDayConflict(cells, employeeId, dayIndex, excludeCellId) {
  for (const c of (cells || [])) {
    if (c.employee_id !== employeeId) continue;
    if (c.cycle_day_index !== dayIndex) continue;
    if (excludeCellId && c.cell_id === excludeCellId) continue;
    return c;
  }
  return null;
}


// --------------------------------------------------------------------
// Phase 2Z.r-3 — RMI AI Assistant: default "Using time from" fallback.
//
// When the AI returns a valid target category + delta but leaves
// `from_category_id` empty (or returns one that is no longer in
// scope / equals the target), we deterministically pick the
// highest-share VALID source from the live mix snapshot so the
// suggestion drawer never lands with an empty "Using time from".
//
// Pure helper — no React, no fetch, no side effects. Returns the
// snapshot row (with `category_id` + `name`) or `null` when no
// safe source exists. The caller is responsible for surfacing the
// "default source" reason to the user; Coverage Guard / preview
// remain the safety layer. NEVER auto-applies.
//
// Rules:
//   • snapshotRows MUST already be sorted by share desc (the
//     existing liveSnapshot.rows is sorted that way).
//   • The target category is excluded.
//   • Only categories present in `inScopeIds` (active, in-scope,
//     non-leave assignable categories) qualify.
//   • Rows with a falsy `category_id` are skipped defensively.
// --------------------------------------------------------------------
function _rmiPickDefaultFromCategory(toCategoryId, snapshotRows, inScopeIds) {
  if (!Array.isArray(snapshotRows) || snapshotRows.length === 0) return null;
  for (const row of snapshotRows) {
    if (!row || !row.category_id) continue;
    if (row.category_id === toCategoryId) continue;
    if (!inScopeIds || typeof inScopeIds.has !== "function") continue;
    if (!inScopeIds.has(row.category_id)) continue;
    return row;
  }
  return null;
}


// --------------------------------------------------------------------
// Phase 2Z.r-5 — RMI AI Assistant: broad balance normalisation.
//
// When a manager asks something broad like "even out the workload
// across teams" or "rebalance the mix", the AI sometimes responds
// with a multi-target spread that doesn't map cleanly to RMI's
// single-action shape (one to, one from, one delta). We normalise
// these requests deterministically into:
//   • TO   = lowest-share VALID in-scope category
//   • FROM = highest-share VALID in-scope category
//   • DELTA = AI's amount (when present and > 0), else 5%
//
// Two pure helpers, no side effects, no fetch, no React state:
//   1. `_rmiIsBroadBalanceRequest(prompt)` — case-insensitive
//      substring match against a small trigger list.
//   2. `_rmiBroadBalanceSuggestion(snapshotRows, inScopeIds, defaultDelta)`
//      — picks lowest/highest from already-sorted snapshot rows,
//      excluding the target as its own source, requiring ≥ 2
//      valid in-scope categories AND a meaningful pct gap
//      (≥ 2 percentage points) so we don't force a change when
//      the mix is already even.
//
// NEVER auto-applies. Coverage Guard / preview / Apply remain the
// safety layer.
// --------------------------------------------------------------------
function _rmiIsBroadBalanceRequest(prompt) {
  if (typeof prompt !== "string" || !prompt) return false;
  const p = prompt.toLowerCase();
  const triggers = [
    "even out workload",
    "even out the workload",
    "even out across",
    "evenly across",
    "rebalance workload",
    "rebalance the workload",
    "rebalance the mix",
    "balance the mix",
    "balance the workload",
    "spread work evenly",
    "spread the workload",
    "spread the work evenly",
    "workload balance",
    "workload evenly",
  ];
  for (const t of triggers) {
    if (p.indexOf(t) !== -1) return true;
  }
  return false;
}


function _rmiBroadBalanceSuggestion(snapshotRows, inScopeIds, defaultDelta) {
  if (!Array.isArray(snapshotRows) || snapshotRows.length === 0) return null;
  if (!inScopeIds || typeof inScopeIds.has !== "function") return null;
  const valid = [];
  for (const row of snapshotRows) {
    if (!row || !row.category_id) continue;
    if (!inScopeIds.has(row.category_id)) continue;
    valid.push(row);
  }
  if (valid.length < 2) return null;
  // snapshotRows is pre-sorted by share desc — preserve that order.
  const highest = valid[0];
  const lowest = valid[valid.length - 1];
  if (highest.category_id === lowest.category_id) return null;
  // No meaningful gap → don't force a change. 2 percentage points
  // is a safe floor; anything smaller is within rounding noise.
  const gap = (Number(highest.pct) || 0) - (Number(lowest.pct) || 0);
  if (gap < 2) return null;
  const delta = (defaultDelta != null && Number(defaultDelta) > 0)
    ? Number(defaultDelta) : 5;
  return {
    to_category_id: lowest.category_id,
    to_name: lowest.name || "(unnamed)",
    from_category_id: highest.category_id,
    from_name: highest.name || "(unnamed)",
    delta_pct: delta,
    reason: (
      `${lowest.name || "the lowest-share category"} has the lowest share `
      + `and ${highest.name || "the highest-share category"} has the highest share, `
      + "so this is the safest simple rebalance suggestion. "
      + "Preview before applying."
    ),
  };
}


// --------------------------------------------------------------------
// Phase 2Z.t — RMI Scope Detection.
//
// Three pure, deterministic, frontend-only helpers that pre-fill the
// existing Refine Scope controls (empPick + dayPick) from the
// manager's plain-English command. They NEVER auto-apply, NEVER call
// the AI endpoint, and NEVER mutate the AI payload — they only ADD
// to the existing scope sets so any manual ticks the manager already
// placed survive.
//
//  • `_rmiParseScopeTeam(prompt, teams)`     — exact case-insensitive
//    full-name match against the company's team list. Returns
//    [team_id, …] (possibly empty, never throws). Unmatched / blank
//    prompts return [].
//
//  • `_rmiParseScopeEmployees(prompt, employees)` — exact UNIQUE
//    match against employee full names first, then first names.
//    Ambiguous first names (e.g. two "Chuck"s in the company) are
//    NEVER picked — they fall through to the empty result so the
//    manager picks manually. Returns [employee_id, …].
//
//  • `_rmiParseScopeWeekdays(prompt, cycleLen)` — detects weekday
//    tokens (monday/mon/mondays/tuesday/tue/tues/…/sunday/sun) and
//    returns every cycle-day index whose day-of-week matches across
//    all weeks. Also detects "W{n} {dow}" / "Week {n} {dow}" for a
//    single-index pick (low-risk bonus). Returns [cycle_day_index,…].
//
// Composition rule (applied by the drawer, not by these helpers):
// when a specific employee is named, the employee scope wins over
// any team match — the most specific scope is always honoured.
// --------------------------------------------------------------------
function _rmiEscapeRegex(s) {
  return String(s || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}


function _rmiParseScopeTeam(prompt, teams) {
  if (!prompt || typeof prompt !== "string") return [];
  if (!Array.isArray(teams)) return [];
  const text = prompt.toLowerCase();
  const out = [];
  for (const t of teams) {
    const tid = t && t.team_id;
    const name = String((t && t.name) || "").trim().toLowerCase();
    if (!tid || !name) continue;
    const re = new RegExp(
      `(^|[^a-z0-9])${_rmiEscapeRegex(name)}([^a-z0-9]|$)`, "i");
    if (re.test(text)) out.push(tid);
  }
  return out;
}


function _rmiParseScopeEmployees(prompt, employees) {
  if (!prompt || typeof prompt !== "string") return [];
  if (!Array.isArray(employees)) return [];
  const text = prompt.toLowerCase();
  // (1) Full-name unique match — every employee whose FULL name
  // appears in the prompt as a standalone phrase.
  const fullHits = [];
  for (const e of employees) {
    const name = String((e && e.name) || "").trim().toLowerCase();
    if (!name) continue;
    const re = new RegExp(
      `(^|[^a-z0-9])${_rmiEscapeRegex(name)}([^a-z0-9]|$)`, "i");
    if (re.test(text)) fullHits.push(e.employee_id);
  }
  if (fullHits.length > 0) return fullHits;
  // (2) First-name unique match — if exactly ONE employee has a
  // given first name AND that first name appears in the prompt,
  // pick that employee. Ambiguous first names are dropped on the
  // floor (we never guess).
  const firstNameMap = new Map();   // first-name → employee_id
  const ambiguous = new Set();
  for (const e of employees) {
    const name = String((e && e.name) || "").trim();
    if (!name) continue;
    const first = name.split(/\s+/)[0].toLowerCase();
    if (!first) continue;
    if (firstNameMap.has(first)) {
      ambiguous.add(first);
    } else {
      firstNameMap.set(first, e.employee_id);
    }
  }
  const out = [];
  for (const [first, eid] of firstNameMap.entries()) {
    if (ambiguous.has(first)) continue;
    const re = new RegExp(
      `(^|[^a-z0-9])${_rmiEscapeRegex(first)}([^a-z0-9]|$)`, "i");
    if (re.test(text)) out.push(eid);
  }
  return out;
}


const _RMI_WEEKDAY_TOKENS = [
  // [dow_index (Mon=0..Sun=6), [tokens, …]]
  [0, ["monday", "mondays", "mon"]],
  [1, ["tuesday", "tuesdays", "tue", "tues"]],
  [2, ["wednesday", "wednesdays", "wed"]],
  [3, ["thursday", "thursdays", "thu", "thur", "thurs"]],
  [4, ["friday", "fridays", "fri"]],
  [5, ["saturday", "saturdays", "sat"]],
  [6, ["sunday", "sundays", "sun"]],
];


function _rmiParseScopeWeekdays(prompt, cycleLen) {
  if (!prompt || typeof prompt !== "string") return [];
  if (typeof cycleLen !== "number" || cycleLen < 1) return [];
  const text = prompt.toLowerCase();
  // (a) Explicit "W{n} {dow}" / "Week {n} {dow}" — narrow single
  // cycle-day index when valid. Only triggers when the week number
  // is in 1..N where N = ceil(cycleLen / 7).
  const wRe = /(^|[^a-z0-9])(?:w(?:eek)?\s*([1-9]))\s+([a-z]+)([^a-z0-9]|$)/i;
  const m = wRe.exec(text);
  if (m) {
    const wk = parseInt(m[2], 10);
    const word = String(m[3] || "").toLowerCase();
    let dow = -1;
    for (const [idx, tokens] of _RMI_WEEKDAY_TOKENS) {
      if (tokens.indexOf(word) >= 0) { dow = idx; break; }
    }
    if (dow >= 0) {
      const dayIdx = (wk - 1) * 7 + dow;
      if (dayIdx >= 0 && dayIdx < cycleLen) return [dayIdx];
    }
  }
  // (b) Otherwise: every cycle-day index that matches any mentioned
  // weekday across all cycle weeks.
  const dowHit = new Set();
  for (const [idx, tokens] of _RMI_WEEKDAY_TOKENS) {
    for (const tok of tokens) {
      const re = new RegExp(
        `(^|[^a-z0-9])${_rmiEscapeRegex(tok)}([^a-z0-9]|$)`, "i");
      if (re.test(text)) { dowHit.add(idx); break; }
    }
  }
  const out = [];
  for (let i = 0; i < cycleLen; i++) {
    if (dowHit.has(i % 7)) out.push(i);
  }
  return out;
}


function _rmi_parseCommand(raw, categories, preferredCategoryIds) {
  const safe = {
    ok: false, action: null,
    to_category_id: null, from_category_id: null,
    delta_pct: null, confidence: "low", message: "",
  };
  if (!raw || typeof raw !== "string") {
    return { ...safe, message: "Type a command to interpret." };
  }
  const text = raw.trim().toLowerCase();
  if (!text) {
    return { ...safe, message: "Type a command to interpret." };
  }

  // 1) Percentage
  const pctMatch = text.match(/(\d{1,3})\s*%/);
  const delta_pct = pctMatch
    ? Math.max(0, Math.min(100, Number(pctMatch[1])))
    : null;

  // 2) Action keyword
  let action = null;
  if (/\b(increase|boost|raise|grow|add|more)\b/.test(text)) {
    action = "increase";
  } else if (/\b(reduce|decrease|cut|drop|lower|less)\b/.test(text)) {
    action = "reduce";
  } else if (/\bmove\b/.test(text)) {
    action = "move";
  }

  // 3) Category matching. Substring is case-insensitive.
  //
  //    Resolution order when more than one category matches the
  //    typed text (per the customer's stated rule):
  //
  //      (a) Prefer categories present in the IN-SCOPE Rota Style
  //          cells. Single in-scope hit wins. Multiple in-scope
  //          hits → try exact-name match WITHIN in-scope, else
  //          mark ambiguous and fail (never guess).
  //      (b) If no in-scope hits at all, fall back to an EXACT
  //          name match across the whole assignable list (typing
  //          the full label should always resolve).
  //      (c) Otherwise → ambiguous fail.
  //
  //    Single-hit and zero-hit short-circuits stay at the top so
  //    the unambiguous case never pays for the tie-break logic.
  const _preferred = preferredCategoryIds instanceof Set
    ? preferredCategoryIds : new Set(preferredCategoryIds || []);
  function findCategory(needle) {
    const n = (needle || "").trim().toLowerCase();
    if (!n) return { id: null, ambiguous: false };
    const hits = categories.filter(
      (c) => (c.name || "").toLowerCase().includes(n));
    if (hits.length === 0) return { id: null, ambiguous: false };
    if (hits.length === 1) return { id: hits[0].category_id, ambiguous: false };
    // (a) In-scope preference wins.
    if (_preferred.size > 0) {
      const inScope = hits.filter(
        (c) => _preferred.has(c.category_id));
      if (inScope.length === 1) {
        return { id: inScope[0].category_id, ambiguous: false };
      }
      if (inScope.length > 1) {
        const inScopeExact = inScope.find(
          (c) => (c.name || "").toLowerCase() === n);
        if (inScopeExact) {
          return { id: inScopeExact.category_id, ambiguous: false };
        }
        return {
          id: null, ambiguous: true,
          names: inScope.map((h) => h.name),
        };
      }
      // No in-scope hits — fall through to global exact-match.
    }
    // (b) Global exact-match fallback.
    const exact = hits.find((c) => (c.name || "").toLowerCase() === n);
    if (exact) return { id: exact.category_id, ambiguous: false };
    // (c) Truly ambiguous.
    return { id: null, ambiguous: true, names: hits.map((h) => h.name) };
  }

  // 4) Try to slice the command around connector words.
  //    Supported shapes (case-insensitive):
  //      "increase X by N% using time from Y"
  //      "increase X by N% from Y"
  //      "boost X +N%"  (Y inferred from current mix — left null here)
  //      "reduce X by N%"  (target inferred — left null here)
  //      "move time from X to Y by N%"
  //      "move from X to Y by N%"
  //      "X to Y by N%"
  //
  //    The parser intentionally NEVER guesses a missing FROM/TO. If
  //    one is missing, we hand back a "low" confidence and let the
  //    customer pick from the structured fields.
  const splitByConnector = (regex) => {
    const m = text.match(regex);
    if (!m || m.index == null) return null;
    return {
      before: text.slice(0, m.index).trim(),
      after: text.slice(m.index + m[0].length).trim(),
    };
  };

  let toRaw = "";
  let fromRaw = "";

  if (action === "move") {
    // "move (time) from X to Y by N%"  -> from=X, to=Y
    const fromSplit = splitByConnector(/\bfrom\b/);
    if (fromSplit) {
      const toSplit = splitByConnector(/\bto\b/);
      if (toSplit) {
        fromRaw = text.slice(
          text.search(/\bfrom\b/) + 4,
          text.search(/\bto\b/),
        ).trim();
        toRaw = text.slice(
          text.search(/\bto\b/) + 2,
          pctMatch ? text.search(/\bby\b/) >= 0
            ? text.search(/\bby\b/) : text.length
            : text.length,
        ).trim();
        // Strip leading words like "time"
        fromRaw = fromRaw.replace(/^time\s+/, "").trim();
      }
    }
  } else if (action === "increase") {
    // "increase X by N% [(using (time from | from |)) Y]"
    //   "increase X by N% using time from Y"
    //   "increase X by N% using from Y"
    //   "increase X by N% using Y"        ← short form (newly added)
    //   "increase X by N% from Y"
    //   "increase X using Y by N%"        ← FROM before BY also supported
    //
    // The connector alternation is ordered longest-first so the more
    // specific phrases win in JS regex left-to-right matching. The
    // capture stops at " by N%" or end-of-string so a FROM that
    // appears BEFORE the percentage doesn't swallow it.
    const FROM_RE =
      /\b(?:using\s+time\s+from|using\s+from|using|from)\s+(.+?)(?:\s+by\s+\d|\s*$)/;
    const fromSplit = text.match(FROM_RE);
    if (fromSplit) fromRaw = fromSplit[1].replace(/[.!?]+$/, "").trim();
    const head = text.replace(FROM_RE, "").trim();
    const byCut = head.match(
      /\b(?:increase|boost|raise|grow|add|more)\s+(.+?)(?:\s+by\s+\d|\s*\+\s*\d|\s*$)/);
    if (byCut) toRaw = byCut[1].trim();
  } else if (action === "reduce") {
    // "reduce X by N% [(and move to | to) Y]"  — TO usually missing.
    //   "reduce X by N% to Y"
    //   "reduce X by N% and move to Y"     ← newly handled phrase
    //   "reduce X by N% moving to Y"
    const TO_RE =
      /\b(?:(?:and\s+)?(?:move|moving|going|shift|shifting)\s+to|to)\s+(.+?)(?:\s+by\s+\d|\s*$)/;
    const toSplit = text.match(TO_RE);
    if (toSplit) toRaw = toSplit[1].replace(/[.!?]+$/, "").trim();
    const head = text.replace(TO_RE, "").trim();
    const byCut = head.match(
      /\b(?:reduce|decrease|cut|drop|lower|less)\s+(.+?)(?:\s+by\s+\d|\s*-\s*\d|\s*$)/);
    if (byCut) fromRaw = byCut[1].trim();
  }

  // Strip very common filler words.
  const strip = (s) => (s || "")
    .replace(/\b(by|time|some|please|the|category)\b/g, "")
    .replace(/[\s,.]+/g, " ")
    .trim();
  toRaw = strip(toRaw);
  fromRaw = strip(fromRaw);

  const toHit = findCategory(toRaw);
  const fromHit = findCategory(fromRaw);

  if (toHit.ambiguous || fromHit.ambiguous) {
    return {
      ...safe,
      action, delta_pct,
      message:
        "Could not safely interpret this. The category name matched "
        + "more than one of your active categories. Please pick from "
        + "the structured fields below.",
    };
  }
  if (!action || delta_pct == null) {
    return {
      ...safe,
      action, delta_pct,
      message: action == null
        ? "Could not safely interpret this. I couldn't find an "
          + "action word (try \"increase\", \"reduce\" or \"move\")."
        : "Could not safely interpret this. I couldn't find a "
          + "percentage (try \"by 10%\").",
    };
  }
  if (!toHit.id && !fromHit.id) {
    return {
      ...safe,
      action, delta_pct,
      message: "Could not safely interpret this. I couldn't match "
        + "either category to your active list. Please pick from the "
        + "structured fields below.",
    };
  }

  // Build the structured result.
  let to_id = null;
  let from_id = null;
  if (action === "increase") {
    to_id = toHit.id;
    from_id = fromHit.id;
  } else if (action === "reduce") {
    from_id = toRaw ? toHit.id : null;
    // No — action=reduce: the subject of "reduce X" is the FROM.
    from_id = fromHit.id || (toRaw && !fromHit.id ? null : null);
    // Defensive: pull subject from the head match above.
    from_id = (fromRaw && fromHit.id) ? fromHit.id : null;
    to_id = toHit.id || null;
    // If we only have a subject hit (toRaw empty, fromRaw populated)
    // the subject IS the FROM.
    if (!from_id && fromRaw === "" && toHit.id) {
      // Shouldn't happen — reduce sets fromRaw, not toRaw. Safety net.
      from_id = toHit.id;
    }
  } else if (action === "move") {
    from_id = fromHit.id;
    to_id = toHit.id;
  }

  // Confidence model:
  //   high   = action + delta + BOTH categories all resolved.
  //   medium = action + delta + ONE category resolved (the other
  //            stays empty, customer picks).
  //   low    = anything weaker (parser already returned above).
  let confidence = "low";
  if (to_id && from_id) confidence = "high";
  else if (to_id || from_id) confidence = "medium";

  return {
    ok: true,
    action,
    to_category_id: to_id,
    from_category_id: from_id,
    delta_pct,
    confidence,
    message: confidence === "high"
      ? "Interpreted with high confidence."
      : "Interpreted partially. Please pick the missing category "
        + "from the structured fields below before previewing.",
  };
}


function RotaMixIntelligenceDrawer({
  cycleLen, employees, categories, cells, saving, scopeLabel, onClose, onApply,
  cgRules, cgTeams, scopeTeamId, onOpenCgSettings,
}) {
  // -----------------------------------------------------------------
  // Phase 2Z.m V3 (admin@test1 follow-up): the previous strict
  // `kind === "work"` filter was hiding categories that managers
  // had legitimately put in their rota style cells but tagged with
  // a non-`work` kind in the company catalogue. Symptom: Admin was
  // assigned to every cell yet missing from both the Live Mix
  // Snapshot AND both dropdowns. The cells are the real source of
  // truth — if a category is used as an assignment in an in-scope
  // cell, it IS a legitimate rota assignment for RMI purposes.
  //
  // New rule: keep this as a permissive lookup (id → name + color)
  // that only rejects explicitly archived (`is_active === false`)
  // categories. The "is it in scope?" gate is handled downstream by
  // `fromCandidateCategories`, which intersects with what's actually
  // in cells. Leave / bank-holiday categories therefore only ever
  // appear in RMI if the manager has explicitly put them in cells
  // (which the rota-style cell editor governs separately).
  // -----------------------------------------------------------------
  const assignableCategories = useMemo(() => {
    return (categories || []).filter((c) => c.is_active !== false);
  }, [categories]);

  // Phase 2A Step 5 - RMI is Work-only in V1. Build a Work-only
  // view used by the snapshot denominator and by the From/To
  // dropdowns. The Away id set is used to exclude Away rows from
  // each cell's per-assignment slot so a Break/Away entry never
  // dilutes a Work category's mix percentage. RMI never replaces
  // Away rows because both From and To are restricted to Work.
  const workCategories = useMemo(() => {
    return assignableCategories.filter((c) => c.kind === "work");
  }, [assignableCategories]);
  const awayCategoryIds = useMemo(() => {
    return new Set(
      assignableCategories
        .filter((c) => c.kind === "away")
        .map((c) => c.category_id));
  }, [assignableCategories]);

  // Same internal state names as the previous AdjustMixDrawer so the
  // existing engine + tests continue to apply unchanged.
  const [toId, setToId] = useState("");
  const [fromId, setFromId] = useState("");
  const [deltaPct, setDeltaPct] = useState(10);
  const [empPick, setEmpPick] = useState(() => new Set());
  const [dayPick, setDayPick] = useState(() => new Set());
  const [empSearch, setEmpSearch] = useState("");
  const [scopeOpen, setScopeOpen] = useState(false);
  const [commandText, setCommandText] = useState("");
  const [interpretation, setInterpretation] = useState(null);
  // Phase 2Z.s — Performance Focus Lite deep-link. When the page
  // is opened with `?rmi_command=...` (from "Suggest rota focus"
  // on the Performance Focus page), we prefill the RMI command box
  // once on mount. We NEVER auto-submit — the manager still has
  // to click Send to AI Assistant / Interpret, then Use suggestion,
  // then Preview, then Apply.
  const [searchParams] = useSearchParams();
  const [rmiCommandConsumed, setRmiCommandConsumed] = useState(false);
  useEffect(() => {
    if (rmiCommandConsumed) return;
    const cmd = searchParams.get("rmi_command");
    if (cmd) {
      setCommandText(cmd);
      setRmiCommandConsumed(true);
    }
  }, [searchParams, rmiCommandConsumed]);

  const _t2m = (t) => {
    if (!t || typeof t !== "string" || !t.includes(":")) return null;
    const [h, m] = t.split(":");
    return Number(h) * 60 + Number(m);
  };
  const _cellNetMins = (c) => {
    const s = _t2m(c.start_time);
    const e = _t2m(c.end_time);
    if (s == null || e == null || e <= s) {
      // Phase 2Z.m Option B — Full-day cells inherit the employee's
      // working-pattern daily_minutes (which is already NET paid /
      // working minutes; do NOT subtract break_minutes a second time).
      // Mirrors the page-level Assignment Mix so the RMI snapshot
      // never disagrees with what the manager sees on the right
      // panel.
      const emp = employees.find((x) => x.employee_id === c.employee_id);
      const daily = Number(
        (emp && emp.working_pattern && emp.working_pattern.daily_minutes)
        ?? 480);
      return Math.max(0, daily);
    }
    const gross = e - s;
    const brk = Math.max(0, Math.min(Number(c.break_minutes) || 0,
                                       gross - 1));
    return Math.max(0, gross - brk);
  };

  const scopedEmployees = useMemo(() => {
    if (!empSearch.trim()) return employees;
    const q = empSearch.trim().toLowerCase();
    return employees.filter(
      (e) => (e.name || "").toLowerCase().includes(q));
  }, [employees, empSearch]);

  // Live mix snapshot — minutes-weighted (F2 rule). Driven entirely
  // by the live `cells` + `categories` props so the labels here are
  // 100% dynamic.
  const liveSnapshot = useMemo(() => {
    let totalMins = 0;
    const minutesByCat = new Map();
    for (const c of cells) {
      const assigns = c.assignments || [];
      if (assigns.length === 0) continue;
      // Phase 2A Step 5 - exclude Away rows from the denominator.
      // `_cellNetMins` already subtracts cell.break_minutes, so
      // dividing by Work-only count keeps Work mix percentages
      // stable when a cell carries an Away/Break row alongside
      // Work assignments.
      const workAssigns = assigns.filter(
        (a) => !awayCategoryIds.has(a.category_id));
      if (workAssigns.length === 0) continue;
      const net = _cellNetMins(c);
      if (net === 0) continue;
      const slot = net / workAssigns.length;
      totalMins += net;
      for (const a of workAssigns) {
        minutesByCat.set(a.category_id,
          (minutesByCat.get(a.category_id) || 0) + slot);
      }
    }
    const rows = Array.from(minutesByCat.entries())
      .map(([cid, mins]) => {
        // RMI filter fix: only surface ASSIGNABLE categories in the
        // snapshot (work-kind, active). If a cell happens to carry a
        // leave/system category id we silently drop that slice — it
        // would otherwise inflate "Total cycle time" with non-work
        // minutes and pollute the chip set.
        const cat = assignableCategories.find(
          (c) => c.category_id === cid);
        if (!cat) return null;
        return {
          category_id: cid,
          name: cat.name || "(unnamed)",
          color: cat.color || "#94A3B8",
          minutes: Math.round(mins),
          pct: totalMins === 0 ? 0 : (mins / totalMins) * 100,
        };
      })
      .filter((r) => r != null)
      .sort((a, b) => b.pct - a.pct);
    return { rows, totalMins: Math.round(totalMins) };
  }, [cells, assignableCategories]);

  // FROM dropdown candidates — every category that's actually
  // attached to any in-scope Rota Style cell. This is INTENTIONALLY
  // broader than the Live Mix Snapshot:
  //   • the snapshot shows mix CONTRIBUTION (needs net minutes)
  //   • the dropdowns show TEMPLATE PRESENCE (any assignment slot)
  // So a category that's assigned to a cell with start==end or
  // break==duration still counts as "in your template" and the
  // manager can pick it. Scope respects empPick + dayPick.
  //
  // Phase 2Z.m (admin@test1 report): previously this also skipped
  // cells with `_cellNetMins === 0`, which silently dropped any
  // category the manager had attached to a timeless / break-consumed
  // cell — even though those categories ARE in the active template.
  // The new rule is simpler: if a category is assigned to any
  // in-scope cell, it appears in both dropdowns.
  const fromCandidateCategories = useMemo(() => {
    const empSet = empPick.size > 0 ? empPick : null;
    const daySet = dayPick.size > 0 ? dayPick : null;
    const present = new Set();
    for (const c of cells) {
      if (empSet && !empSet.has(c.employee_id)) continue;
      if (daySet && !daySet.has(c.cycle_day_index)) continue;
      const assigns = c.assignments || [];
      // Cells with literally zero assignments have nothing to surface.
      if (assigns.length === 0) continue;
      for (const a of assigns) {
        if (a.category_id) present.add(a.category_id);
      }
    }
    return assignableCategories.filter(
      (c) => present.has(c.category_id)
              // Phase 2A Step 5 - From/To restricted to Work
              // categories so a manager cannot point RMI at
              // Break/Away in V1. RMI engine flips Work-only mix.
              && c.kind === "work");
  }, [assignableCategories, cells, empPick, dayPick]);

  // RMI parser preference set — ids actually present in the in-scope
  // Rota Style cells. Passed to `_rmi_parseCommand` so short names
  // like "inbound" prefer the live in-scope category over a stale
  // company category with the same substring.
  const inScopeCategoryIds = useMemo(() => {
    return new Set(fromCandidateCategories.map((c) => c.category_id));
  }, [fromCandidateCategories]);

  // TP-RMI.to-scope (Feb 2026) — TARGET ("Increase ...") candidates
  // must be sourced from the FULL Rota Style scope, NOT from the
  // refine-scope (empPick / dayPick) narrowing. Otherwise asking
  // "increase Customer support on Monday" silently drops Customer
  // support from the dropdown when Monday currently has 0 Customer
  // support cells, even though Customer support IS in the Rota
  // Style on other days. The engine supports promoting a target
  // that has 0 cells in refine-scope (it ADDs to a single-slot
  // cell via the existing "add" branch in `adjustMix`), so the UI
  // should not gate the manager out.
  //
  // FROM stays narrower: time can only be taken FROM categories
  // that genuinely exist in the refine-scope.
  const toCandidateCategories = useMemo(() => {
    const present = new Set();
    for (const c of cells) {
      const assigns = c.assignments || [];
      if (assigns.length === 0) continue;
      for (const a of assigns) {
        if (a.category_id) present.add(a.category_id);
      }
    }
    return assignableCategories.filter(
      (c) => present.has(c.category_id) && c.kind === "work");
  }, [assignableCategories, cells]);

  // Broader id set covering EVERY work category in the active Rota
  // Style. Used wherever we need to validate / recover a TARGET
  // category id (recovery helper below, AI suggestion mapping).
  // The narrower `inScopeCategoryIds` continues to gate FROM and
  // the regex parser preference.
  const inStyleAnyDayCategoryIds = useMemo(() => {
    return new Set(toCandidateCategories.map((c) => c.category_id));
  }, [toCandidateCategories]);

  // TO dropdown candidates — TP-RMI.to-scope. TO uses the Rota Style
  // scope (broader); FROM uses the refine-scope (narrower). The
  // ordering still puts in-style categories under one optgroup, and
  // the "Other assignment categories" group remains empty for V1.
  const toDropdownCategories = useMemo(() => {
    return { inScope: toCandidateCategories, other: [] };
  }, [toCandidateCategories]);

  // Dynamic suggestion chips — labels and targets derived from the
  // live snapshot. NEVER any hardcoded category name.
  const chips = useMemo(() => {
    const rows = liveSnapshot.rows;
    if (rows.length < 2) return [];
    const highest = rows[0];
    const lowest = rows[rows.length - 1];
    const list = [];
    list.push({
      key: "boost-lowest",
      label: `Boost ${lowest.name} +10%`,
      apply: () => {
        setToId(lowest.category_id);
        setFromId(highest.category_id);
        setDeltaPct(10);
        setInterpretation(null);
      },
    });
    list.push({
      key: "reduce-highest",
      label: `Reduce ${highest.name} -10%`,
      apply: () => {
        setFromId(highest.category_id);
        setToId(lowest.category_id);
        setDeltaPct(10);
        setInterpretation(null);
      },
    });
    if (rows.length >= 2) {
      const second = rows[1];
      const gap = highest.pct - second.pct;
      if (gap > 0.1) {
        list.push({
          key: "rebalance-top-two",
          label: `Rebalance ${highest.name} ↔ ${second.name}`,
          apply: () => {
            setToId(second.category_id);
            setFromId(highest.category_id);
            setDeltaPct(Math.max(1, Math.round(gap / 2)));
            setInterpretation(null);
          },
        });
      }
    }
    if (rows.length >= 3) {
      // "Even out workload" — moves from the highest to the lowest
      // by half the gap so neither overshoots.
      const gap = highest.pct - lowest.pct;
      if (gap > 0.1) {
        list.push({
          key: "even-out",
          label: `Even out workload`,
          apply: () => {
            setToId(lowest.category_id);
            setFromId(highest.category_id);
            setDeltaPct(Math.max(1, Math.round(gap / 2)));
            setInterpretation(null);
          },
        });
      }
    }
    return list;
  }, [liveSnapshot]);

  // Phase 2Z.t — RMI Scope Detection.
  // Single shared scope-prefill routine used by BOTH the regex
  // parser and the AI assistant paths. Pure local detection —
  // never touches the AI payload, never auto-applies, only ADDS
  // to the existing empPick / dayPick sets so any manual ticks
  // the manager already placed survive.
  //
  // Composition rule: if the prompt names a specific employee,
  // the employee scope wins over any team match (most-specific
  // wins). When no employee is named, team matches expand to
  // every employee in those teams.
  function _applyScopePrefillFromPrompt(text) {
    if (!text || typeof text !== "string") {
      return { empAdded: [], dayAdded: [], teamUsed: [] };
    }
    const empDirect = _rmiParseScopeEmployees(text, employees);
    const teamHits = empDirect.length === 0
      ? _rmiParseScopeTeam(text, cgTeams || [])
      : [];
    let scopedEmpIds = empDirect.slice();
    const teamSet = new Set(teamHits);
    if (teamHits.length > 0) {
      for (const e of (employees || [])) {
        if (e && e.team_id && teamSet.has(e.team_id)) {
          scopedEmpIds.push(e.employee_id);
        }
      }
    }
    // De-dup
    scopedEmpIds = Array.from(new Set(scopedEmpIds));
    const dayAdded = _rmiParseScopeWeekdays(
      text, Number(cycleLen) || 7);
    if (scopedEmpIds.length > 0) {
      setEmpPick((prev) => {
        const next = new Set(prev);
        for (const eid of scopedEmpIds) next.add(eid);
        return next;
      });
    }
    if (dayAdded.length > 0) {
      setDayPick((prev) => {
        const next = new Set(prev);
        for (const idx of dayAdded) next.add(idx);
        return next;
      });
    }
    if (scopedEmpIds.length > 0 || dayAdded.length > 0) {
      setScopeOpen(true);
    }
    return {
      empAdded: scopedEmpIds, dayAdded, teamUsed: teamHits,
    };
  }

  function interpretCommand() {
    // RMI filter fix: parse against the FILTERED assignable list so
    // short names like "outbound" can never resolve to a leave or
    // system category that happens to share a substring. Also pass
    // the in-scope id set so substring ties are broken in favour of
    // categories actually present in the current Rota Style cells.
    const r = _rmi_parseCommand(commandText, assignableCategories,
                                  inScopeCategoryIds);
    setInterpretation(r);
    if (r.ok) {
      if (r.to_category_id) setToId(r.to_category_id);
      if (r.from_category_id) setFromId(r.from_category_id);
      if (r.delta_pct != null) setDeltaPct(r.delta_pct);
    }
    // Phase 2Z.t — pre-fill Refine Scope from the manager's prompt.
    _applyScopePrefillFromPrompt(commandText);
  }

  // Phase 2Z.r — RMI AI Assistant. Calls /api/rmi/interpret with a
  // whitelisted payload (no employee IDs / names / leave / personal
  // data). On any failure (disabled, capped, network, bad JSON), we
  // SILENTLY fall back to the existing regex parser so the command
  // box keeps working.
  const [aiAssistantLoading, setAiAssistantLoading] = useState(false);
  const [aiAssistantMessage, setAiAssistantMessage] = useState(null);
  // Phase 2Z.r-2 — premium AI suggestion card + "Advanced manual
  // controls" reveal. Pure presentation state — no engine touch.
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [showManualControls, setShowManualControls] = useState(false);
  async function interpretCommandWithAI() {
    if (aiAssistantLoading || !commandText.trim()) return;
    setAiAssistantLoading(true);
    setAiAssistantMessage(null);
    const safeCategories = assignableCategories.map((c) => ({
      category_id: c.category_id, name: c.name,
    }));
    // Phase 2Z.r-1 — liveSnapshot is an OBJECT `{rows, totalMins}`,
    // not an array. Read `.rows` defensively (handles undefined,
    // null, partial-shape during loading) and fall back to [] so
    // .map() never blows up.
    const rawRows = Array.isArray(liveSnapshot?.rows)
      ? liveSnapshot.rows
      : [];
    const safeMix = rawRows.map((r) => ({
      category_id: r.category_id, pct: r.pct,
    }));
    // Phase 2Z.ag - tell the AI which categories are actually in the
    // current Rota Style. Empty list means "no scope hint" (matches
    // legacy behaviour). The backend post-processor blanks any
    // to_/from_ id that is outside this set so a company-wide
    // category that is not in the current style can never be
    // auto-applied.
    const inStyleCategoryIds = rawRows
      .map((r) => r.category_id)
      .filter((id) => typeof id === "string" && id.length > 0);
    try {
      const { data } = await api.post("/rmi/interpret", {
        prompt: commandText,
        categories: safeCategories,
        current_mix: safeMix,
        coverage_status: null,
        in_style_category_ids: inStyleCategoryIds,
      });
      const r = {
        ok: !!(data.to_category_id || data.from_category_id || data.delta_pct),
        to_category_id: data.to_category_id || null,
        from_category_id: data.from_category_id || null,
        delta_pct: data.delta_pct ?? null,
        confidence: data.confidence === "high" ? 0.9
                    : data.confidence === "medium" ? 0.65 : 0.4,
        reason: data.reason || "",
        source: "ai",
      };
      // Phase 2Z.r-5 — broad balance normalisation. When the user
      // asks something broad like "even out the workload across
      // teams", the AI sometimes returns a multi-target spread
      // that doesn't fit RMI's single-action shape. Detect those
      // requests against the user's own prompt (the source of
      // truth) and replace the AI suggestion with a deterministic
      // safe rebalance: lowest-share → highest-share. NEVER
      // auto-applies; Coverage Guard / preview / Apply remain the
      // safety layer.
      let broadBalanceApplied = false;
      let broadBalanceNote = "";
      let broadBalanceSyntheticCommand = "";
      if (_rmiIsBroadBalanceRequest(commandText)) {
        const pick = _rmiBroadBalanceSuggestion(
          liveSnapshot.rows, inScopeCategoryIds, r.delta_pct);
        if (pick) {
          r.to_category_id = pick.to_category_id;
          r.from_category_id = pick.from_category_id;
          r.delta_pct = pick.delta_pct;
          r.reason = pick.reason;
          r.ok = true;
          broadBalanceApplied = true;
          broadBalanceSyntheticCommand = (
            `Increase ${pick.to_name} using ${pick.from_name} `
            + `by ${pick.delta_pct}%`
          );
          broadBalanceNote = (
            `Broad rebalance: increasing ${pick.to_name} `
            + `(lowest share) using ${pick.from_name} (highest share).`
          );
        } else {
          // No valid simple rebalance possible (fewer than 2
          // in-scope categories, or the mix is already roughly
          // even). Suppress any AI suggestion and surface the
          // friendly fallback instead — manager picks manually.
          r.ok = false;
          r.to_category_id = null;
          r.from_category_id = null;
        }
      }
      // Phase 2Z.r-3 — default-source fallback. If the AI returned a
      // target but left "Using time from" empty (or returned one
      // that's no longer in scope, or equals the target), pick the
      // highest-share valid source from the live snapshot so the
      // user never lands on an empty dropdown. Coverage Guard /
      // preview remain the safety layer; we NEVER auto-apply.
      const aiTo = r.to_category_id;
      const rawFrom = r.from_category_id;
      const validAiFrom = !!(
        rawFrom
        && inScopeCategoryIds.has(rawFrom)
        && rawFrom !== aiTo
      );
      let effectiveFromId = validAiFrom ? rawFrom : null;
      let defaultFromUsed = false;
      let defaultFromName = "";
      if (aiTo && !validAiFrom) {
        const pick = _rmiPickDefaultFromCategory(
          aiTo, liveSnapshot.rows, inScopeCategoryIds);
        if (pick) {
          effectiveFromId = pick.category_id;
          defaultFromUsed = true;
          defaultFromName = pick.name;
        }
      }
      r.from_category_id = effectiveFromId;
      // TP-RMI.scope-recover (Feb 2026) — frontend defence layer.
      // TP-RMI.to-scope (Feb 2026) — TARGET uses the BROADER set
      // (`inStyleAnyDayCategoryIds` / `toCandidateCategories`) so a
      // Customer support target survives even when the manager has
      // a single weekday picked that currently has 0 Customer
      // support cells. SOURCE keeps using the NARROWER refine-scope
      // set because time can only be taken from categories that
      // genuinely exist in refine-scope.
      const _recoverToInDropdown = (id) => {
        if (!id) return "";
        if (inStyleAnyDayCategoryIds.has(id)) return id;
        const aimed = assignableCategories.find(
          (c) => c.category_id === id);
        if (!aimed) return "";
        const nm = String(aimed.name || "").trim().toLowerCase();
        if (!nm) return "";
        const match = toCandidateCategories.find(
          (c) => String(c.name || "").trim().toLowerCase() === nm);
        return match ? match.category_id : "";
      };
      const _recoverFromInDropdown = (id) => {
        if (!id) return "";
        if (inScopeCategoryIds.has(id)) return id;
        const aimed = assignableCategories.find(
          (c) => c.category_id === id);
        if (!aimed) return "";
        const nm = String(aimed.name || "").trim().toLowerCase();
        if (!nm) return "";
        const match = fromCandidateCategories.find(
          (c) => String(c.name || "").trim().toLowerCase() === nm);
        return match ? match.category_id : "";
      };
      const safeToId = _recoverToInDropdown(r.to_category_id);
      const safeFromId = _recoverFromInDropdown(effectiveFromId);
      setInterpretation(r);
      if (r.ok) {
        if (safeToId) setToId(safeToId);
        if (safeFromId) setFromId(safeFromId);
        if (r.delta_pct != null) setDeltaPct(r.delta_pct);
      }
      // Phase 2Z.t — pre-fill Refine Scope from the manager's
      // ORIGINAL prompt (not from the AI suggestion). Local
      // deterministic parser only; no extra data ever leaves
      // the browser. Never auto-applies.
      _applyScopePrefillFromPrompt(commandText);
      // Phase 2Z.r-5 — when broad-balance normalisation overrode
      // the AI, build the suggestion card from our deterministic
      // payload (not the original AI response, which may have
      // suggested a multi-target spread). Otherwise use the AI
      // suggestion as before.
      const effectiveCommand = broadBalanceApplied
        ? broadBalanceSyntheticCommand
        : data.suggested_command;
      if (effectiveCommand) {
        setAiAssistantMessage({
          kind: "ok", text: effectiveCommand,
        });
        // Phase 2Z.r-2 — keep the full AI suggestion for the
        // premium response card (command + reason + confidence).
        // Phase 2Z.r-3 — also carry the normalised from_category_id
        // + default-source note so the "Use suggestion" button
        // applies the fallback choice and the UI can explain it.
        // Phase 2Z.r-5 — also carry the broad-balance flag so the
        // card can render the rationale inline.
        setAiSuggestion({
          suggested_command: effectiveCommand,
          reason: r.reason || "",
          confidence: broadBalanceApplied ? "medium"
                       : (data.confidence || "low"),
          to_category_id: r.to_category_id || "",
          from_category_id: effectiveFromId || "",
          delta_pct: r.delta_pct ?? null,
          default_from_used: defaultFromUsed,
          default_from_name: defaultFromName,
          broad_balance_applied: broadBalanceApplied,
          broad_balance_note: broadBalanceNote,
        });
      } else if (!r.ok) {
        // Phase 2Z.r-4 — AI returned 200 but the response was
        // malformed / incomplete / out-of-scope (e.g. broad
        // "even out workload across teams" requests). Show the
        // sanitised friendly fallback instead of leaving the
        // assistant silent or surfacing raw payload contents.
        setAiAssistantMessage({
          kind: "fallback", text: RMI_FRIENDLY_FALLBACK,
        });
      }
    } catch (err) {
      // Phase 2Z.r-4 — sanitised error display. Raw payload
      // text is never surfaced to the user; all failures fall
      // back to a single friendly message + the deterministic
      // regex parser so the command box keeps working. The
      // capped (429) status is the only case where we
      // differentiate, because the user can act on it (wait
      // until tomorrow).
      const status = err?.response?.status;
      const friendly = status === 429
        ? "AI Assistant daily limit reached. Using standard parser."
        : RMI_FRIENDLY_FALLBACK;
      setAiAssistantMessage({ kind: "fallback", text: friendly });
      interpretCommand();
    } finally {
      setAiAssistantLoading(false);
    }
  }

  // Preview impact — current minutes-weighted mix vs projected mix
  // for the SCOPE chosen by the user. Same math as the previous
  // drawer.
  const preview = useMemo(() => {
    const empty = {
      hasValidPick: false,
      currentRows: [], projectedRows: [],
      totalScopeMinutes: 0,
      toFlip: [], skippedDuplicate: [],
      singleSlotRiskyCount: 0,
      flipActionCount: 0, addActionCount: 0,
      currentToPct: 0, currentFromPct: 0,
      resultingToPct: 0, resultingFromPct: 0,
      targetToPct: 0, minutesActuallyShifted: 0,
      explanation: "",
    };
    if (!toId || !fromId || toId === fromId) return empty;
    const delta = Math.max(-100, Math.min(100, Number(deltaPct) || 0));
    if (delta === 0) return empty;

    const empSet = empPick.size > 0 ? empPick : null;
    const daySet = dayPick.size > 0 ? dayPick : null;
    const scoped = cells.filter((c) => {
      if (empSet && !empSet.has(c.employee_id)) return false;
      if (daySet && !daySet.has(c.cycle_day_index)) return false;
      return _cellNetMins(c) > 0 && (c.assignments || []).length > 0;
    });

    let totalMins = 0;
    const minutesByCat = new Map();
    for (const c of scoped) {
      const net = _cellNetMins(c);
      const n = (c.assignments || []).length;
      const slot = net / n;
      totalMins += net;
      for (const a of c.assignments) {
        minutesByCat.set(a.category_id,
          (minutesByCat.get(a.category_id) || 0) + slot);
      }
    }
    if (totalMins === 0) return empty;

    const curToMins = minutesByCat.get(toId) || 0;
    const curFromMins = minutesByCat.get(fromId) || 0;
    const curToPct = (curToMins / totalMins) * 100;
    const curFromPct = (curFromMins / totalMins) * 100;
    const targetToPct = Math.max(0, Math.min(100, curToPct + delta));
    const minutesToShift = ((targetToPct - curToPct) / 100) * totalMins;

    const candidates = [];
    const skippedDuplicate = [];
    for (const c of scoped) {
      const assigns = c.assignments || [];
      const hasFrom = assigns.some((a) => a.category_id === fromId);
      if (!hasFrom) continue;
      const hasTo = assigns.some((a) => a.category_id === toId);
      if (hasTo) { skippedDuplicate.push(c); continue; }
      candidates.push(c);
    }
    const sortKey = (c) =>
      `${c.cycle_day_index.toString().padStart(3, "0")}|`
      + `${(c.start_time || "")}|${c.employee_id}`;
    candidates.sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
    skippedDuplicate.sort((a, b) => sortKey(a).localeCompare(sortKey(b)));

    const toFlip = [];
    let shifted = 0;
    const target = Math.abs(minutesToShift);
    for (const c of candidates) {
      if (shifted >= target) break;
      const assigns = c.assignments || [];
      const slot = _cellNetMins(c) / assigns.length;
      // C2 — annotate each preview entry with the action the cell
      // SHOULD take when C3 ships. A single-slot cell whose only
      // assignment is `from` will lose all `from` coverage if it is
      // flipped, so the correct action there is "add" (append `to`
      // as a 2nd assignment, splitting net minutes 50/50). Every
      // other cell is a straight slot-flip.
      //
      // C2 is metadata-only — the apply engine still flips ALL
      // entries below; the C1 amber warning makes the risk visible.
      // C3 will introduce the add-branch in `adjustMix`.
      const action = (
        assigns.length === 1 && assigns[0].category_id === fromId
      ) ? "add" : "flip";
      toFlip.push({ cell: c, slotMinutes: slot, action });
      shifted += slot;
    }

    // C1 safety audit (preview-only) — count cells in `toFlip` whose
    // C2 action is "add" (i.e. they would lose all `from` coverage
    // under today's flip-only engine). C3 will route these through
    // a real add/blend; for now the count surfaces the risk in the
    // preview so managers can decide whether to narrow scope.
    const singleSlotRiskyCount = toFlip.filter((f) => f.action === "add").length;
    // C2 also exposes a per-action breakdown for the preview labels.
    const flipActionCount = toFlip.filter((f) => f.action === "flip").length;
    const addActionCount = singleSlotRiskyCount;

    const projMinsByCat = new Map(minutesByCat);
    projMinsByCat.set(toId, (projMinsByCat.get(toId) || 0) + shifted);
    projMinsByCat.set(
      fromId, Math.max(0, (projMinsByCat.get(fromId) || 0) - shifted));

    const buildRows = (mb) =>
      Array.from(mb.entries())
        .map(([cid, mins]) => {
          // RMI filter fix: only surface assignable categories in
          // the preview tables. Any rogue non-assignment id is
          // silently dropped so the projected mix sums match the
          // current mix.
          const cat = assignableCategories.find(
            (c) => c.category_id === cid);
          if (!cat) return null;
          return {
            category_id: cid,
            name: cat.name || "(unnamed)",
            color: cat.color || "#94A3B8",
            minutes: Math.round(mins),
            pct: totalMins === 0 ? 0 : (mins / totalMins) * 100,
          };
        })
        .filter((r) => r != null)
        .sort((a, b) => b.pct - a.pct);

    let explanation = "";
    if (toFlip.length === 0 && candidates.length === 0) {
      explanation = "No cells in this scope contain "
        + `${assignableCategories.find((c) => c.category_id === fromId)?.name || "-"} `
        + `without already containing `
        + `${assignableCategories.find((c) => c.category_id === toId)?.name || "-"}.`;
    } else if (shifted + 1e-6 < target) {
      explanation = "The exact target can't be reached. Flipping "
        + "every eligible cell still leaves the resulting mix below "
        + "the target. Skipped cells are not auto-deduped in this "
        + "version.";
    } else if (toFlip.length > 0 && shifted > target * 1.0001) {
      explanation = "The exact target lands between two cells. The "
        + "smallest safe set is shown. The next cell would overshoot.";
    }

    return {
      hasValidPick: true,
      currentRows: buildRows(minutesByCat),
      projectedRows: buildRows(projMinsByCat),
      totalScopeMinutes: totalMins,
      toFlip, skippedDuplicate,
      singleSlotRiskyCount,
      flipActionCount, addActionCount,
      currentToPct: curToPct, currentFromPct: curFromPct,
      resultingToPct: (
        (projMinsByCat.get(toId) || 0) / totalMins) * 100,
      resultingFromPct: (
        (projMinsByCat.get(fromId) || 0) / totalMins) * 100,
      targetToPct, minutesActuallyShifted: shifted,
      explanation,
    };
  }, [toId, fromId, deltaPct, cells, empPick, dayPick, assignableCategories]);

  // Phase 2Z.n — Coverage Guard "after" verdict for the RMI preview.
  // Pure in-memory mirror of C3 adjustMix kernel → fresh evaluation
  // against the existing rules. NO PUT, NO setCells, NO api call.
  // When the preview has no valid pick we fall back to evaluating
  // the current cells (so the badge reflects the baseline).
  const cgPreviewVerdict = useMemo(() => {
    if (!cgRules || cgRules.length === 0) {
      return { overall: "grey", perRule: [], totalEvaluatedCount: 0,
                unsupportedRulesCount: 0 };
    }
    const baseCells = preview.hasValidPick && preview.toFlip.length > 0
      ? simulateRmiAdjustMix({
          cells, toFlip: preview.toFlip,
          fromCategoryId: fromId, toCategoryId: toId,
        })
      : cells;
    return evaluateRotaStyleCoverage({
      cells: baseCells, employees, categories, rules: cgRules,
      cycle_days: cycleLen, teams: cgTeams,
      // TP-CG.team — RMI verdict honours the same team scope as the
      // page-level badge so cross-team rules are not factored in.
      scope_team_id: scopeTeamId || null,
    });
  }, [cgRules, preview.hasValidPick, preview.toFlip, fromId, toId, cells, employees, categories, cycleLen, cgTeams, scopeTeamId]);

  // Phase 2Z.n.2 — Coverage Guard Auto-Skip. Toggle default is ON.
  // The split is recomputed whenever the preview changes; cells that
  // would worsen any V1-supported rule day are diverted from the
  // apply payload (but stay visible in the cell-details list with a
  // SKIPPED badge so managers can see what was held back and why).
  const [autoSkip, setAutoSkip] = useState(true);
  const cgSafeSplit = useMemo(() => {
    if (!cgRules || cgRules.length === 0 || !preview.hasValidPick) {
      return { safe: preview.toFlip || [], unsafe: [], summarySkipped: 0 };
    }
    return computeRmiSafeCells({
      baselineCells: cells, toFlip: preview.toFlip,
      rules: cgRules, employees, categories,
      cycle_days: cycleLen,
      fromCategoryId: fromId, toCategoryId: toId,
      teams: cgTeams,
      // TP-CG.team — same scope as the rest of the drawer so a
      // cross-team rule cannot block / skip a safe RMI flip.
      scope_team_id: scopeTeamId || null,
    });
  }, [cgRules, preview.hasValidPick, preview.toFlip, cells, employees, categories, cycleLen, fromId, toId, cgTeams, scopeTeamId]);
  // A Set of cell_ids skipped by Coverage Guard for fast lookup
  // during row rendering — purely a render-time index, no mutation
  // of the underlying preview data.
  const cgSkippedCellIds = useMemo(
    () => new Set(cgSafeSplit.unsafe.map((u) => u.cell.cell_id)),
    [cgSafeSplit.unsafe],
  );

  function toggleEmp(id) {
    setEmpPick((p) => {
      const n = new Set(p);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }
  function toggleDay(i) {
    setDayPick((p) => {
      const n = new Set(p);
      n.has(i) ? n.delete(i) : n.add(i);
      return n;
    });
  }

  const toCat = assignableCategories.find((c) => c.category_id === toId);
  const fromCat = assignableCategories.find((c) => c.category_id === fromId);
  const canApply = preview.hasValidPick
    && preview.toFlip.length > 0 && !saving;

  function applyAll() {
    if (!canApply) return;
    // Phase 2Z.n.2 — Coverage Guard Auto-Skip. When the toggle is
    // ON, filter the toFlip list to only safe cells BEFORE building
    // the apply payload. adjustMix never sees unsafe cells, so its
    // body stays byte-for-byte unchanged.
    const cellsToApply = autoSkip ? cgSafeSplit.safe : preview.toFlip;
    if (autoSkip && cellsToApply.length === 0) {
      // The "all skipped" guard — handled in the inline UI message.
      // Belt-and-braces here so applyAll never silently no-ops.
      toast.info(
        "Coverage Guard would skip every change. "
        + "Turn off Respect Coverage Guard to apply anyway.");
      return;
    }
    const ok = window.confirm(
      `Apply this rota mix change? ${cellsToApply.length} cell`
      + `${cellsToApply.length === 1 ? "" : "s"} will change. `
      + `${toCat?.name || "-"}: ${preview.currentToPct.toFixed(1)}% → `
      + `${preview.resultingToPct.toFixed(1)}%. `
      + `${fromCat?.name || "-"}: ${preview.currentFromPct.toFixed(1)}% → `
      + `${preview.resultingFromPct.toFixed(1)}%.`
      + (autoSkip && cgSafeSplit.summarySkipped > 0
          ? `\n\nCoverage Guard skipped ${cgSafeSplit.summarySkipped} risky `
            + `change${cgSafeSplit.summarySkipped === 1 ? "" : "s"}.`
          : ""),
    );
    if (!ok) return;
    onApply({
      from_category_id: fromId,
      to_category_id: toId,
      // C3 — pass per-cell action so the engine knows whether to
      // FLIP (replace first FROM slot with TO) or ADD (append TO
      // alongside FROM). The action is the same one the manager saw
      // on the preview row badge, so visible == applied.
      cells: cellsToApply.map((f) => ({
        cell_id: f.cell.cell_id,
        action: f.action,
      })),
    });
  }

  const dayLabels = Array.from({ length: cycleLen }, (_, i) => {
    if (cycleLen <= 7) {
      return ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][i % 7];
    }
    const week = Math.floor(i / 7) + 1;
    const wd = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][i % 7];
    return `W${week} ${wd}`;
  });

  const fmtMins = (mins) => {
    const m = Math.round(mins);
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return h > 0
      ? `${h}h${mm > 0 ? ` ${mm}m` : ""}`
      : `${mm}m`;
  };

  // Phase 2Z.r-4 — dynamic placeholder + friendly fallback example.
  // Both the textarea placeholder and the friendly error message
  // pull category names from the live snapshot so we never surface
  // hardcoded examples (e.g. "Retention", "Admin", "Inbound") to
  // tenants that don't have those categories. Falls back to fully
  // generic wording when fewer than 2 categories are present.
  const _rmiDynamicExamples = useMemo(() => {
    const rows = Array.isArray(liveSnapshot?.rows) ? liveSnapshot.rows : [];
    if (rows.length < 2) {
      return {
        hasNames: false,
        increaseExample: "Increase the lowest category by 10%",
        reduceExample: "Reduce the highest category by 5%",
      };
    }
    const highest = rows[0];
    const lowest = rows[rows.length - 1];
    return {
      hasNames: true,
      highestName: highest.name,
      lowestName: lowest.name,
      increaseExample: `Increase ${lowest.name} using ${highest.name}`,
      reduceExample: `Reduce ${highest.name} by 10%`,
    };
  }, [liveSnapshot]);

  const placeholderCmd = (
    `${_rmiDynamicExamples.increaseExample}\n`
    + `${_rmiDynamicExamples.reduceExample}\n`
    + "Even out the workload across categories"
  );

  // Phase 2Z.r-4 — single, sanitised friendly fallback. Never
  // surfaces raw HTTP/Cloudflare/origin errors. Uses dynamic
  // category examples (or generic wording) so customers don't see
  // hardcoded category names.
  const RMI_FRIENDLY_FALLBACK = (
    "I couldn't safely turn that into a rota mix change. "
    + "Try asking for a category mix change, for example: "
    + `"${_rmiDynamicExamples.increaseExample}" or `
    + `"${_rmiDynamicExamples.reduceExample}".`
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/30"
      onClick={onClose}
      data-testid="rota-style-adjust-mix-drawer"
    >
      <div
        className="bg-white rounded-t-xl shadow-2xl w-full max-w-[1100px] max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="flex items-center gap-2">
              <BrainCircuit className="h-4 w-4 text-[#5775A6]" />
              <h2 className="text-[15px] font-semibold text-[#0B1A36]">
                Rota Mix Intelligence
              </h2>
            </div>
            <p className="text-[11px] text-[#4B5773] mt-0.5">
              Adjust your rota mix using a simple instruction, with
              preview before anything changes.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-slate-100"
            data-testid="rota-style-adjust-mix-close"
          >
            <X className="h-4 w-4 text-[#94A3B8]" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-5">
          {/* 1. Tell the assistant what to do */}
          <section data-testid="rmi-command-section">
            {/* Phase 2Z.r-2 — Premium AI-first hero. The existing
                deterministic regex parser stays available as a
                "Change manually" reveal below, but the primary
                action is "Send to AI Assistant". */}
            <div
              className="flex items-center gap-1.5 mb-2"
              data-testid="rmi-assistant-hero"
            >
              <Sparkles className="h-4 w-4 text-indigo-500" />
              <h3 className="text-[13px] font-semibold text-[#0B1A36]">
                RMI Assistant
              </h3>
              <span className="text-[9px] uppercase tracking-widest font-semibold text-indigo-500/80 ml-1">
                Powered by AI
              </span>
            </div>
            <p
              className="text-[11px] text-[#4B5773] leading-relaxed mb-2.5"
              data-testid="rmi-assistant-intro"
            >
              Tell NextRota what you want to improve. AI will suggest
              a safe rota mix change. You stay in control and preview
              before applying.
            </p>
            <div className="relative">
              <textarea
                value={commandText}
                onChange={(e) => {
                  setCommandText(e.target.value);
                  setInterpretation(null);
                  setAiSuggestion(null);
                  setAiAssistantMessage(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    interpretCommandWithAI();
                  }
                }}
                placeholder={placeholderCmd}
                rows={3}
                maxLength={500}
                className="w-full text-[13px] border border-slate-300 rounded-lg p-3 resize-none focus:outline-none focus:border-indigo-500 placeholder:text-[#94A3B8]"
                data-testid="rmi-command-input"
              />
            </div>
            <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
              <button
                type="button"
                onClick={interpretCommandWithAI}
                disabled={aiAssistantLoading || !commandText.trim()}
                data-testid="rmi-ai-assistant-btn"
                className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3.5 py-1.5 rounded-md border border-indigo-700 bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors shadow-sm"
              >
                <Sparkles className="h-3.5 w-3.5" />
                {aiAssistantLoading ? "Thinking…" : "Send to AI Assistant"}
              </button>
              {aiAssistantMessage && aiAssistantMessage.kind !== "ok" && (
                <span
                  data-testid="rmi-ai-assistant-message"
                  className="text-[11px] text-[#94A3B8]"
                >
                  {aiAssistantMessage.text}
                </span>
              )}
            </div>
            {aiSuggestion && (
              <div
                data-testid="rmi-ai-suggestion-card"
                className="mt-3 rounded-lg border border-indigo-200 bg-gradient-to-br from-indigo-50/80 to-white p-3"
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <Sparkles className="h-3 w-3 text-indigo-600" />
                  <span className="text-[10px] uppercase tracking-widest font-semibold text-indigo-700">
                    AI suggestion
                  </span>
                  {aiSuggestion.confidence && (
                    <span
                      data-testid="rmi-ai-suggestion-confidence"
                      className="text-[10px] text-[#94A3B8] ml-auto"
                    >
                      Confidence: {aiSuggestion.confidence}
                    </span>
                  )}
                </div>
                <p
                  data-testid="rmi-ai-suggestion-command"
                  className="text-[13px] text-[#0B1A36] font-medium"
                >
                  {aiSuggestion.suggested_command}
                </p>
                {aiSuggestion.reason && (
                  <p
                    data-testid="rmi-ai-suggestion-reason"
                    className="text-[11px] text-[#4B5773] mt-1.5 leading-relaxed"
                  >
                    <span className="font-semibold">Reason:</span>{" "}
                    {aiSuggestion.reason}
                  </p>
                )}
                {aiSuggestion.default_from_used && aiSuggestion.default_from_name && (
                  <p
                    data-testid="rmi-ai-suggestion-default-from-note"
                    className="text-[11px] text-[#4B5773] mt-1 leading-relaxed italic"
                  >
                    Default source: {aiSuggestion.default_from_name} currently has the
                    largest share, so it was selected as the default source.
                    Preview before applying.
                  </p>
                )}
                {aiSuggestion.broad_balance_applied && aiSuggestion.broad_balance_note && (
                  <p
                    data-testid="rmi-ai-suggestion-broad-balance-note"
                    className="text-[11px] text-[#4B5773] mt-1 leading-relaxed italic"
                  >
                    {aiSuggestion.broad_balance_note}
                  </p>
                )}
                {/* TP-RMI.day-scope-note (Feb 2026) — when the manager
                    has narrowed Refine Scope to specific weekdays
                    (e.g. via "on Monday" in the prompt), surface it
                    explicitly so it's clear RMI will only change
                    those days within the current Rota Style scope.
                    Same idea for picked employees so the manager
                    sees BOTH refine narrowings at a glance. */}
                {(dayPick.size > 0 || empPick.size > 0) && (
                  <p
                    data-testid="rmi-ai-suggestion-refine-scope-note"
                    className="text-[11px] text-[#4B5773] mt-1 leading-relaxed italic"
                  >
                    Refine scope active:{" "}
                    {dayPick.size > 0 && (
                      <>
                        adjusting only{" "}
                        {Array.from(dayPick)
                          .sort((a, b) => a - b)
                          .map((i) => ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][i % 7] || `Day ${i + 1}`)
                          .join(", ")}
                        {empPick.size > 0 ? "; " : "."}
                      </>
                    )}
                    {empPick.size > 0 && (
                      <>
                        {empPick.size} employee{empPick.size === 1 ? "" : "s"} selected.
                      </>
                    )}{" "}
                    Within the current Rota Style scope only.
                  </p>
                )}
                <div className="flex gap-2 mt-2.5">
                  <button
                    type="button"
                    onClick={() => {
                      // Pre-fills the existing RMI fields — does
                      // NOT apply. The user must still use the
                      // existing preview/apply flow below.
                      // TP-RMI.scope-recover + TP-RMI.to-scope
                      // (Feb 2026) — TARGET uses the BROADER Rota
                      // Style scope; SOURCE uses the narrower
                      // refine-scope. Never clobber the manager's
                      // existing selection with a non-existent id.
                      const _recoverTo = (id) => {
                        if (!id) return "";
                        if (inStyleAnyDayCategoryIds.has(id)) return id;
                        const aimed = assignableCategories.find(
                          (c) => c.category_id === id);
                        if (!aimed) return "";
                        const nm = String(aimed.name || "").trim().toLowerCase();
                        if (!nm) return "";
                        const match = toCandidateCategories.find(
                          (c) => String(c.name || "").trim().toLowerCase() === nm);
                        return match ? match.category_id : "";
                      };
                      const _recoverFrom = (id) => {
                        if (!id) return "";
                        if (inScopeCategoryIds.has(id)) return id;
                        const aimed = assignableCategories.find(
                          (c) => c.category_id === id);
                        if (!aimed) return "";
                        const nm = String(aimed.name || "").trim().toLowerCase();
                        if (!nm) return "";
                        const match = fromCandidateCategories.find(
                          (c) => String(c.name || "").trim().toLowerCase() === nm);
                        return match ? match.category_id : "";
                      };
                      const safeTo = _recoverTo(aiSuggestion.to_category_id);
                      const safeFrom = _recoverFrom(aiSuggestion.from_category_id);
                      if (safeTo) setToId(safeTo);
                      if (safeFrom) setFromId(safeFrom);
                      if (aiSuggestion.delta_pct != null)
                        setDeltaPct(aiSuggestion.delta_pct);
                      setShowManualControls(true);
                    }}
                    data-testid="rmi-ai-use-suggestion-btn"
                    className="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-md border border-emerald-700 bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                  >
                    Use suggestion
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowManualControls(true)}
                    data-testid="rmi-ai-change-manually-btn"
                    className="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-md border border-slate-200 bg-white text-[#0B1A36] hover:border-slate-300 transition-colors"
                  >
                    Change manually
                  </button>
                </div>
              </div>
            )}
            {/* Phase 2Z.r-2 — "Advanced manual controls" reveal.
                Hides the deterministic Interpret button + chip row
                until the user opts in (either via "Change manually"
                on the AI card or by clicking the reveal toggle).
                The controls themselves stay in the DOM — they're
                just visually hidden so existing behaviour and tests
                that depend on the regex parser keep working. */}
            <button
              type="button"
              onClick={() => setShowManualControls((v) => !v)}
              data-testid="rmi-advanced-manual-toggle"
              className="inline-flex items-center gap-1 text-[11px] text-[#5775A6] hover:text-[#0B1A36] mt-2.5 transition-colors"
            >
              <ChevronDown
                className={`h-3 w-3 transition-transform ${showManualControls ? "rotate-180" : ""}`}
              />
              Advanced manual controls
            </button>
            <div
              data-testid="rmi-advanced-manual-controls"
              className={`mt-2 ${showManualControls ? "" : "hidden"}`}
            >
              <Button
                size="sm"
                onClick={interpretCommand}
                disabled={!commandText.trim()}
                data-testid="rmi-interpret-btn"
                variant="outline"
              >
                <Wand2 className="h-3.5 w-3.5 mr-1.5" />
                Interpret (standard parser)
              </Button>
            </div>
            {chips.length > 0 && (
              <div
                className="flex flex-wrap gap-1.5 mt-2.5"
                data-testid="rmi-chip-row"
              >
                {chips.map((ch) => (
                  <button
                    key={ch.key}
                    type="button"
                    onClick={ch.apply}
                    className="text-[11px] px-2.5 py-1 rounded-full border border-slate-200 bg-white text-[#0B1A36] hover:border-[#5775A6] hover:bg-slate-50"
                    data-testid={`rmi-chip-${ch.key}`}
                  >
                    {ch.label}
                  </button>
                ))}
              </div>
            )}
            <p className="text-[10px] text-[#94A3B8] mt-1.5">
              Suggestions are based on your active categories and
              current mix.
            </p>
          </section>

          {/* 2. Live mix snapshot */}
          <section
            className="rounded-lg border border-slate-200 bg-[#F8FAFC] p-3"
            data-testid="rmi-live-snapshot"
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[11px] uppercase tracking-widest text-[#5775A6] font-semibold">
                2. Live mix snapshot
              </h3>
              <span className="text-[10px] text-[#94A3B8]">
                Total cycle time {fmtMins(liveSnapshot.totalMins)}
              </span>
            </div>
            {liveSnapshot.rows.length === 0 ? (
              <p className="text-[12px] text-[#94A3B8]">
                Add timed assignments to the grid to see your mix.
              </p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                {liveSnapshot.rows.map((r) => (
                  <div
                    key={r.category_id}
                    className="bg-white rounded p-2 border border-slate-100"
                    data-testid={`rmi-snapshot-cell-${r.category_id}`}
                  >
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-[#0B1A36] truncate">
                        {r.name}
                      </span>
                      <span className="text-[#4B5773] tabular-nums">
                        {r.pct.toFixed(0)}%
                      </span>
                    </div>
                    <div className="h-1 bg-slate-100 rounded mt-1 overflow-hidden">
                      <div
                        className="h-full rounded"
                        style={{
                          width: `${r.pct}%`,
                          background: r.color,
                        }}
                      />
                    </div>
                    <div className="text-[10px] text-[#94A3B8] tabular-nums mt-1">
                      {fmtMins(r.minutes)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* 3. Parsed interpretation */}
          <section data-testid="rmi-interpretation">
            <div className="flex items-center gap-1.5 mb-2">
              <Sparkles className="h-3 w-3 text-[#5775A6]" />
              <h3 className="text-[11px] uppercase tracking-widest text-[#5775A6] font-semibold">
                3. Parsed interpretation
              </h3>
            </div>
            {interpretation && !interpretation.ok && (
              <div
                className="bg-amber-50 border border-amber-200 rounded p-2.5 text-[12px] text-[#7B4A0B] mb-3"
                data-testid="rmi-parse-error"
              >
                {interpretation.message}
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 items-end">
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold mb-1.5">
                  Action
                </label>
                <div className="text-[12px] border border-slate-200 rounded px-2.5 py-1.5 bg-slate-50 text-[#0B1A36]">
                  {interpretation?.action
                    ? interpretation.action.charAt(0).toUpperCase()
                      + interpretation.action.slice(1)
                    : "Increase"}
                </div>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold mb-1.5">
                  Increase
                </label>
                <select
                  value={toId}
                  onChange={(e) => setToId(e.target.value)}
                  className="w-full text-[13px] border border-slate-300 rounded px-2.5 py-1.5 bg-white"
                  data-testid="rota-style-adjust-mix-to-select"
                >
                <option value="">Pick a category</option>
                  {/* RMI filter fix: TO list only shows assignable
                      (work-kind, active) categories. Never leave /
                      system / payment rows like Annual Leave, BACS,
                      Bank Holiday, etc. In-scope categories appear
                      first under "In current rota style", then the
                      rest under "Other assignment categories" so the
                      manager sees what's already in their grid
                      before any new ones they might introduce. */}
                  {toDropdownCategories.inScope.length > 0 && (
                    <optgroup label="In current rota style">
                      {toDropdownCategories.inScope.map((c) => (
                        <option key={c.category_id} value={c.category_id}>
                          {c.name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {toDropdownCategories.other.length > 0 && (
                    <optgroup label="Other assignment categories">
                      {toDropdownCategories.other.map((c) => (
                        <option key={c.category_id} value={c.category_id}>
                          {c.name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold mb-1.5">
                  By
                </label>
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    min={1}
                    max={100}
                    value={deltaPct}
                    onChange={(e) => setDeltaPct(e.target.value)}
                    className="text-[13px] h-9"
                    data-testid="rota-style-adjust-mix-delta"
                  />
                  <span className="text-[12px] text-[#94A3B8]">%</span>
                </div>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold mb-1.5">
                  Using time from
                </label>
                <select
                  value={fromId}
                  onChange={(e) => setFromId(e.target.value)}
                  className="w-full text-[13px] border border-slate-300 rounded px-2.5 py-1.5 bg-white"
                  data-testid="rota-style-adjust-mix-from-select"
                >
                  <option value="">Pick a category</option>
                  {/* RMI filter fix: FROM list is restricted to
                      categories actually present in the IN-SCOPE
                      Rota Style cells. You can only move time away
                      from a category that's present. */}
                  {fromCandidateCategories
                    .filter((c) => c.category_id !== toId)
                    .map((c) => (
                      <option
                        key={c.category_id}
                        value={c.category_id}
                      >
                        {c.name}
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold mb-1.5">
                  Confidence
                </label>
                <div
                  className={`text-[12px] px-2.5 py-1.5 rounded border tabular-nums ${
                    interpretation?.confidence === "high"
                      ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                      : interpretation?.confidence === "medium"
                      ? "bg-amber-50 border-amber-200 text-amber-700"
                      : "bg-slate-50 border-slate-200 text-[#4B5773]"
                  }`}
                  data-testid="rmi-confidence-chip"
                >
                  {interpretation?.ok
                    ? interpretation.confidence === "high"
                      ? "High"
                      : interpretation.confidence === "medium"
                      ? "Medium"
                      : "Low"
                    : "-"}
                </div>
              </div>
            </div>
          </section>

          {/* 4. Refine scope */}
          <section>
            <button
              type="button"
              onClick={() => setScopeOpen((v) => !v)}
              className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-[#5775A6] font-semibold"
              data-testid="rmi-refine-scope-toggle"
            >
              <ChevronDown
                className={`h-3 w-3 transition-transform ${
                  scopeOpen ? "rotate-0" : "-rotate-90"
                }`}
              />
              4. Refine scope (optional)
              <span className="text-[10px] text-[#94A3B8] normal-case tracking-normal font-normal ml-1">
                {empPick.size === 0
                  ? (scopeLabel || "All employees")
                  : `${empPick.size} employees`}
                {" · "}
                {dayPick.size === 0 ? "All days" : `${dayPick.size} days`}
              </span>
            </button>
            {scopeOpen && (
              <div className="mt-3 space-y-4">
                {/* Phase 2Z.t — Teams chips. Multi-select; toggling a
                    team adds or removes every employee in that team
                    to/from `empPick`. Hidden when the company has no
                    named teams. Manual tick state is never silently
                    cleared. */}
                {(() => {
                  const teamList = (cgTeams || []).filter(
                    (t) => t && t.team_id && (t.name || "").trim());
                  if (teamList.length === 0) return null;
                  return (
                    <div data-testid="rmi-scope-teams-section">
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold">
                          Teams
                        </label>
                        <span className="text-[10px] text-[#94A3B8]">
                          Toggling a team picks every employee in it.
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {teamList.map((t) => {
                          const teamEmps = (employees || []).filter(
                            (e) => e && e.team_id === t.team_id);
                          if (teamEmps.length === 0) return null;
                          const allOn = teamEmps.every(
                            (e) => empPick.has(e.employee_id));
                          return (
                            <button
                              key={t.team_id}
                              type="button"
                              onClick={() => {
                                setEmpPick((prev) => {
                                  const next = new Set(prev);
                                  if (allOn) {
                                    for (const e of teamEmps) {
                                      next.delete(e.employee_id);
                                    }
                                  } else {
                                    for (const e of teamEmps) {
                                      next.add(e.employee_id);
                                    }
                                  }
                                  return next;
                                });
                              }}
                              className={`text-[11px] px-2.5 py-1 rounded-full border ${
                                allOn
                                  ? "bg-[#0B1A36] text-white border-[#0B1A36]"
                                  : "bg-white text-[#0B1A36] border-slate-200 hover:border-slate-300"
                              }`}
                              data-testid={`rmi-scope-team-${t.team_id}`}
                            >
                              {t.name}
                              <span className="ml-1 text-[10px] opacity-70">
                                ({teamEmps.length})
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold">
                      Employees
                    </label>
                    <span className="text-[10px] text-[#94A3B8]">
                      {empPick.size === 0
                        ? (scopeLabel || "All employees")
                        : `${empPick.size} selected`}
                    </span>
                  </div>
                  <Input
                    placeholder="Search…"
                    value={empSearch}
                    onChange={(e) => setEmpSearch(e.target.value)}
                    className="text-[12px] h-8 mb-2"
                    data-testid="rota-style-adjust-mix-emp-search"
                  />
                  <div className="max-h-[140px] overflow-y-auto border border-slate-200 rounded p-2 grid grid-cols-2 gap-1.5">
                    {scopedEmployees.length === 0 ? (
                      <p className="text-[12px] text-[#94A3B8] col-span-2 px-1">
                        No employees match.
                      </p>
                    ) : (
                      scopedEmployees.map((e) => (
                        <label
                          key={e.employee_id}
                          className="flex items-center gap-1.5 text-[12px] text-[#0B1A36] cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={empPick.has(e.employee_id)}
                            onChange={() => toggleEmp(e.employee_id)}
                            data-testid={`rota-style-adjust-mix-emp-${e.employee_id}`}
                          />
                          <span className="truncate">{e.name}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold">
                      Cycle days
                    </label>
                    <span className="text-[10px] text-[#94A3B8]">
                      {dayPick.size === 0
                        ? "All days"
                        : `${dayPick.size} selected`}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {dayLabels.map((label, idx) => {
                      const on = dayPick.has(idx);
                      return (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => toggleDay(idx)}
                          className={`text-[11px] px-2 py-1 rounded border ${
                            on
                              ? "bg-[#0B1A36] text-white border-[#0B1A36]"
                              : "bg-white text-[#0B1A36] border-slate-200 hover:border-slate-300"
                          }`}
                          data-testid={`rota-style-adjust-mix-day-${idx}`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* 5. Preview impact */}
          <section data-testid="rota-style-adjust-mix-preview">
            <h3 className="text-[11px] uppercase tracking-widest text-[#5775A6] font-semibold mb-2">
              5. Preview impact
            </h3>
            {!preview.hasValidPick ? (
              <p className="text-[12px] text-[#94A3B8] bg-slate-50 border border-slate-200 rounded p-3">
                Pick categories and a percentage above to see what
                will change. No changes will happen until you click
                Apply.
              </p>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <div className="bg-white border border-slate-200 rounded p-3">
                  <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] mb-2">
                    Current mix
                  </div>
                  <ul className="space-y-1">
                    {preview.currentRows.map((r) => (
                      <li
                        key={r.category_id}
                        className="flex items-center justify-between text-[12px]"
                      >
                        <span className="flex items-center gap-1.5 text-[#0B1A36] truncate">
                          <span
                            className="h-2 w-2 rounded-full shrink-0"
                            style={{ background: r.color }}
                          />
                          {r.name}
                        </span>
                        <span className="text-[#4B5773] tabular-nums">
                          {r.pct.toFixed(0)}%{" "}
                          <span className="text-[#94A3B8]">
                            {fmtMins(r.minutes)}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-white border border-slate-200 rounded p-3">
                  <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] mb-2">
                    Projected mix
                  </div>
                  <ul className="space-y-1">
                    {preview.projectedRows.map((r) => {
                      const cur = preview.currentRows.find(
                        (x) => x.category_id === r.category_id);
                      const isUp = cur && r.pct > cur.pct + 0.05;
                      const isDown = cur && r.pct < cur.pct - 0.05;
                      return (
                        <li
                          key={r.category_id}
                          className="flex items-center justify-between text-[12px]"
                        >
                          <span className="flex items-center gap-1.5 text-[#0B1A36] truncate">
                            <span
                              className="h-2 w-2 rounded-full shrink-0"
                              style={{ background: r.color }}
                            />
                            {r.name}
                          </span>
                          <span
                            className={`tabular-nums ${
                              isUp ? "text-emerald-700 font-medium"
                                : isDown ? "text-rose-600"
                                : "text-[#4B5773]"
                            }`}
                          >
                            {r.pct.toFixed(0)}%{" "}
                            <span className="text-[#94A3B8]">
                              {fmtMins(r.minutes)}
                            </span>
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-[12px] text-[#0B1A36]">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="text-[10px] uppercase tracking-widest text-emerald-700">
                      Impact summary
                    </div>
                    <CoverageGuardBadge
                      verdict={cgPreviewVerdict}
                      onOpenSettings={onOpenCgSettings}
                      size="xs"
                      testid="coverage-guard-badge-rmi"
                      dayLabels={Array.from({ length: cycleLen }).map((_, i) => dayLabel(i, cycleLen))}
                      emptyHint="No Coverage Guard rules. Click Manage to set up."
                    />
                  </div>
                  <div className="space-y-0.5 tabular-nums">
                    <div>
                      Cells will change:{" "}
                      <strong>{preview.toFlip.length}</strong>
                      {preview.toFlip.length > 0 && (
                        <span
                          className="ml-1.5 text-[11px] text-[#5775A6]"
                          data-testid="rota-style-adjust-mix-action-breakdown"
                        >
                          ({preview.flipActionCount} flip ·{" "}
                          {preview.addActionCount} add)
                        </span>
                      )}
                    </div>
                    {autoSkip && cgSafeSplit.summarySkipped > 0 && (
                      <div
                        className="text-[#5775A6]"
                        data-testid="rota-style-adjust-mix-cg-skipped-summary"
                      >
                        Coverage Guard skipped{" "}
                        <strong>{cgSafeSplit.summarySkipped}</strong> risky
                        {cgSafeSplit.summarySkipped === 1 ? " change" : " changes"}
                      </div>
                    )}
                    <div>
                      Mixed cells skipped:{" "}
                      <strong>{preview.skippedDuplicate.length}</strong>
                    </div>
                    <div className="text-[#4B5773]">
                      Minutes shifted: {fmtMins(
                        preview.minutesActuallyShifted)}
                    </div>
                  </div>
                  {/* Phase 2Z.n.3 — Respect Coverage Guard toggle.
                      Moved here from the footer so it's visible
                      right next to the impact the manager is
                      deciding on. Switch (not checkbox) for premium
                      feel. Only renders when CG rules exist. */}
                  {cgRules && cgRules.length > 0 && (
                    <div
                      className="mt-3 flex items-start justify-between gap-3 rounded-md border border-emerald-200/70 bg-white/70 px-3 py-2"
                      data-testid="rota-style-adjust-mix-cg-autoskip-block"
                    >
                      <div className="min-w-0 flex-1">
                        <label
                          htmlFor="rota-style-adjust-mix-cg-autoskip-toggle"
                          className="block text-[12px] font-medium text-[#0B1A36] cursor-pointer select-none"
                          data-testid="rota-style-adjust-mix-cg-autoskip-label"
                        >
                          Respect Coverage Guard
                        </label>
                        <p className="text-[11px] text-[#5775A6] mt-0.5">
                          Automatically skip changes that would break coverage.
                        </p>
                        {autoSkip && cgSafeSplit.summarySkipped > 0 && (
                          <span
                            className="mt-1 inline-flex items-center px-1.5 py-0.5 rounded bg-rose-50 border border-rose-200 text-rose-700 text-[10px] font-medium"
                            data-testid="rota-style-adjust-mix-cg-skipped-pill"
                          >
                            {cgSafeSplit.summarySkipped} skipped
                          </span>
                        )}
                      </div>
                      <Switch
                        id="rota-style-adjust-mix-cg-autoskip-toggle"
                        data-testid="rota-style-adjust-mix-cg-autoskip-toggle"
                        checked={autoSkip}
                        onCheckedChange={setAutoSkip}
                      />
                    </div>
                  )}
                  {preview.explanation && (
                    <p
                      className="text-[11px] text-[#B4530B] mt-2"
                      data-testid="rota-style-adjust-mix-explanation"
                    >
                      {preview.explanation}
                    </p>
                  )}
                  {/* C3 informational notice — the cells that would
                      have lost all `from` coverage under flip-only
                      will instead be ADDED to (keep `from`, append
                      `to`). C3 made this safe by default; we still
                      tell the manager what to expect so they can
                      narrow scope if they'd rather flip outright. */}
                  {preview.singleSlotRiskyCount > 0 && (
                    <div
                      className="mt-2 rounded-md border border-sky-200 bg-sky-50 p-2 text-[11px] text-sky-900"
                      data-testid="rota-style-adjust-mix-single-slot-warning"
                    >
                      <div className="font-semibold text-sky-800 mb-0.5">
                        ℹ {preview.singleSlotRiskyCount}{" "}
                        single-category {preview.singleSlotRiskyCount === 1 ? "cell" : "cells"} will keep{" "}
                        {fromCat?.name || "-"} and add {toCat?.name || "-"} alongside
                      </div>
                      <div className="text-sky-900">
                        These cells only contain {fromCat?.name || "-"}, so NextRota will
                        preserve {fromCat?.name || "-"} and add {toCat?.name || "-"} as a
                        second assignment. Each will get half the cell&apos;s net minutes.
                        If you&apos;d rather flip them outright (losing all {fromCat?.name || "-"}),
                        narrow your scope (employees / days) above before applying.
                      </div>
                    </div>
                  )}
                  {preview.toFlip.length > 0 && (
                    <details className="text-[11px] mt-2">
                      <summary className="cursor-pointer text-[#5775A6]">
                        View cell details
                      </summary>
                      <ul
                        className="mt-1.5 max-h-[140px] overflow-y-auto bg-white border border-emerald-100 rounded p-1.5 space-y-0.5"
                        data-testid="rota-style-adjust-mix-flip-list"
                      >
                        {preview.toFlip.map(({ cell: c, slotMinutes, action }) => {
                          const empName = employees.find(
                            (e) => e.employee_id === c.employee_id)?.name
                            || c.employee_id;
                          const isAdd = action === "add";
                          const isSkipped = autoSkip
                            && cgSkippedCellIds.has(c.cell_id);
                          const skipReason = isSkipped
                            ? (cgSafeSplit.unsafe.find(
                                (u) => u.cell.cell_id === c.cell_id)?.skipReason
                                || "Skipped by Coverage Guard")
                            : null;
                          return (
                            <li
                              key={c.cell_id}
                              className={`tabular-nums flex items-center gap-1.5 ${
                                isSkipped
                                  ? "text-[#6B7B96] line-through"
                                  : "text-[#0B1A36]"
                              }`}
                              title={skipReason || undefined}
                            >
                              <span
                                className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider shrink-0 ${
                                  isAdd
                                    ? "bg-amber-100 text-amber-800 border border-amber-200"
                                    : "bg-slate-100 text-slate-700 border border-slate-200"
                                }`}
                                data-testid={
                                  isAdd
                                    ? "rota-style-adjust-mix-action-badge-add"
                                    : "rota-style-adjust-mix-action-badge-flip"
                                }
                              >
                                {isAdd ? "ADD" : "FLIP"}
                              </span>
                              {isSkipped && (
                                <span
                                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider shrink-0 bg-rose-100 text-rose-800 border border-rose-200"
                                  data-testid="rota-style-adjust-mix-cg-skipped-badge"
                                >
                                  SKIPPED
                                </span>
                              )}
                              <span className="truncate">
                                {dayLabels[c.cycle_day_index]} ·{" "}
                                {c.start_time || "all-day"}
                                {c.end_time ? `-${c.end_time}` : ""} ·{" "}
                                {empName} · {fmtMins(slotMinutes)}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </details>
                  )}
                  {preview.skippedDuplicate.length > 0 && (
                    <details className="text-[11px] mt-1">
                      <summary className="cursor-pointer text-[#B4530B]">
                        View skipped cells
                      </summary>
                      <ul
                        className="mt-1.5 max-h-[120px] overflow-y-auto bg-white border border-emerald-100 rounded p-1.5 space-y-0.5"
                        data-testid="rota-style-adjust-mix-skipped-list"
                      >
                        {preview.skippedDuplicate.map((c) => {
                          const empName = employees.find(
                            (e) => e.employee_id === c.employee_id)?.name
                            || c.employee_id;
                          return (
                            <li
                              key={c.cell_id}
                              className="text-[#0B1A36] tabular-nums"
                            >
                              {dayLabels[c.cycle_day_index]} ·{" "}
                              {c.start_time || "all-day"}
                              {c.end_time ? `-${c.end_time}` : ""} ·{" "}
                              {empName}
                            </li>
                          );
                        })}
                      </ul>
                    </details>
                  )}
                </div>
              </div>
            )}
          </section>

          {/* Safety first */}
          <div
            className="rounded-lg border border-slate-200 bg-[#F8FAFC] p-3 flex items-start gap-3"
            data-testid="rmi-safety-note"
          >
            <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
            <div>
              <div className="text-[12px] font-semibold text-[#0B1A36]">
                Safety first
              </div>
              <p className="text-[11px] text-[#4B5773] mt-0.5">
                Times, breaks, notes and coverage rules stay the same.
                Cells already containing the target category are
                skipped. All changes are previewed before they apply,
                you can review, then apply.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-200 sticky bottom-0 bg-white">
          {/* Phase 2Z.n.2 + 2Z.n.3 — "all skipped" message lives in
              the footer (close to Apply) so the manager sees it
              before pressing the button. The Respect Coverage Guard
              toggle itself has been moved up next to the Impact
              summary so it's not hidden at the very bottom. */}
          {cgRules && cgRules.length > 0
            && autoSkip
            && preview.toFlip.length > 0
            && cgSafeSplit.safe.length === 0 && (
            <div
              className="text-[11px] text-rose-700 mb-2"
              data-testid="rota-style-adjust-mix-cg-all-skipped-msg"
            >
              Coverage Guard would skip every change. Turn off
              Respect Coverage Guard to apply anyway.
            </div>
          )}
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
              data-testid="rota-style-adjust-mix-cancel"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={applyAll}
              disabled={!canApply}
              data-testid="rota-style-adjust-mix-apply"
            >
              {saving ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
              )}
              Apply
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
// AdjustMixDrawer is the legacy export name — kept as an alias so
// existing imports (and the 15 source-level locks added in
// test_phase_2zl_c_full_adjust_mix.py) still match. The function is
// identical to RotaMixIntelligenceDrawer; consumers can use either.
const AdjustMixDrawer = RotaMixIntelligenceDrawer;

